import React, { useState, useEffect, useCallback, createContext, useContext, useRef, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import {
  Trophy, Users, BarChart3, Calendar, Home, LogOut, LogIn,
  Plus, Search, ArrowLeft, ArrowRight, Edit, Trash2,
  Medal, Activity, MapPin, Hand, Check, Settings, Clock,
  PlayCircle, X, Save, RefreshCw, Star, Menu, Newspaper, Image, Shield, UserPlus, Key, CheckCircle, AlertCircle, ChevronDown, Eye, WifiOff, Lock,
  ZoomIn, ZoomOut, Maximize2, FileText, UserMinus, User, TrendingUp
} from 'lucide-react';
import { playersAPI, tournamentsAPI, rankingsAPI, powerIndexAPI, leaguesAPI, matchesAPI, newsAPI, authAPI, usersAPI, activityAPI, adsAPI, clubsAPI, publicStatsAPI, analyticsAPI, getRefreshToken, setAccessToken, clearTokens } from './services/api';

// ==================== SEO COMPONENT ====================
const BASE_URL = 'https://teniscg.me';

const SEO = ({
  title = 'TenisCG.me | Tenis',
  description = 'Platforma za rekreativni tenis u Crnoj Gori. Rang liste, turniri, lige i više.',
  image = '/og-image.jpg',
  url,
  type = 'website'
}) => {
  const pageUrl = url ? `${BASE_URL}${url}` : BASE_URL;
  const imageUrl = image.startsWith('http') ? image : `${BASE_URL}${image}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={pageUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="TenisCG.me" />
      <meta property="og:locale" content="sr_RS" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
    </Helmet>
  );
};

// ==================== REUSABLE UI COMPONENTS ====================

// Skeleton Loaders
const Skeleton = ({ className = '', variant = 'text' }) => {
  const baseClass = 'skeleton';
  const variantClass = variant === 'avatar' ? 'skeleton-avatar' : variant === 'card' ? 'skeleton-card' : 'skeleton-text';
  return <div className={`${baseClass} ${variantClass} ${className}`} />;
};

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-neutral-200/60 p-4 space-y-3">
    <div className="flex items-center gap-3">
      <Skeleton variant="avatar" className="w-12 h-12" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  </div>
);

const SkeletonList = ({ count = 5 }) => (
  <div className="space-y-3">
    {[...Array(count)].map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
);

const SkeletonTable = ({ rows = 5 }) => (
  <div className="bg-white rounded-2xl border border-neutral-200/60 overflow-hidden">
    <div className="p-4 border-b border-neutral-100">
      <Skeleton className="h-4 w-1/4" />
    </div>
    {[...Array(rows)].map((_, i) => (
      <div key={i} className="flex items-center gap-4 p-4 border-b border-neutral-100 last:border-0">
        <Skeleton variant="avatar" className="w-10 h-10" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/3" />
        </div>
        <Skeleton className="h-6 w-16" />
      </div>
    ))}
  </div>
);

// Loading Spinner
const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizeClass = size === 'sm' ? 'spinner-sm' : size === 'lg' ? 'spinner-lg' : '';
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className={`spinner ${sizeClass}`} />
    </div>
  );
};

// Full Page Loading
const PageLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-neutral-50">
    <div className="text-center">
      <LoadingSpinner size="lg" className="mb-4" />
      <p className="text-neutral-500 text-sm animate-pulse-soft">Učitavanje...</p>
    </div>
  </div>
);

// Empty State
const EmptyState = ({ icon: Icon = AlertCircle, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
    <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mb-4 animate-float">
      <Icon className="w-8 h-8 text-neutral-400" />
    </div>
    <h3 className="text-lg font-semibold text-neutral-900 mb-1">{title}</h3>
    {description && <p className="text-neutral-500 text-sm mb-4 max-w-sm">{description}</p>}
    {action && action}
  </div>
);

// Lazy Image with blur placeholder
const LazyImage = ({ src, alt, className = '', placeholderColor = '#f3f4f6' }) => {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );
    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={`relative overflow-hidden ${className}`} style={{ backgroundColor: placeholderColor }}>
      {inView && (
        <>
          <div className={`absolute inset-0 bg-neutral-200 transition-opacity duration-500 ${loaded ? 'opacity-0' : 'opacity-100 animate-pulse'}`} />
          <img
            src={src}
            alt={alt}
            className={`w-full h-full object-cover transition-all duration-500 ${loaded ? 'opacity-100 blur-0 scale-100' : 'opacity-0 blur-sm scale-105'}`}
            onLoad={() => setLoaded(true)}
          />
        </>
      )}
    </div>
  );
};

// Pull to Refresh - uses padding instead of transform to preserve fixed positioning
const PullToRefresh = ({ onRefresh, children }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);
  const threshold = 80;

  const handleTouchStart = (e) => {
    if (window.scrollY === 0) {
      startY.current = e.touches[0].clientY;
      pulling.current = true;
    }
  };

  const handleTouchMove = (e) => {
    if (!pulling.current || refreshing) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    if (diff > 0 && window.scrollY === 0) {
      e.preventDefault();
      setPullDistance(Math.min(diff * 0.5, threshold * 1.5));
    }
  };

  const handleTouchEnd = async () => {
    if (pullDistance >= threshold && !refreshing) {
      setRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
      }
    }
    setPullDistance(0);
    pulling.current = false;
  };

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="relative"
    >
      {/* Pull indicator - fixed position so it doesn't affect layout */}
      {(pullDistance > 0 || refreshing) && (
        <div
          className="fixed left-1/2 -translate-x-1/2 flex items-center justify-center z-[60]"
          style={{ top: Math.max(70, 70 + pullDistance - 40) }}
        >
          <div className={`w-10 h-10 rounded-full bg-white shadow-lg border border-neutral-200 flex items-center justify-center ${refreshing ? 'animate-spin' : ''}`}>
            <RefreshCw className={`w-5 h-5 text-rose-500 transition-transform duration-200 ${pullDistance >= threshold ? 'rotate-180' : ''}`} />
          </div>
        </div>
      )}
      {/* Content with padding instead of transform */}
      <div style={{ paddingTop: refreshing ? 50 : pullDistance * 0.4, transition: 'padding-top 0.2s ease-out' }}>
        {children}
      </div>
    </div>
  );
};

// Offline Indicator
const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowBanner(true);
      setTimeout(() => setShowBanner(false), 3000);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showBanner && isOnline) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 z-[100] transition-transform duration-300 ${showBanner ? 'translate-y-0' : '-translate-y-full'}`}>
      <div className={`px-4 py-3 text-center text-sm font-medium flex items-center justify-center gap-2 ${isOnline ? 'bg-green-500 text-white' : 'bg-neutral-800 text-white'}`}>
        {isOnline ? (
          <>
            <CheckCircle className="w-4 h-4" />
            Ponovo ste online
          </>
        ) : (
          <>
            <WifiOff className="w-4 h-4" />
            Nema internet konekcije
          </>
        )}
      </div>
    </div>
  );
};

// Page Transition Wrapper
const PageTransition = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Return children directly without wrapper to preserve fixed positioning
  return children;
};

// Error Boundary
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-neutral-900 mb-2">Ups! Nešto je pošlo po zlu</h1>
            <p className="text-neutral-500 text-sm mb-6">
              Došlo je do neočekivane greške. Molimo osvježite stranicu ili se vratite na početnu.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg font-medium hover:bg-neutral-200 transition-colors"
              >
                Osvježi stranicu
              </button>
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.href = '/';
                }}
                className="px-4 py-2 bg-rose-500 text-white rounded-lg font-medium hover:bg-rose-600 transition-colors"
              >
                Početna stranica
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ==================== AUTH CONTEXT ====================
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initAuth();

    // Listen for forced logout events from API interceptors
    const handleForcedLogout = () => {
      setUser(null);
    };
    window.addEventListener('auth:logout', handleForcedLogout);
    return () => window.removeEventListener('auth:logout', handleForcedLogout);
  }, []);

  const initAuth = async () => {
    // Try to restore session using refresh token
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        // Refresh the access token
        await authAPI.refreshToken();
        // Get current user with the new access token
        const response = await authAPI.getCurrentUser();
        if (response.data.authenticated) {
          setUser(response.data.user);
        } else {
          clearTokens();
          setUser(null);
        }
      } catch (error) {
        clearTokens();
        setUser(null);
      }
    } else {
      setUser(null);
    }
    setLoading(false);
  };

  const checkAuth = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      if (response.data.authenticated) {
        setUser(response.data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    }
  };

  const login = async (username, password) => {
    const response = await authAPI.login(username, password);
    setUser(response.data.user);
    return response.data.user;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Logout anyway even if API call fails
      clearTokens();
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== TOAST NOTIFICATION SYSTEM ====================
const ToastContext = createContext(null);

const useToast = () => useContext(ToastContext);

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success', duration = 4000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  };

  const success = (message) => addToast(message, 'success');
  const error = (message) => addToast(message, 'error');
  const info = (message) => addToast(message, 'info');

  return (
    <ToastContext.Provider value={{ addToast, success, error, info }}>
      {children}
      {/* Toast Container */}
      <div className="fixed bottom-4 right-4 z-[9999] space-y-2">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border backdrop-blur-sm animate-slide-in ${
              toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
              toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
              'bg-blue-50 border-blue-200 text-blue-800'
            }`}
          >
            {toast.type === 'success' ? <CheckCircle className="w-5 h-5 text-green-500" /> :
             toast.type === 'error' ? <AlertCircle className="w-5 h-5 text-red-500" /> :
             <AlertCircle className="w-5 h-5 text-blue-500" />}
            <span className="text-sm font-medium">{toast.message}</span>
            <button
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              className="ml-2 text-neutral-400 hover:text-neutral-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// ==================== CONFIRM MODAL SYSTEM ====================
const ConfirmContext = createContext(null);

const useConfirm = () => useContext(ConfirmContext);

const ConfirmProvider = ({ children }) => {
  const [confirmState, setConfirmState] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Da',
    cancelText: 'Odustani',
    onConfirm: null,
    variant: 'default' // 'default' | 'danger'
  });

  const confirm = ({ title, message, confirmText = 'Da', cancelText = 'Odustani', variant = 'default' }) => {
    return new Promise((resolve) => {
      setConfirmState({
        show: true,
        title,
        message,
        confirmText,
        cancelText,
        variant,
        onConfirm: () => {
          setConfirmState(prev => ({ ...prev, show: false }));
          resolve(true);
        }
      });
    });
  };

  const handleCancel = () => {
    setConfirmState(prev => ({ ...prev, show: false }));
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {confirmState.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4 modal-backdrop" onClick={handleCancel}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 modal-content" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">{confirmState.title}</h3>
            <p className="text-sm text-neutral-600 mb-6">{confirmState.message}</p>
            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className="flex-1 px-4 py-2.5 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-xl text-sm font-medium transition-colors"
              >
                {confirmState.cancelText}
              </button>
              <button
                onClick={confirmState.onConfirm}
                className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  confirmState.variant === 'danger'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-neutral-900 hover:bg-neutral-800 text-white'
                }`}
              >
                {confirmState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

// ==================== SHARED HEADER ====================
const PublicHeader = ({ current }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setDropdownOpen(false);
    if (dropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [dropdownOpen]);

  const moreMenuItems = ['players', 'news', 'h2h', 'pravilnik'];
  const isMoreActive = moreMenuItems.includes(current);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-neutral-200/50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center">
            <span className="text-lg font-semibold tracking-tight text-neutral-900">TenisCG<span className="text-rose-500">.me</span></span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className={`text-sm font-medium transition-colors ${current === 'home' ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-900'}`}>
              Početna
            </Link>
            <Link to="/rankings" className={`text-sm font-medium transition-colors ${current === 'rankings' ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-900'}`}>
              Rang Lista
            </Link>
            <Link to="/power-index" className={`text-sm font-medium transition-colors ${current === 'power-index' ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-900'}`}>
              Power Index
            </Link>
            <Link to="/tournaments" className={`text-sm font-medium transition-colors ${current === 'tournaments' ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-900'}`}>
              Turniri
            </Link>
            <Link to="/leagues" className={`text-sm font-medium transition-colors ${current === 'leagues' ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-900'}`}>
              Lige
            </Link>
            <Link to="/clubs" className={`text-sm font-medium transition-colors ${current === 'clubs' ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-900'}`}>
              Tereni
            </Link>

            {/* More Dropdown */}
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setDropdownOpen(!dropdownOpen); }}
                className={`text-sm font-medium transition-colors flex items-center gap-1 ${isMoreActive ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-900'}`}
              >
                Više
                <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border border-neutral-200/60 py-2 z-50 animate-dropdown">
                  <Link to="/players" onClick={() => setDropdownOpen(false)} className={`block px-4 py-2 text-sm transition-colors touch-feedback ${current === 'players' ? 'text-rose-600 bg-rose-50' : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'}`}>
                    Igrači
                  </Link>
                  <Link to="/head-to-head" onClick={() => setDropdownOpen(false)} className={`block px-4 py-2 text-sm transition-colors touch-feedback ${current === 'h2h' ? 'text-rose-600 bg-rose-50' : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'}`}>
                    Head to Head
                  </Link>
                  <Link to="/news" onClick={() => setDropdownOpen(false)} className={`block px-4 py-2 text-sm transition-colors touch-feedback ${current === 'news' ? 'text-rose-600 bg-rose-50' : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'}`}>
                    Vijesti
                  </Link>
                  <div className="my-1 border-t border-neutral-100" />
                  <Link to="/pravilnik" onClick={() => setDropdownOpen(false)} className={`block px-4 py-2 text-sm transition-colors touch-feedback ${current === 'pravilnik' ? 'text-rose-600 bg-rose-50' : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'}`}>
                    Pravilnik
                  </Link>
                </div>
              )}
            </div>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <Link to="/panel/dashboard" className="text-sm font-medium text-neutral-900">
                  {user.username}
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors flex items-center gap-1"
                >
                  <LogOut className="w-4 h-4" />
                  Odjava
                </button>
              </>
            ) : (
              <Link to="/login" className="text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors">
                Prijava
              </Link>
            )}
          </div>

          {/* Mobile Menu Button - Custom Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden p-2 w-10 h-10 flex flex-col justify-center items-center gap-1.5 ${mobileMenuOpen ? 'hamburger-open' : ''}`}
          >
            <span className="hamburger-line block w-5 h-0.5 bg-neutral-700 rounded-full"></span>
            <span className="hamburger-line block w-5 h-0.5 bg-neutral-700 rounded-full"></span>
            <span className="hamburger-line block w-5 h-0.5 bg-neutral-700 rounded-full"></span>
          </button>
        </div>

      </div>

      {/* Mobile Menu - Fixed overlay below header */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed top-16 left-0 right-0 bottom-0 z-40">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Menu */}
          <div className="absolute top-0 left-0 right-0 bg-white border-b border-neutral-200 shadow-lg animate-slide-down">
            <nav className="flex flex-col p-4 gap-1 max-w-6xl mx-auto">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${current === 'home' ? 'text-neutral-900 bg-neutral-100' : 'text-neutral-600'}`}>
                Početna
              </Link>
              <Link to="/rankings" onClick={() => setMobileMenuOpen(false)} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${current === 'rankings' ? 'text-neutral-900 bg-neutral-100' : 'text-neutral-600'}`}>
                Rang Lista
              </Link>
              <Link to="/power-index" onClick={() => setMobileMenuOpen(false)} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${current === 'power-index' ? 'text-neutral-900 bg-neutral-100' : 'text-neutral-600'}`}>
                Power Index
              </Link>
              <Link to="/tournaments" onClick={() => setMobileMenuOpen(false)} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${current === 'tournaments' ? 'text-neutral-900 bg-neutral-100' : 'text-neutral-600'}`}>
                Turniri
              </Link>
              <Link to="/leagues" onClick={() => setMobileMenuOpen(false)} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${current === 'leagues' ? 'text-neutral-900 bg-neutral-100' : 'text-neutral-600'}`}>
                Lige
              </Link>
              <Link to="/clubs" onClick={() => setMobileMenuOpen(false)} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${current === 'clubs' ? 'text-neutral-900 bg-neutral-100' : 'text-neutral-600'}`}>
                Tereni
              </Link>
              <div className="my-2 border-t border-neutral-100" />
              <Link to="/players" onClick={() => setMobileMenuOpen(false)} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${current === 'players' ? 'text-neutral-900 bg-neutral-100' : 'text-neutral-600'}`}>
                Igrači
              </Link>
              <Link to="/head-to-head" onClick={() => setMobileMenuOpen(false)} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${current === 'h2h' ? 'text-neutral-900 bg-neutral-100' : 'text-neutral-600'}`}>
                Head to Head
              </Link>
              <Link to="/news" onClick={() => setMobileMenuOpen(false)} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${current === 'news' ? 'text-neutral-900 bg-neutral-100' : 'text-neutral-600'}`}>
                Vijesti
              </Link>
              <Link to="/pravilnik" onClick={() => setMobileMenuOpen(false)} className={`px-3 py-2.5 rounded-lg text-sm font-medium ${current === 'pravilnik' ? 'text-neutral-900 bg-neutral-100' : 'text-neutral-600'}`}>
                Pravilnik
              </Link>
              <div className="my-2 border-t border-neutral-100" />
              {user ? (
                <>
                  <Link
                    to={user.is_super_admin ? '/panel/dashboard' : '/panel/leagues'}
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-3 py-2.5 rounded-lg text-sm font-medium text-neutral-900 bg-neutral-50 flex items-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    {user.first_name || user.username || 'Panel'}
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="px-3 py-2.5 rounded-lg text-sm font-medium text-rose-500 text-left flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Odjava
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="px-3 py-2.5 rounded-lg text-sm font-medium text-rose-500">
                  Prijava
                </Link>
              )}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

// ==================== SHARED FOOTER ====================
const PublicFooter = () => {
  const { user } = useAuth();

  return (
  <footer className="border-t border-neutral-200 bg-neutral-50">
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-12">
      <div className="flex flex-col md:flex-row justify-between items-start gap-8">
        <div>
          <div className="mb-3">
            <span className="text-lg font-semibold tracking-tight">TenisCG<span className="text-rose-500">.me</span></span>
          </div>
          <p className="text-sm text-neutral-500 max-w-xs">
            Platforma za praćenje rekreativnog tenisa u Crnoj Gori. Rangiranja, turniri, rezultati.
          </p>
        </div>
        <div className="flex gap-12 md:gap-16">
          <div>
            <h4 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-4">Navigacija</h4>
            <div className="space-y-3">
              <Link to="/rankings" className="block text-sm text-neutral-600 hover:text-neutral-900 transition-colors">Rang Lista</Link>
              <Link to="/tournaments" className="block text-sm text-neutral-600 hover:text-neutral-900 transition-colors">Turniri</Link>
              <Link to="/leagues" className="block text-sm text-neutral-600 hover:text-neutral-900 transition-colors">Lige</Link>
              <Link to="/players" className="block text-sm text-neutral-600 hover:text-neutral-900 transition-colors">Igrači</Link>
              <Link to="/news" className="block text-sm text-neutral-600 hover:text-neutral-900 transition-colors">Vijesti</Link>
            </div>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-4">Admin</h4>
            <div className="space-y-3">
              {user ? (
                <Link to="/panel/dashboard" className="block text-sm text-neutral-600 hover:text-neutral-900 transition-colors">Admin Panel</Link>
              ) : (
                <Link to="/login" className="block text-sm text-neutral-600 hover:text-neutral-900 transition-colors">Prijava</Link>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-neutral-200 mt-10 pt-6 flex flex-col md:flex-row items-center justify-between gap-2">
        <p className="text-xs text-neutral-400">© 2026 TenisCG.me - Rekreativni tenis Crne Gore</p>
        <p className="text-xs text-neutral-400">
          Dizajn i razvoj: <span className="text-neutral-600 font-medium">Seid Kršić</span>
        </p>
      </div>
    </div>
  </footer>
  );
};

// ==================== LANDING PAGE ====================
const Landing = () => {
  const [rankings, setRankings] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [featuredNews, setFeaturedNews] = useState([]);
  const [rankingsLoading, setRankingsLoading] = useState(true);
  const [newsLoading, setNewsLoading] = useState(true);
  const [stats, setStats] = useState({ players: 0, tournaments: 0, matches: 0 });
  const [ads, setAds] = useState({ homepage_top: [], homepage_bottom: [] });
  const navigate = useNavigate();

  useEffect(() => {
    // Track page visit
    analyticsAPI.trackVisit('/').catch(() => {});

    // Fetch ads
    adsAPI.getByPosition().then(res => {
      // Response is {data: {homepage_top: [], homepage_bottom: [], sidebar: []}}
      if (res.data?.data) setAds(res.data.data);
      else if (res.data) setAds(res.data);
    }).catch(() => {});

    // Fetch stats from optimized endpoint (just counts, not full data)
    publicStatsAPI.get().then(res => {
      if (res.data) setStats(res.data);
    }).catch(() => {});

    // Fetch recent matches from optimized endpoint (only 5 matches, not all)
    matchesAPI.getRecent(5).then(res => {
      const mData = Array.isArray(res.data) ? res.data : [];
      setRecentMatches(mData);
    }).catch(() => {});

    // Fetch display data independently - a slow request (e.g. news) must not
    // hold back sections that have already loaded (e.g. the ranking list).
    rankingsAPI.getAll()
      .then(r => setRankings(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setRankingsLoading(false));

    tournamentsAPI.getAllPublic()
      .then(t => setTournaments(Array.isArray(t.data) ? t.data : []))
      .catch(() => {});

    newsAPI.getFeatured()
      .then(n => setFeaturedNews(Array.isArray(n.data) ? n.data : []))
      .catch(() => {})
      .finally(() => setNewsLoading(false));
  }, []);

  const ongoingTournament = tournaments.find(t => t.status === 'ongoing');
  const upcomingTournaments = tournaments.filter(t => t.status === 'upcoming').slice(0, 2);
  const pastTournaments = tournaments.filter(t => t.status === 'finished' || t.status === 'archived').slice(0, 3);

  return (
    <>
      <SEO
        title="TenisCG.me | Rekreativni tenis Crne Gore"
        description="Platforma za rekreativni tenis u Crnoj Gori. Rang liste, turniri, lige, rezultati mečeva i statistike igrača."
        url="/"
      />
      <div className="min-h-screen bg-white">
        <PublicHeader current="home" />

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-6 relative overflow-hidden">
          {/* Subtle rose gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-rose-50/50 via-white to-white pointer-events-none" />
          <div className="max-w-4xl mx-auto text-center relative">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-neutral-900 mb-6">
              Rekreativni tenis<br />
              <span className="bg-gradient-to-r from-neutral-400 to-rose-300 bg-clip-text text-transparent">Crne Gore</span>
            </h1>
            <p className="text-xl text-neutral-500 max-w-2xl mx-auto mb-10">
              Prati rangiranja, turnire i rezultate mečeva.
            Jedna platforma za cijelu tenisku zajednicu.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Link to="/rankings" className="w-full sm:w-auto text-center bg-neutral-900 text-white px-8 py-4 rounded-full font-medium hover:bg-neutral-800 transition-colors">
              Pogledaj Rang Listu
            </Link>
            <Link to="/tournaments" className="w-full sm:w-auto text-center bg-rose-50 text-rose-900 px-8 py-4 rounded-full font-medium hover:bg-rose-100 border border-rose-100 transition-colors">
              Svi Turniri
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-16 border-y border-neutral-100 bg-gradient-to-r from-neutral-50 via-rose-50/30 to-neutral-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-3 gap-4 md:gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-5xl font-bold text-neutral-900 mb-1 md:mb-2">{stats.players}</div>
              <div className="text-xs md:text-sm text-neutral-500 font-medium">Aktivnih igrača</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-neutral-900 to-rose-600 bg-clip-text text-transparent mb-1 md:mb-2">{stats.tournaments}</div>
              <div className="text-xs md:text-sm text-neutral-500 font-medium">Turnira</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-5xl font-bold text-neutral-900 mb-1 md:mb-2">{stats.matches}</div>
              <div className="text-xs md:text-sm text-neutral-500 font-medium">Odigranih mečeva</div>
            </div>
          </div>
        </div>
      </section>

      {/* News Section - ispod stats */}
      {(newsLoading || featuredNews.length > 0) && (
        <section className="py-12 md:py-16 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-1">Vijesti</h2>
                <p className="text-sm text-neutral-500">Najnovije iz svijeta tenisa</p>
              </div>
              <Link to="/news" className="text-sm font-medium text-neutral-500 hover:text-neutral-900 flex items-center gap-1 transition-colors">
                Sve vijesti <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {newsLoading ? [...Array(3)].map((_, i) => (
                <div key={`nsk-${i}`} className="bg-neutral-50 rounded-2xl overflow-hidden">
                  <Skeleton className="aspect-video w-full" />
                  <div className="p-4 space-y-2">
                    <Skeleton className="h-3 w-1/3" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
              )) : featuredNews.slice(0, 3).map(article => (
                <Link
                  key={article.id}
                  to={`/news/${article.id}`}
                  className="group bg-neutral-50 rounded-2xl overflow-hidden hover:bg-rose-50/50 transition-all"
                >
                  {article.image_url ? (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-rose-100 to-neutral-100 flex items-center justify-center">
                      <Newspaper className="w-10 h-10 text-neutral-300" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="text-xs text-neutral-400 mb-2">
                      {new Date(article.created_at).toLocaleDateString('sr-Latn-ME', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                    <h3 className="font-semibold text-neutral-900 line-clamp-2 group-hover:text-rose-600 transition-colors">
                      {article.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Top Ad Banner */}
      {ads.homepage_top && ads.homepage_top.length > 0 && (
        <section className="py-4 px-6">
          <div className="max-w-4xl mx-auto">
            {ads.homepage_top.map(ad => (
              <a
                key={ad.id}
                href={ad.link_url || '#'}
                target={ad.link_url ? '_blank' : '_self'}
                rel="noopener noreferrer"
                onClick={() => ad.link_url && adsAPI.trackClick(ad.id).catch(() => {})}
                className="block relative overflow-hidden rounded-xl bg-neutral-100 hover:shadow-lg transition-shadow h-24 md:h-28"
              >
                <img
                  src={ad.image_url}
                  alt={ad.name}
                  className="w-full h-full object-cover"
                />
                <span className="absolute bottom-1.5 right-1.5 text-[9px] text-neutral-400 bg-white/80 px-1.5 py-0.5 rounded">
                  Reklama
                </span>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Live Tournament Banner */}
      {ongoingTournament && (
        <section className="py-12 px-6">
          <div className="max-w-4xl mx-auto">
            <div
              onClick={() => navigate(`/tournaments/${ongoingTournament.id}`)}
              className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-3xl p-10 text-white cursor-pointer hover:scale-[1.01] transition-transform"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <span className="text-sm font-medium text-neutral-400 uppercase tracking-wider">Turnir u toku</span>
              </div>
              <h2 className="text-3xl font-bold mb-2">{ongoingTournament.name}</h2>
              <p className="text-neutral-400 mb-6">{ongoingTournament.location} • {ongoingTournament.start_date}</p>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-white">
                Pogledaj rezultate <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </section>
      )}

      {/* Rankings Section */}
      <section className="py-12 md:py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 md:mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-1 md:mb-2">Rang Lista</h2>
              <p className="text-sm md:text-base text-neutral-500">ATP stil bodovanja • Sistem 365 dana</p>
            </div>
            <Link to="/rankings" className="text-sm font-medium text-neutral-500 hover:text-neutral-900 flex items-center gap-1 transition-colors">
              Kompletna lista <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="border border-neutral-200 rounded-2xl overflow-hidden bg-white">
            {rankingsLoading ? [...Array(5)].map((_, i) => (
              <div key={`rsk-${i}`} className={`flex items-center gap-3 md:gap-4 px-4 md:px-6 py-4 md:py-5 ${i !== 4 ? 'border-b border-neutral-100' : ''}`}>
                <Skeleton variant="avatar" className="w-8 h-8" />
                <Skeleton variant="avatar" className="w-10 h-10 md:w-12 md:h-12" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
                <Skeleton className="h-6 w-12" />
              </div>
            )) : rankings.filter(r => r.current_rank && r.total_points > 0).slice(0, 5).map((r, idx) => (
              <div
                key={r.id || idx}
                onClick={() => r.player?.id && navigate(`/players/${r.player.id}`)}
                className={`flex items-center gap-3 md:gap-4 px-4 md:px-6 py-4 md:py-5 hover:bg-rose-50/50 transition-colors cursor-pointer ${idx !== 4 ? 'border-b border-neutral-100' : ''}`}
              >
                <div className="flex-shrink-0">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${
                    idx === 0 ? 'bg-gradient-to-br from-amber-400 to-rose-400 text-white' :
                    idx === 1 ? 'bg-neutral-300 text-neutral-700' :
                    idx === 2 ? 'bg-amber-600 text-white' :
                    'bg-neutral-100 text-neutral-600'
                  }`}>{r.current_rank}</span>
                </div>
                {r.player?.photo ? (
                  <img src={r.player.photo} alt={r.player.full_name} className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                    {(r.player?.first_name || '?')[0]}{(r.player?.last_name || '?')[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-900 text-sm md:text-base truncate">{r.player?.full_name || 'Nepoznat'}</div>
                  <div className="text-xs md:text-sm text-neutral-500">{r.player?.country || 'Crna Gora'}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-lg md:text-xl font-bold text-neutral-900">{r.total_points}</span>
                  <span className="text-xs text-neutral-400 ml-1 md:hidden">bod.</span>
                </div>
              </div>
            ))}
            {!rankingsLoading && rankings.length === 0 && (
              <div className="py-16 text-center text-neutral-400">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Nema podataka o rangiranju</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Tournaments Grid */}
      <section className="py-12 md:py-20 px-6 bg-neutral-50">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 md:mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-1 md:mb-2">Turniri</h2>
              <p className="text-sm md:text-base text-neutral-500">Predstojeći i završeni turniri</p>
            </div>
            <Link to="/tournaments" className="text-sm font-medium text-neutral-500 hover:text-neutral-900 flex items-center gap-1 transition-colors">
              Svi turniri <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcomingTournaments.map(t => (
              <div
                key={t.id}
                onClick={() => navigate(`/tournaments/${t.id}`)}
                className="bg-white rounded-2xl p-6 cursor-pointer hover:shadow-lg transition-all border border-neutral-200"
              >
                <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-3">Uskoro</div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-2">{t.name}</h3>
                <div className="flex items-center gap-4 text-sm text-neutral-500">
                  {t.location && (
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" /> {t.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" /> {t.start_date}
                  </span>
                </div>
              </div>
            ))}
            {pastTournaments.map(t => (
              <div
                key={t.id}
                onClick={() => navigate(`/tournaments/${t.id}`)}
                className="bg-white rounded-2xl p-6 cursor-pointer hover:shadow-lg transition-all border border-neutral-200"
              >
                <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-3">Završen</div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-2">{t.name}</h3>
                <div className="flex items-center gap-4 text-sm text-neutral-500 mb-3">
                  {t.location && (
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" /> {t.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" /> {t.start_date}
                  </span>
                </div>
                {t.winner_name && (
                  <div className="flex items-center gap-2 text-sm text-amber-600 font-medium">
                    <Trophy className="w-4 h-4" />
                    {t.winner_name}
                  </div>
                )}
              </div>
            ))}
            {tournaments.length === 0 && (
              <div className="col-span-full py-16 text-center text-neutral-400">
                <Trophy className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Nema turnira</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Recent Matches */}
      {recentMatches.length > 0 && (
        <section className="py-12 md:py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8 md:mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-1 md:mb-2">Posljednji Rezultati</h2>
              <p className="text-sm md:text-base text-neutral-500">Nedavno završeni mečevi</p>
            </div>

            <div className="space-y-3 md:space-y-4">
              {recentMatches.map((m) => {
                return (
                  <div key={m.id} className="p-4 md:p-5 bg-neutral-50 rounded-2xl hover:bg-rose-50/50 transition-colors">
                    {/* Tournament/League/Round info */}
                    <div className="flex items-center gap-2 mb-2 md:mb-3">
                      {m.tournament_name && (
                        <span className="text-xs font-medium text-rose-500 uppercase tracking-wider truncate">
                          {m.tournament_name}
                        </span>
                      )}
                      {m.league_name && (
                        <span className="text-xs font-medium text-blue-500 uppercase tracking-wider truncate">
                          {m.league_name}
                        </span>
                      )}
                      {m.round && (
                        <>
                          <span className="text-neutral-300">•</span>
                          <span className="text-xs text-neutral-400 capitalize flex-shrink-0">
                            {m.round === 'final' ? 'Finale' :
                             m.round === 'semifinal' ? 'Polufinale' :
                             m.round === 'quarterfinal' ? 'Četvrtfinale' :
                             m.round === 'round_of_16' ? 'Osmina finala' :
                             m.round === 'round_of_32' ? 'Šesnaestina finala' :
                             m.round === 'round_1' ? 'Prvo kolo' :
                             m.round === 'group' ? 'Grupna faza' : m.round}
                          </span>
                        </>
                      )}
                    </div>
                    {/* Match info - Mobile: stacked, Desktop: horizontal */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
                      <div className="flex items-center justify-between md:justify-start md:flex-1 gap-2 md:gap-4">
                        <div className="flex-1 md:flex-none md:text-right md:w-40">
                          <span className={`text-sm md:text-base font-medium ${m.winner?.id === m.player1?.id ? 'text-neutral-900' : 'text-neutral-400'}`}>
                            {m.player1?.full_name || 'TBD'}
                          </span>
                        </div>
                        <div className="text-xs md:text-sm text-neutral-300 font-medium flex-shrink-0">vs</div>
                        <div className="flex-1 md:flex-none text-right md:text-left md:w-40">
                          <span className={`text-sm md:text-base font-medium ${m.winner?.id === m.player2?.id ? 'text-neutral-900' : 'text-neutral-400'}`}>
                            {m.player2?.full_name || 'TBD'}
                          </span>
                        </div>
                      </div>
                      <div className="font-mono font-semibold text-neutral-900 text-center md:text-right text-sm md:text-base">
                        {m.score_summary && m.score_summary !== 'Bez rezultata' ? m.score_summary : '-'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Bottom Ad Banner */}
      {ads.homepage_bottom && ads.homepage_bottom.length > 0 && (
        <section className="py-4 px-6">
          <div className="max-w-4xl mx-auto">
            {ads.homepage_bottom.map(ad => (
              <a
                key={ad.id}
                href={ad.link_url || '#'}
                target={ad.link_url ? '_blank' : '_self'}
                rel="noopener noreferrer"
                onClick={() => ad.link_url && adsAPI.trackClick(ad.id).catch(() => {})}
                className="block relative overflow-hidden rounded-xl bg-neutral-100 hover:shadow-lg transition-shadow h-24 md:h-28"
              >
                <img
                  src={ad.image_url}
                  alt={ad.name}
                  className="w-full h-full object-cover"
                />
                <span className="absolute bottom-1.5 right-1.5 text-[9px] text-neutral-400 bg-white/80 px-1.5 py-0.5 rounded">
                  Reklama
                </span>
              </a>
            ))}
          </div>
        </section>
      )}

        <PublicFooter />
      </div>
    </>
  );
};

// ==================== LOGIN PAGE ====================
const Login = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      // Non-super-admins go directly to leagues (they can't see dashboard anyway)
      navigate(user.is_super_admin ? '/panel/dashboard' : '/panel/leagues');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const loggedInUser = await login(form.username, form.password);
      // Non-super-admins go directly to leagues
      navigate(loggedInUser.is_super_admin ? '/panel/dashboard' : '/panel/leagues');
    } catch (err) {
      const errorMsg = err.response?.data?.non_field_errors?.[0] ||
                       err.response?.data?.error ||
                       'Pogrešni podaci za prijavu';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6 animate-fade-in">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <span className="text-2xl font-semibold tracking-tight text-neutral-900">
              TenisCG<span className="text-rose-500">.me</span>
            </span>
          </Link>
          <p className="text-neutral-500 text-sm mt-2">Prijava u admin panel</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl border border-neutral-200/60 shadow-sm overflow-hidden">
          <div className="p-6 md:p-8 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center gap-3">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-red-600 text-sm font-medium">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Korisničko ime</label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                  placeholder="Unesite korisničko ime"
                  autoComplete="username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Lozinka</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                  placeholder="Unesite lozinku"
                  autoComplete="current-password"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-neutral-900 hover:bg-neutral-800 text-white py-3.5 rounded-xl font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Prijavljivanje...
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    Prijavi se
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <Link to="/" className="flex items-center justify-center gap-2 mt-6 text-sm text-neutral-500 hover:text-neutral-900 font-medium transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Nazad na početnu
        </Link>
      </div>
    </div>
  );
};

// ==================== SIDEBAR ====================
const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const { user, logout } = useAuth();

  // Check which submenu should be open based on current path
  useEffect(() => {
    if (location.pathname.includes('/panel/tournaments') || location.pathname.includes('/panel/leagues')) {
      setOpenSubmenu('takmicenja');
    } else if (location.pathname.includes('/panel/news') || location.pathname.includes('/panel/ads')) {
      setOpenSubmenu('sadrzaj');
    } else if (location.pathname.includes('/panel/users') || location.pathname.includes('/panel/activity') || location.pathname.includes('/panel/settings') || location.pathname.includes('/panel/statistics')) {
      setOpenSubmenu('sistem');
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const NavItem = ({ item, onClick }) => {
    const isActive = location.pathname === item.path ||
      (item.path !== '/panel/dashboard' && location.pathname.startsWith(item.path));
    return (
      <Link to={item.path} onClick={onClick}>
        <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
          isActive
            ? 'bg-neutral-900 text-white'
            : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
        }`}>
          <item.icon className={`w-4 h-4 ${isActive ? 'text-rose-400' : ''}`} />
          {item.name}
        </div>
      </Link>
    );
  };

  const Submenu = ({ title, icon: Icon, id, items }) => {
    const isOpen = openSubmenu === id;
    const hasActiveChild = items.some(item =>
      location.pathname === item.path || location.pathname.startsWith(item.path)
    );

    return (
      <div>
        <button
          onClick={() => setOpenSubmenu(isOpen ? null : id)}
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
            hasActiveChild ? 'text-neutral-900 bg-neutral-100' : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
          }`}
        >
          <div className="flex items-center gap-3">
            <Icon className="w-4 h-4" />
            {title}
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div className="ml-4 mt-1 space-y-1 border-l border-neutral-200 pl-3">
            {items.map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path);
              return (
                <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}>
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                    isActive
                      ? 'text-rose-600 font-medium bg-rose-50'
                      : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50'
                  }`}>
                    <item.icon className="w-4 h-4" />
                    {item.name}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const SidebarContent = ({ isMobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Hide logo section on mobile since header already has it */}
      {!isMobile && (
        <div className="p-6 border-b border-neutral-100 flex-shrink-0">
          <Link to="/panel/dashboard" className="flex items-center" onClick={() => setMobileOpen(false)}>
            <span className="text-lg font-semibold tracking-tight text-neutral-900">
              TenisCG<span className="text-rose-500">.me</span>
            </span>
          </Link>
          <p className="text-xs text-neutral-400 mt-1">Admin Panel</p>
        </div>
      )}

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {/* Main Items - Super Admin Only */}
        {user?.is_super_admin && (
          <>
            <NavItem item={{ name: 'Početna', path: '/panel/dashboard', icon: Home }} onClick={() => setMobileOpen(false)} />
            <NavItem item={{ name: 'Igrači', path: '/panel/players', icon: Users }} onClick={() => setMobileOpen(false)} />
            <NavItem item={{ name: 'Rang Lista', path: '/panel/rankings', icon: BarChart3 }} onClick={() => setMobileOpen(false)} />
            <NavItem item={{ name: 'Power Index', path: '/panel/power-index', icon: Activity }} onClick={() => setMobileOpen(false)} />
            <NavItem item={{ name: 'Tereni', path: '/panel/clubs', icon: MapPin }} onClick={() => setMobileOpen(false)} />
            <div className="pt-2 mt-2 border-t border-neutral-100" />
          </>
        )}

        {/* Takmičenja Submenu - visible to all */}
        <Submenu
          title="Takmičenja"
          icon={Trophy}
          id="takmicenja"
          items={[
            { name: 'Turniri', path: '/panel/tournaments', icon: Trophy },
            { name: 'Lige', path: '/panel/leagues', icon: Calendar },
          ]}
        />

        {/* Super Admin Only Sections */}
        {user?.is_super_admin && (
          <Submenu
            title="Sadržaj"
            icon={Newspaper}
            id="sadrzaj"
            items={[
              { name: 'Vijesti', path: '/panel/news', icon: Newspaper },
              { name: 'Reklame', path: '/panel/ads', icon: Image },
            ]}
          />
        )}

        {/* Sistem Submenu */}
        <Submenu
          title="Sistem"
          icon={Settings}
          id="sistem"
          items={[
            ...(user?.is_super_admin ? [
              { name: 'Korisnici', path: '/panel/users', icon: Shield },
              { name: 'Aktivnosti', path: '/panel/activity', icon: Clock },
              { name: 'Statistika', path: '/panel/statistics', icon: BarChart3 },
            ] : []),
            { name: 'Postavke', path: '/panel/settings', icon: Settings },
          ]}
        />
      </nav>

      <div className="p-4 border-t border-neutral-100 flex-shrink-0">
        <Link to="/" className="block mb-2" onClick={() => setMobileOpen(false)}>
          <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 transition-all">
            <ArrowLeft className="w-4 h-4" /> Javni sajt
          </div>
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm text-rose-500 hover:bg-rose-50 transition-all"
        >
          <LogOut className="w-4 h-4" /> Odjavi se
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-neutral-200/50">
        <div className="flex items-center justify-between px-4 h-16">
          <span className="text-lg font-semibold tracking-tight text-neutral-900">
            TenisCG<span className="text-rose-500">.me</span>
          </span>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-neutral-600">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" onClick={() => setMobileOpen(false)}>
          <aside className="fixed left-0 top-16 bottom-0 w-72 bg-white shadow-xl flex flex-col overflow-y-auto" onClick={e => e.stopPropagation()}>
            <SidebarContent isMobile={true} />
          </aside>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-screen w-64 bg-white/80 backdrop-blur-xl border-r border-neutral-200/50 flex-col">
        <SidebarContent />
      </aside>
    </>
  );
};

// ==================== ADMIN LAYOUT ====================
const AdminLayout = ({ children }) => (
  <div className="min-h-screen bg-neutral-50">
    <Sidebar />
    <main className="md:ml-64 pt-20 md:pt-8 px-4 md:px-8 pb-8 animate-fade-in">{children}</main>
  </div>
);

// ==================== DASHBOARD ====================
const Dashboard = () => {
  const [stats, setStats] = useState({ players: 0, tournaments: 0, leagues: 0, matches: 0 });
  const [rankings, setRankings] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    // Fetch dashboard stats from dedicated endpoint (filtered by user role)
    authAPI.getDashboardStats()
      .then(response => {
        setStats(response.data);
      })
      .catch(err => console.error('Dashboard stats error:', err));

    // Fetch rankings and recent matches
    Promise.all([
      matchesAPI.getAll(),
      rankingsAPI.getTop(5),
    ]).then(([m, r]) => {
      // Rankings - r.data is the array directly
      const rankingsData = Array.isArray(r.data) ? r.data : (Array.isArray(r) ? r : []);
      setRankings(rankingsData);
      // Sort by updated_at descending (recently updated first), filter out BYE matches, and take 5
      const sortedMatches = Array.isArray(m.data)
        ? [...m.data]
            .filter(match => match.status === 'completed' && match.player1 && match.player2)
            .sort((a, b) => new Date(b.updated_at || b.match_date) - new Date(a.updated_at || a.match_date))
            .slice(0, 5)
        : [];
      setRecentMatches(sortedMatches);
    }).catch(err => console.error('Dashboard load error:', err));
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Dobrodošli nazad</h1>
        <p className="text-neutral-500 mt-1">Pregled aktivnosti na platformi</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-neutral-200/60">
          <div className="text-3xl font-semibold text-neutral-900">{stats.players}</div>
          <div className="text-sm text-neutral-500 mt-1">Igrača</div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-neutral-200/60">
          <div className="text-3xl font-semibold text-neutral-900">{stats.tournaments}</div>
          <div className="text-sm text-neutral-500 mt-1">Turnira</div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-neutral-200/60">
          <div className="text-3xl font-semibold text-neutral-900">{stats.leagues}</div>
          <div className="text-sm text-neutral-500 mt-1">Liga</div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-neutral-200/60">
          <div className="text-3xl font-semibold text-neutral-900">{stats.matches}</div>
          <div className="text-sm text-neutral-500 mt-1">Mečeva</div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Rankings */}
        <div className="bg-white rounded-2xl border border-neutral-200/60">
          <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
            <span className="font-medium text-neutral-900">Rang lista</span>
            <Link to="/panel/rankings" className="text-sm text-rose-500 hover:text-rose-600">
              Prikaži sve
            </Link>
          </div>
          {rankings.length > 0 ? (
            <div className="divide-y divide-neutral-100">
              {rankings.map((r, idx) => (
                <div key={r.id || idx} className="px-5 py-3 flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-medium ${
                    idx === 0 ? 'bg-rose-500 text-white' :
                    idx === 1 ? 'bg-neutral-200 text-neutral-700' :
                    idx === 2 ? 'bg-amber-100 text-amber-700' :
                    'bg-neutral-100 text-neutral-600'
                  }`}>{idx + 1}</span>
                  {r.player?.photo ? (
                    <img src={r.player.photo} alt={r.player.full_name} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                      {(r.player?.first_name || '?')[0]}{(r.player?.last_name || '?')[0]}
                    </div>
                  )}
                  <span className="flex-1 text-neutral-900 truncate text-sm">{r.player?.full_name || 'Nepoznat'}</span>
                  <span className="text-sm text-neutral-500">{r.total_points || 0} bod.</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-neutral-400 text-sm">Nema podataka</div>
          )}
        </div>

        {/* Recent Matches */}
        <div className="bg-white rounded-2xl border border-neutral-200/60">
          <div className="px-5 py-4 border-b border-neutral-100">
            <span className="font-medium text-neutral-900">Nedavni mečevi</span>
          </div>
          {recentMatches.length > 0 ? (
            <div className="divide-y divide-neutral-100">
              {recentMatches.map((m) => (
                <div key={m.id} className="px-4 py-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-neutral-900 truncate">
                        {m.player1_name || m.player1?.full_name || 'TBD'} vs {m.player2_name || m.player2?.full_name || 'TBD'}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {m.tournament_name && (
                          <span className="text-xs text-rose-500 font-medium truncate max-w-[120px]">{m.tournament_name}</span>
                        )}
                        {m.league_name && (
                          <span className="text-xs text-blue-500 font-medium truncate max-w-[120px]">{m.league_name}</span>
                        )}
                        {m.round && (
                          <span className="text-xs text-neutral-400">• {
                            m.round === 'final' ? 'Finale' :
                            m.round === 'semifinal' ? 'Polufinale' :
                            m.round === 'quarterfinal' ? 'Četvrtfinale' :
                            m.round === 'round_of_16' ? 'Osmina finala' :
                            m.round === 'round_of_32' ? 'Šesnaestina finala' :
                            m.round === 'round_1' ? 'Prvo kolo' : m.round
                          }</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {m.status === 'completed' && m.score_summary && m.score_summary !== 'Bez rezultata' && (
                        <span className="text-sm font-medium text-neutral-700">{m.score_summary}</span>
                      )}
                      <span className={`text-xs px-2 py-1 rounded-md whitespace-nowrap ${
                        m.status === 'completed' ? 'bg-green-50 text-green-600' :
                        m.status === 'ongoing' ? 'bg-amber-50 text-amber-600' :
                        'bg-neutral-100 text-neutral-500'
                      }`}>
                        {m.status === 'completed' ? 'Završen' : m.status === 'ongoing' ? 'U toku' : 'Zakazan'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-neutral-400 text-sm">Nema mečeva</div>
          )}
        </div>
      </div>

      {/* Quick Links */}
      <div className="flex flex-wrap gap-3">
        <Link to="/panel/players/new" className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-xl text-sm hover:bg-neutral-800 transition-colors">
          <Plus className="w-4 h-4" /> Dodaj igrača
        </Link>
        {user?.is_super_admin && (
          <Link to="/panel/news/new" className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 text-neutral-700 rounded-xl text-sm hover:bg-neutral-50 transition-colors">
            <Newspaper className="w-4 h-4" /> Nova vijest
          </Link>
        )}
        <Link to="/panel/tournaments" className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 text-neutral-700 rounded-xl text-sm hover:bg-neutral-50 transition-colors">
          <Trophy className="w-4 h-4" /> Turniri
        </Link>
      </div>
    </div>
  );
};

// ==================== PLAYERS LIST ====================
const Players = () => {
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ show: false, player: null });
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    playersAPI.getAll()
      .then(res => { setPlayers(Array.isArray(res.data) ? res.data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = players.filter(p =>
    `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  const openDeleteModal = (player, e) => {
    e.stopPropagation();
    setDeleteModal({ show: true, player });
  };

  const handleDelete = async () => {
    if (deleteModal.player) {
      try {
        await playersAPI.delete(deleteModal.player.id);
        setPlayers(players.filter(p => p.id !== deleteModal.player.id));
        toast.success('Igrač uspješno obrisan');
      } catch (err) {
        toast.error(err.response?.data?.error || 'Greška pri brisanju igrača');
      }
      setDeleteModal({ show: false, player: null });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Igrači</h1>
          <p className="text-neutral-500 mt-1">Upravljanje profilima igrača</p>
        </div>
        <Link to="/panel/players/new" className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-xl text-sm hover:bg-neutral-800 transition-colors">
          <Plus className="w-4 h-4" /> Dodaj igrača
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <input
          type="text"
          placeholder="Pretraži igrače..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-11 pr-4 py-2.5 bg-white border border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-neutral-300 transition-colors"
        />
      </div>

      {/* Players List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-neutral-300 border-t-neutral-600" />
        </div>
      ) : filtered.length > 0 ? (
        <div className="bg-white rounded-2xl border border-neutral-200/60 divide-y divide-neutral-100">
          {filtered.map((player) => (
            <div
              key={player.id}
              onClick={() => navigate(`/panel/players/${player.id}`)}
              className="flex items-center gap-4 px-5 py-4 hover:bg-neutral-50 transition-colors cursor-pointer"
            >
              {player.photo ? (
                <img src={player.photo} alt={`${player.first_name} ${player.last_name}`} className="w-10 h-10 rounded-xl object-cover" />
              ) : (
                <div className="w-10 h-10 bg-neutral-100 rounded-xl flex items-center justify-center text-neutral-600 text-sm font-medium">
                  {(player.first_name || '?')[0]}{(player.last_name || '?')[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-neutral-900 font-medium">{player.first_name} {player.last_name}</div>
                <div className="text-sm text-neutral-500">
                  {player.country || 'Crna Gora'} · {player.handedness === 'R' ? 'Dešnjak' : 'Ljevak'}
                </div>
              </div>
              <button
                onClick={(e) => openDeleteModal(player, e)}
                className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-neutral-400">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nema pronađenih igrača</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" onClick={() => setDeleteModal({ show: false, player: null })}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">Obriši igrača</h3>
              <p className="text-neutral-500 text-sm mb-6">
                Da li ste sigurni da želite obrisati igrača <span className="font-medium text-neutral-700">{deleteModal.player?.first_name} {deleteModal.player?.last_name}</span>? Ova akcija se ne može poništiti.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteModal({ show: false, player: null })}
                  className="flex-1 px-4 py-2.5 border border-neutral-200 text-neutral-700 rounded-xl text-sm hover:bg-neutral-50 transition-colors"
                >
                  Otkaži
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm hover:bg-red-600 transition-colors"
                >
                  Obriši
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== PLAYER FORM ====================
const PlayerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const [form, setForm] = useState({ first_name: '', last_name: '', country: 'Crna Gora', city: '', date_of_birth: '', handedness: 'R', bio: '' });
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    if (id) {
      playersAPI.get(id).then(res => {
        setForm(res.data);
        if (res.data.photo) setPhotoPreview(res.data.photo);
      }).catch(() => navigate('/panel/players'));
    }
  }, [id, navigate]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('first_name', form.first_name);
      formData.append('last_name', form.last_name);
      formData.append('country', form.country || '');
      formData.append('city', form.city || '');
      // Only send date_of_birth if it has a value (avoid empty string)
      if (form.date_of_birth) {
        formData.append('date_of_birth', form.date_of_birth);
      }
      formData.append('handedness', form.handedness);
      formData.append('bio', form.bio || '');
      // IMPORTANT: Always send is_active=true to keep player visible
      formData.append('is_active', 'true');
      if (photoFile) {
        formData.append('photo', photoFile);
      }

      if (id) {
        await playersAPI.update(id, formData);
        toast.success('Igrač uspješno ažuriran');
      } else {
        await playersAPI.create(formData);
        toast.success('Igrač uspješno kreiran');
      }
      navigate('/panel/players');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri čuvanju igrača');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-lg space-y-6">
      <button onClick={() => navigate('/panel/players')} className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 text-sm">
        <ArrowLeft className="w-4 h-4" /> Nazad na igrače
      </button>

      <div>
        <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">{id ? 'Uredi igrača' : 'Novi igrač'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-neutral-200/60 p-6 space-y-5">
        {/* Photo Upload */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">Fotografija</label>
          <div className="flex items-center gap-4">
            {photoPreview ? (
              <img src={photoPreview} alt="Preview" className="w-20 h-20 rounded-full object-cover border-2 border-neutral-200" />
            ) : (
              <div className="w-20 h-20 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white text-xl font-bold">
                {(form.first_name || '?')[0]}{(form.last_name || '?')[0]}
              </div>
            )}
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
                id="photo-upload"
              />
              <label
                htmlFor="photo-upload"
                className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-lg text-sm font-medium cursor-pointer transition-colors"
              >
                <Image className="w-4 h-4" />
                {photoPreview ? 'Promijeni sliku' : 'Dodaj sliku'}
              </label>
              {photoPreview && (
                <button
                  type="button"
                  onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                  className="ml-2 text-sm text-red-500 hover:text-red-700"
                >
                  Ukloni
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Ime</label>
            <input
              type="text"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Prezime</label>
            <input
              type="text"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
              required
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Datum rođenja</label>
            <input
              type="date"
              value={form.date_of_birth || ''}
              onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
              className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Grad</label>
            <input
              type="text"
              value={form.city || ''}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
              placeholder="npr. Podgorica"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Država</label>
            <input
              type="text"
              value={form.country || ''}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
              className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Ruka</label>
            <select
              value={form.handedness}
              onChange={(e) => setForm({ ...form, handedness: e.target.value })}
              className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
            >
              <option value="R">Dešnjak</option>
              <option value="L">Ljevak</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1.5">Biografija</label>
          <textarea
            value={form.bio || ''}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            rows={3}
            className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300 resize-none"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-neutral-900 hover:bg-neutral-800 text-white py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
        >
          {loading ? 'Čuvanje...' : 'Sačuvaj igrača'}
        </button>
      </form>
    </div>
  );
};

// ==================== PLAYER DETAIL ====================
const PlayerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    playersAPI.get(id).then(res => setPlayer(res.data)).catch(() => navigate('/panel/players'));
    playersAPI.getStats(id).then(res => setStats(res.data)).catch(() => {});
  }, [id, navigate]);

  if (!player) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-6 w-6 border-2 border-neutral-300 border-t-neutral-600" />
    </div>
  );

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 text-sm">
        <ArrowLeft className="w-4 h-4" /> Nazad
      </button>

      {/* Player Header */}
      <div className="bg-white rounded-2xl border border-neutral-200/60 p-6">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          {player.photo ? (
            <img src={player.photo} alt={`${player.first_name} ${player.last_name}`} className="w-20 h-20 rounded-2xl object-cover" />
          ) : (
            <div className="w-20 h-20 bg-neutral-100 rounded-2xl flex items-center justify-center text-neutral-600 text-2xl font-semibold">
              {player.first_name[0]}{player.last_name[0]}
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">{player.first_name} {player.last_name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-neutral-500 text-sm">
              {player.country && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" /> {player.country}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Hand className="w-4 h-4" /> {player.handedness === 'R' ? 'Dešnjak' : 'Ljevak'}
              </span>
            </div>
            {player.bio && <p className="mt-3 text-neutral-600 text-sm">{player.bio}</p>}
          </div>
          <Link to={`/panel/players/${id}/edit`}>
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 text-neutral-700 rounded-xl text-sm hover:bg-neutral-50 transition-colors">
              <Edit className="w-4 h-4" /> Uredi
            </button>
          </Link>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: 'Power Index', value: stats.power_index ? Number(stats.power_index.power_index).toFixed(2) : '-', icon: Activity },
            { label: 'Trenutni rang', value: stats.current_rank || '-', icon: Medal },
            { label: 'Ukupno bodova', value: stats.total_points || 0, icon: Star },
            { label: 'Pobjeda', value: stats.matches_won || 0, icon: Trophy },
            { label: 'Turnira', value: stats.tournaments_played || 0, icon: Calendar },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 border border-neutral-200/60">
              <s.icon className="w-6 h-6 text-neutral-400 mb-2" />
              <div className="text-2xl font-semibold text-neutral-900">{s.value}</div>
              <div className="text-sm text-neutral-500">{s.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== RANKINGS ====================
const Rankings = () => {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    rankingsAPI.getAll()
      .then(res => { setRankings(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleRecalculate = async () => {
    setLoading(true);
    await rankingsAPI.recalculate();
    const res = await rankingsAPI.getAll();
    setRankings(res.data);
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Rang lista</h1>
          <p className="text-neutral-500 mt-1">Bodovanje po sistemu 365 dana</p>
        </div>
        <button
          onClick={handleRecalculate}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 text-neutral-700 rounded-xl text-sm hover:bg-neutral-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Ažuriraj
        </button>
      </div>

      {/* Rankings Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-neutral-300 border-t-neutral-600" />
        </div>
      ) : rankings.length > 0 ? (
        <div className="bg-white rounded-2xl border border-neutral-200/60 divide-y divide-neutral-100">
          {rankings.filter(r => r.current_rank).map((r, idx) => (
            <div key={r.id} className="flex items-center gap-3 px-5 py-3">
              <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm font-medium ${
                idx === 0 ? 'bg-rose-500 text-white' :
                idx === 1 ? 'bg-neutral-200 text-neutral-700' :
                idx === 2 ? 'bg-amber-100 text-amber-700' :
                'bg-neutral-100 text-neutral-600'
              }`}>{r.current_rank || idx + 1}</span>
              {r.player?.photo ? (
                <img src={r.player.photo} alt={r.player.full_name} className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <div className="w-9 h-9 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                  {(r.player?.first_name || '?')[0]}{(r.player?.last_name || '?')[0]}
                </div>
              )}
              <span className="flex-1 text-neutral-900">{r.player?.full_name || 'Nepoznat'}</span>
              <span className={`text-sm ${
                r.rank_change_display?.startsWith('+') ? 'text-green-600' :
                r.rank_change_display?.startsWith('-') ? 'text-red-500' : 'text-neutral-400'
              }`}>
                {r.rank_change_display || '–'}
              </span>
              <span className="text-sm font-medium text-neutral-500 w-20 text-right">{r.total_points || 0} bod.</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-neutral-400">
          <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nema podataka o rangiranju</p>
        </div>
      )}
    </div>
  );
};

// ==================== POWER INDEX ====================
const PowerIndex = () => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    powerIndexAPI.getAll()
      .then(res => { setRatings(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleRecalculate = async () => {
    setLoading(true);
    await powerIndexAPI.recalculate();
    const res = await powerIndexAPI.getAll();
    setRatings(res.data);
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Power Index</h1>
          <p className="text-neutral-500 mt-1">Rejting stvarne jačine igrača • Skala 1.0–10.0</p>
        </div>
        <button
          onClick={handleRecalculate}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 text-neutral-700 rounded-xl text-sm hover:bg-neutral-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Preračunaj
        </button>
      </div>

      {/* Power Index Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-neutral-300 border-t-neutral-600" />
        </div>
      ) : ratings.length > 0 ? (
        <div className="bg-white rounded-2xl border border-neutral-200/60 divide-y divide-neutral-100">
          {ratings.map((r, idx) => (
            <div key={r.id} className="flex items-center gap-3 px-5 py-3">
              <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm font-medium ${
                idx === 0 ? 'bg-rose-500 text-white' :
                idx === 1 ? 'bg-neutral-200 text-neutral-700' :
                idx === 2 ? 'bg-amber-100 text-amber-700' :
                'bg-neutral-100 text-neutral-600'
              }`}>{r.index_rank || idx + 1}</span>
              {r.player?.photo ? (
                <img src={r.player.photo} alt={r.player.full_name} className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <div className="w-9 h-9 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                  {(r.player?.first_name || '?')[0]}{(r.player?.last_name || '?')[0]}
                </div>
              )}
              <span className="flex-1 text-neutral-900 flex items-center gap-2 min-w-0">
                <span className="truncate">{r.player?.full_name || 'Nepoznat'}</span>
                {r.is_provisional && (
                  <span className="text-amber-500 font-bold flex-shrink-0" title="Provizoran rejting">*</span>
                )}
              </span>
              <span className="text-xs text-neutral-400 hidden sm:inline">{r.matches_count} mečeva</span>
              <span className="text-sm font-semibold text-neutral-900 w-14 text-right">{Number(r.power_index).toFixed(2)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-neutral-400">
          <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nema podataka o Power Index-u</p>
        </div>
      )}
    </div>
  );
};

// ==================== TOURNAMENTS ====================
const Tournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', start_date: '', location: '', total_points: 1000 });
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    tournamentsAPI.getAll().then(res => setTournaments(res.data)).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    await tournamentsAPI.create(form);
    const res = await tournamentsAPI.getAll();
    setTournaments(res.data);
    setShowModal(false);
    setForm({ name: '', start_date: '', location: '', total_points: 1000 });
  };

  const statusLabels = {
    upcoming: 'Predstojeći',
    ongoing: 'U toku',
    finished: 'Završen',
    archived: 'Arhiviran',
  };

  const statusColors = {
    upcoming: 'bg-blue-50 text-blue-600',
    ongoing: 'bg-amber-50 text-amber-600',
    finished: 'bg-green-50 text-green-600',
    archived: 'bg-neutral-100 text-neutral-500',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Turniri</h1>
          <p className="text-neutral-500 mt-1">Upravljanje turnirima</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-xl text-sm hover:bg-neutral-800 transition-colors"
        >
          <Plus className="w-4 h-4" /> Novi turnir
        </button>
      </div>

      {/* Tournaments List */}
      {tournaments.length > 0 ? (
        <div className="bg-white rounded-2xl border border-neutral-200/60 divide-y divide-neutral-100">
          {tournaments.map((t) => (
            <div
              key={t.id}
              onClick={() => navigate(`/panel/tournaments/${t.id}`)}
              className="px-4 sm:px-5 py-3 sm:py-4 hover:bg-neutral-50 transition-colors cursor-pointer"
            >
              <div className="flex items-start sm:items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <span className="text-neutral-900 font-medium text-sm sm:text-base">{t.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-md whitespace-nowrap ${statusColors[t.status]}`}>
                      {statusLabels[t.status] || t.status}
                    </span>
                  </div>
                  <div className="text-xs sm:text-sm text-neutral-500 mt-1 flex flex-wrap gap-x-1">
                    <span>{t.start_date}</span>
                    {t.location && <span>· {t.location}</span>}
                    <span>· {t.player_count || 0} igrača</span>
                    <span className="sm:hidden">· {t.total_points} bod.</span>
                  </div>
                </div>
                <span className="hidden sm:inline text-sm text-neutral-500 whitespace-nowrap">{t.total_points} bod.</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-neutral-400">
          <Trophy className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nema turnira</p>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl flex flex-col max-h-[90vh]">
            <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between flex-shrink-0">
              <h2 className="font-semibold text-neutral-900">Novi turnir</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-neutral-100 rounded-xl transition-colors">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1.5">Naziv</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-neutral-50 border-0 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-all"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5">Datum</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="w-full bg-neutral-50 border-0 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5">Lokacija</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full bg-neutral-50 border-0 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-all"
                  />
                </div>
              </div>
              {user?.is_super_admin && (
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5">Rang bodovi</label>
                  <input
                    type="number"
                    value={form.total_points}
                    onChange={(e) => setForm({ ...form, total_points: e.target.value === '' ? '' : parseInt(e.target.value) })}
                    className="w-full bg-neutral-50 border-0 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-all"
                  />
                </div>
              )}
              <button
                type="submit"
                className="w-full bg-neutral-900 hover:bg-neutral-800 text-white py-3 rounded-xl text-sm font-semibold transition-colors"
              >
                Kreiraj
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== TOURNAMENT DETAIL ====================
const TournamentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [scoreSets, setScoreSets] = useState([{ player1: 0, player2: 0 }, { player1: 0, player2: 0 }, { player1: 0, player2: 0 }]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [playerSeeds, setPlayerSeeds] = useState({});
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [editForm, setEditForm] = useState({ name: '', start_date: '', location: '', total_points: 1000, assigned_admin_ids: [] });
  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [bracketView, setBracketView] = useState('list'); // 'list' or 'bracket'
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replacePlayer, setReplacePlayer] = useState(null);
  const [newPlayerId, setNewPlayerId] = useState('');
  const [showSeedingModal, setShowSeedingModal] = useState(false);
  const [byeStrategy, setByeStrategy] = useState('top_seeds');
  const [bracketPreview, setBracketPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editBracketMode, setEditBracketMode] = useState(false);
  const [bracketZoom, setBracketZoom] = useState(1);
  const [bracketSwapMode, setBracketSwapMode] = useState('players'); // 'players' | 'matches'
  const [swapFirstMatchId, setSwapFirstMatchId] = useState(null);
  const [swapFirstSlot, setSwapFirstSlot] = useState(null); // { matchId, slot }

  // Check if editing bracket is allowed (bracket not locked)
  const canEditBracket = matches.length > 0 && !tournament?.bracket_locked;

  const resetBracketSelection = () => {
    setSwapFirstMatchId(null);
    setSwapFirstSlot(null);
  };

  const handleToggleEditBracket = () => {
    setEditBracketMode(prev => !prev);
    resetBracketSelection();
  };

  const handleSetBracketSwapMode = (mode) => {
    setBracketSwapMode(mode);
    resetBracketSelection();
  };

  // Handle bracket lock/unlock
  const handleLockBracket = async () => {
    try {
      await tournamentsAPI.lockBracket(id);
      toast.success('Žrijeb je zaključan');
      setEditBracketMode(false);
      resetBracketSelection();
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri zaključavanju žrijeba');
    }
  };

  // Get first round key (the round with bracket_position set)
  const getFirstRound = () => {
    const roundOrder = ['round64', 'round32', 'round16', 'quarterfinal', 'semifinal', 'final'];
    const roundsWithMatches = [...new Set(matches.filter(m => m.bracket_position !== null).map(m => m.round))];
    return roundsWithMatches.sort((a, b) => roundOrder.indexOf(a) - roundOrder.indexOf(b))[0];
  };

  // Click-to-select swap: whole match (both slots)
  const handleTournamentMatchClick = async (match) => {
    if (swapFirstMatchId === null) {
      setSwapFirstMatchId(match.id);
      return;
    }
    if (swapFirstMatchId === match.id) {
      setSwapFirstMatchId(null);
      return;
    }
    const firstMatch = matches.find(m => m.id === swapFirstMatchId);
    if (!firstMatch) {
      setSwapFirstMatchId(null);
      return;
    }
    if (firstMatch.round !== match.round) {
      toast.error('Možeš zamijeniti samo mečeve iz istog kola');
      setSwapFirstMatchId(null);
      return;
    }
    const confirmed = await confirm({
      title: 'Zamijeniti pozicije mečeva?',
      message: 'Oba igrača iz jednog meča zamijeniće mjesta sa oba igrača iz drugog meča.',
      confirmText: 'Zamijeni',
      cancelText: 'Odustani'
    });
    if (!confirmed) {
      setSwapFirstMatchId(null);
      return;
    }
    try {
      await tournamentsAPI.swapMatches(id, swapFirstMatchId, match.id);
      toast.success('Mečevi zamijenjeni');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri zamjeni');
    } finally {
      setSwapFirstMatchId(null);
    }
  };

  // Click-to-select swap: individual player slot
  const handleTournamentSlotClick = async (match, slot) => {
    if (swapFirstSlot === null) {
      setSwapFirstSlot({ matchId: match.id, slot });
      return;
    }
    if (swapFirstSlot.matchId === match.id && swapFirstSlot.slot === slot) {
      setSwapFirstSlot(null);
      return;
    }
    const firstMatch = matches.find(m => m.id === swapFirstSlot.matchId);
    if (!firstMatch) {
      setSwapFirstSlot(null);
      return;
    }
    if (firstMatch.round !== match.round) {
      toast.error('Igrače možeš mijenjati samo unutar istog kola');
      setSwapFirstSlot(null);
      return;
    }
    const confirmed = await confirm({
      title: 'Zamijeniti ova dva igrača?',
      message: 'Dva izabrana igrača zamijeniće mjesta u žrijebu.',
      confirmText: 'Zamijeni',
      cancelText: 'Odustani'
    });
    if (!confirmed) {
      setSwapFirstSlot(null);
      return;
    }
    try {
      await tournamentsAPI.swapPlayers(
        id,
        swapFirstSlot.matchId, swapFirstSlot.slot,
        match.id, slot
      );
      toast.success('Igrači zamijenjeni');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri zamjeni');
    } finally {
      setSwapFirstSlot(null);
    }
  };

  const loadData = useCallback(async () => {
    try {
      // OPTIMIZED: Use getBracket (returns tournament + matches) and getDropdown (no ranking queries)
      const [bracketRes, pRes] = await Promise.all([
        tournamentsAPI.getBracket(id),
        playersAPI.getDropdown(),
      ]);
      setTournament(bracketRes.data.tournament);
      setMatches(bracketRes.data.matches || []);
      setAllPlayers(pRes.data);
    } catch {
      navigate('/panel/tournaments');
    }
  }, [id, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get players from tournament_players
  const players = tournament?.tournament_players?.map(tp => ({
    id: tp.player?.id,
    first_name: tp.player?.first_name,
    last_name: tp.player?.last_name,
    seed: tp.seed
  })) || [];

  const availablePlayers = allPlayers.filter(p => !players.find(tp => tp.id === p.id));

  const handleAddPlayers = async () => {
    for (const playerId of selectedPlayers) {
      const seed = playerSeeds[playerId] || null;
      await tournamentsAPI.addPlayer(id, playerId, seed);
    }
    await loadData();
    setShowAddPlayer(false);
    setSelectedPlayers([]);
    setPlayerSeeds({});
  };

  const handleRemovePlayer = async (playerId) => {
    await tournamentsAPI.removePlayer(id, playerId);
    await loadData();
  };

  const openReplaceModal = (player) => {
    setReplacePlayer(player);
    setNewPlayerId('');
    setShowReplaceModal(true);
  };

  const handleReplacePlayer = async () => {
    if (!replacePlayer || !newPlayerId) return;
    try {
      const res = await tournamentsAPI.replacePlayer(id, replacePlayer.id, newPlayerId);
      toast.success(res.data.message || 'Igrač zamijenjen');
      await loadData();
      setShowReplaceModal(false);
      setReplacePlayer(null);
      setNewPlayerId('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri zamjeni igrača');
    }
  };

  const handleWithdrawPlayer = async (player) => {
    if (!window.confirm(`Da li ste sigurni da želite povući ${player.first_name} ${player.last_name} iz turnira? Svi njegovi mečevi će biti obrisani.`)) return;
    try {
      const res = await tournamentsAPI.withdrawPlayer(id, player.id);
      toast.success(res.data.message || 'Igrač povučen');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri povlačenju igrača');
    }
  };

  const handleGenerateBracket = async () => {
    try {
      await tournamentsAPI.generateBracket(id);
      await loadData();
      setShowPreview(false);
    } catch (error) {
      console.error('Error generating bracket:', error);
      toast.error(error.response?.data?.error || 'Greška pri generisanju žrijeba');
    }
  };

  const handleOpenSeedingModal = () => {
    // Initialize playerSeeds with existing seeds
    const initialSeeds = {};
    players.forEach(p => {
      if (p.seed) {
        initialSeeds[p.id] = p.seed;
      }
    });
    setPlayerSeeds(initialSeeds);
    setByeStrategy(tournament.bye_strategy || 'top_seeds');
    setShowSeedingModal(true);
  };

  const handleSaveSeeds = async () => {
    try {
      const seeds = players
        .filter(p => playerSeeds[p.id])
        .map(p => ({ player_id: p.id, seed: playerSeeds[p.id] }));

      await tournamentsAPI.setManualSeeds(id, seeds, byeStrategy);
      await loadData();
      setShowSeedingModal(false);
    } catch (error) {
      console.error('Error saving seeds:', error);
      toast.error(error.response?.data?.error || 'Greška pri čuvanju nosilaca');
    }
  };

  const handleShowPreview = async () => {
    try {
      const res = await tournamentsAPI.getBracketPreview(id);
      setBracketPreview(res.data);
      setShowPreview(true);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error(error.response?.data?.error || 'Greška pri učitavanju pregleda');
    }
  };

  const handleEdit = async () => {
    setEditForm({
      name: tournament.name,
      start_date: tournament.start_date,
      location: tournament.location || '',
      total_points: tournament.total_points,
      assigned_admin_ids: tournament.assigned_admins?.map(a => a.id) || []
    });
    // Load available admins (only super_admin can see this)
    if (user?.role === 'super_admin') {
      try {
        const res = await usersAPI.getAll();
        setAvailableAdmins((res.data || []).filter(u => u.role === 'admin'));
      } catch { /* ignore */ }
    }
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    await tournamentsAPI.update(id, editForm);
    await loadData();
    setShowEditModal(false);
  };

  const handleDelete = async () => {
    await tournamentsAPI.delete(id);
    navigate('/panel/tournaments');
  };

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayers(prev =>
      prev.includes(playerId)
        ? prev.filter(pid => pid !== playerId)
        : [...prev, playerId]
    );
  };

  const handleOpenScore = (match) => {
    setSelectedMatch(match);
    if (match.sets && match.sets.length > 0) {
      setScoreSets(match.sets.map(s => ({ player1: s.player1_games || 0, player2: s.player2_games || 0 })));
    } else {
      setScoreSets([{ player1: 0, player2: 0 }]);
    }
    setShowScoreModal(true);
  };

  const addSet = () => {
    if (scoreSets.length < 5) {
      setScoreSets([...scoreSets, { player1: 0, player2: 0 }]);
    }
  };

  const removeSet = (idx) => {
    if (scoreSets.length > 1) {
      setScoreSets(scoreSets.filter((_, i) => i !== idx));
    }
  };

  const handleSaveScore = async () => {
    const validSets = scoreSets.filter(s => s.player1 > 0 || s.player2 > 0);
    await matchesAPI.updateScore(selectedMatch.id, validSets.map((s, i) => ({
      set_number: i + 1,
      player1_games: s.player1,
      player2_games: s.player2
    })));
    await loadData();
    setShowScoreModal(false);
    setSelectedMatch(null);
  };

  const handleResetScore = async () => {
    if (!selectedMatch?.id) return;
    try {
      await matchesAPI.resetScore(selectedMatch.id);
      await loadData();
      setShowScoreModal(false);
      setSelectedMatch(null);
      toast.success('Rezultat uspješno obrisan');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri brisanju rezultata');
    }
  };

  const handleForfeit = async (playerId, forfeitType) => {
    if (!selectedMatch?.id || !playerId) return;
    try {
      // First save any entered sets (for retirement with partial score)
      const validSets = scoreSets.filter(s => s.player1 > 0 || s.player2 > 0);
      if (validSets.length > 0) {
        await matchesAPI.updateScore(selectedMatch.id, validSets.map((s, i) => ({
          set_number: i + 1,
          player1_games: s.player1,
          player2_games: s.player2
        })));
      }
      // Then record the forfeit
      await matchesAPI.forfeit(selectedMatch.id, playerId, forfeitType);
      await loadData();
      setShowScoreModal(false);
      setSelectedMatch(null);
      toast.success(forfeitType === 'walkover' ? 'Walkover zabilježen' : 'Predaja zabilježena');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri bilježenju predaje');
    }
  };

  const roundLabels = {
    'final': 'Finale',
    'semifinal': 'Polufinale',
    'quarterfinal': 'Četvrtfinale',
    'round16': 'Osmina finala',
    'round32': 'Šesnaestina finala',
    'round64': '1. kolo',
    'round_1': '1. kolo',
  };

  const roundOrder = ['round64', 'round32', 'round16', 'quarterfinal', 'semifinal', 'final'];

  // Group matches by round
  const matchesByRound = matches.reduce((acc, m) => {
    const round = m.round || 'other';
    if (!acc[round]) acc[round] = [];
    acc[round].push(m);
    return acc;
  }, {});

  const sortedRounds = Object.keys(matchesByRound).sort((a, b) => {
    return roundOrder.indexOf(a) - roundOrder.indexOf(b);
  });

  const statusLabels = {
    upcoming: 'Predstojeći',
    ongoing: 'U toku',
    finished: 'Završen',
    archived: 'Arhiviran',
    scheduled: 'Zakazan',
    completed: 'Završen',
  };

  const statusColors = {
    upcoming: 'bg-blue-50 text-blue-600',
    ongoing: 'bg-amber-50 text-amber-600',
    finished: 'bg-green-50 text-green-600',
    scheduled: 'bg-neutral-100 text-neutral-500',
    completed: 'bg-green-50 text-green-600',
    archived: 'bg-neutral-100 text-neutral-500',
  };

  if (!tournament) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-6 w-6 border-2 border-neutral-300 border-t-neutral-600" />
    </div>
  );

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/panel/tournaments')} className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 text-sm">
        <ArrowLeft className="w-4 h-4" /> Nazad na turnire
      </button>

      {/* Tournament Header */}
      <div className="bg-white rounded-2xl border border-neutral-200/60 p-6">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${statusColors[tournament.status]}`}>
                {statusLabels[tournament.status] || tournament.status}
              </span>
              <span className="text-sm text-neutral-500">{tournament.total_points} bodova</span>
            </div>
            <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">{tournament.name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-neutral-500 text-sm">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" /> {tournament.start_date}
              </span>
              {tournament.location && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" /> {tournament.location}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <button
              onClick={handleEdit}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-neutral-200 text-neutral-700 rounded-xl text-sm hover:bg-neutral-50 transition-colors"
            >
              <Edit className="w-4 h-4" /> <span className="hidden sm:inline">Uredi</span>
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-neutral-200 text-red-600 rounded-xl text-sm hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" /> <span className="hidden sm:inline">Obriši</span>
            </button>
            {tournament.status === 'upcoming' && players.length >= 2 && (
              <>
                <button
                  onClick={handleOpenSeedingModal}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-neutral-100 text-neutral-700 rounded-xl text-sm hover:bg-neutral-200 transition-colors"
                >
                  <Settings className="w-4 h-4" /> <span className="hidden sm:inline">Nosioci</span>
                </button>
                <button
                  onClick={handleShowPreview}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-rose-100 text-rose-700 rounded-xl text-sm hover:bg-rose-200 transition-colors"
                >
                  <Eye className="w-4 h-4" /> <span className="hidden sm:inline">Pregled</span>
                </button>
                <button
                  onClick={handleGenerateBracket}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-neutral-900 text-white rounded-xl text-sm hover:bg-neutral-800 transition-colors"
                >
                  <PlayCircle className="w-4 h-4" /> <span className="sm:hidden">Žrijeb</span><span className="hidden sm:inline">Generiši žrijeb</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Players */}
        <div className="bg-white rounded-2xl border border-neutral-200/60 overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
            <h2 className="font-medium text-neutral-900">Igrači ({players.length})</h2>
            {tournament.status === 'upcoming' && (
              <button
                onClick={() => setShowAddPlayer(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-neutral-100 text-neutral-700 rounded-lg text-sm hover:bg-neutral-200 transition-colors"
              >
                <Plus className="w-4 h-4" /> Dodaj
              </button>
            )}
          </div>
          <div className="divide-y divide-neutral-100 max-h-96 overflow-y-auto">
            {players.sort((a, b) => (a.seed || 999) - (b.seed || 999)).map((p, idx) => (
              <div key={p.id} className="flex items-center gap-3 px-5 py-3 hover:bg-neutral-50 group">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-medium ${
                  p.seed ? 'bg-rose-100 text-rose-700' : 'bg-neutral-100 text-neutral-500'
                }`}>
                  {p.seed || idx + 1}
                </div>
                <span className="flex-1 text-neutral-900">
                  {p.first_name} {p.last_name}
                  {p.seed && <span className="ml-2 text-xs text-rose-500 font-medium">({p.seed}. nosilac)</span>}
                </span>
                <div className="flex items-center gap-1">
                  {tournament.status === 'upcoming' && (
                    <button
                      onClick={() => handleRemovePlayer(p.id)}
                      className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all sm:opacity-0 sm:group-hover:opacity-100"
                      title="Ukloni"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => openReplaceModal(p)}
                    className="p-1.5 text-neutral-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all sm:opacity-0 sm:group-hover:opacity-100"
                    title="Zamijeni igrača"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {players.length === 0 && (
              <div className="px-5 py-8 text-center text-neutral-400 text-sm">Nema igrača</div>
            )}
          </div>
        </div>

        {/* Matches / Bracket */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-neutral-200/60 overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
            <h2 className="font-medium text-neutral-900">Žrijeb i rezultati</h2>
            <div className="flex items-center gap-3">
              {/* Zoom controls */}
              {matches.length > 0 && (
                <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
                  <button
                    onClick={() => setBracketZoom(z => Math.max(0.3, z - 0.1))}
                    className="p-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-neutral-600 hover:text-neutral-900"
                    title="Umanji"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setBracketZoom(1)}
                    className="px-2 py-1 text-xs font-medium text-neutral-600 hover:text-neutral-900 min-w-[3rem]"
                    title="Resetuj zoom"
                  >
                    {Math.round(bracketZoom * 100)}%
                  </button>
                  <button
                    onClick={() => setBracketZoom(z => Math.min(1.5, z + 0.1))}
                    className="p-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-neutral-600 hover:text-neutral-900"
                    title="Uvećaj"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                </div>
              )}
              {editBracketMode && (
                <>
                  <span className="inline-flex rounded-lg bg-neutral-100 p-0.5">
                    <button
                      onClick={() => handleSetBracketSwapMode('players')}
                      className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
                        bracketSwapMode === 'players' ? 'bg-white text-rose-700 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
                      }`}
                    >
                      Igrači
                    </button>
                    <button
                      onClick={() => handleSetBracketSwapMode('matches')}
                      className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
                        bracketSwapMode === 'matches' ? 'bg-white text-rose-700 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
                      }`}
                    >
                      Mečevi
                    </button>
                  </span>
                  <span className="text-xs text-neutral-400">
                    {bracketSwapMode === 'players'
                      ? (swapFirstSlot ? 'Klikni drugog igrača za zamjenu' : 'Klikni dva igrača da im zamijeniš mjesta')
                      : (swapFirstMatchId ? 'Klikni drugi meč za zamjenu' : 'Klikni dva meča da im zamijeniš pozicije')}
                  </span>
                </>
              )}
              {/* Show locked status */}
              {tournament?.bracket_locked && (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium">
                  <Lock className="w-3.5 h-3.5" /> Žrijeb zaključan
                </span>
              )}
              {/* Edit bracket button - only if not locked */}
              {canEditBracket && (
                <button
                  onClick={handleToggleEditBracket}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    editBracketMode
                      ? 'bg-rose-100 text-rose-700 hover:bg-rose-200'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {editBracketMode ? (
                    <>
                      <Check className="w-3.5 h-3.5" /> Završi uređivanje
                    </>
                  ) : (
                    <>
                      <Edit className="w-3.5 h-3.5" /> Izmijeni žrijeb
                    </>
                  )}
                </button>
              )}
              {/* Lock button - once locked, no going back */}
              {matches.length > 0 && (
                tournament?.bracket_locked ? (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-100 text-neutral-500 rounded-lg text-xs font-medium">
                    <Lock className="w-3.5 h-3.5" /> Žrijeb zaključan
                  </span>
                ) : (
                  <button
                    onClick={handleLockBracket}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg text-xs font-medium transition-colors"
                  >
                    <Lock className="w-3.5 h-3.5" /> Zaključaj žrijeb
                  </button>
                )
              )}
            </div>
          </div>
          <div className="p-5">
            {matches.length > 0 ? (
              <div>
                <div className="overflow-x-auto overflow-y-auto border border-neutral-200 rounded-xl bg-neutral-50 p-4">
                  {(() => {
                    const firstRound = getFirstRound();
                    const firstRoundMatchCount = matchesByRound[firstRound]?.length || 1;

                    // Match box dimensions
                    const matchWidth = 208; // w-52 = 13rem = 208px
                    const matchHeight = 80;
                    const roundGap = 56; // Gap between rounds (includes space for lines)
                    const headerHeight = 32;

                    // Calculate vertical spacing for each round
                    const getMatchPositions = (roundIndex, matchCount) => {
                      const positions = [];
                      if (roundIndex === 0) {
                        // First round: evenly spaced
                        const gap = 24;
                        for (let i = 0; i < matchCount; i++) {
                          positions.push(i * (matchHeight + gap));
                        }
                      } else {
                        // Later rounds: centered between their feeder matches
                        const prevMatchCount = matchCount * 2;
                        const prevPositions = getMatchPositions(roundIndex - 1, prevMatchCount);
                        for (let i = 0; i < matchCount; i++) {
                          const topMatch = prevPositions[i * 2];
                          const bottomMatch = prevPositions[i * 2 + 1];
                          positions.push((topMatch + bottomMatch + matchHeight) / 2 - matchHeight / 2);
                        }
                      }
                      return positions;
                    };

                    // Get total dimensions
                    const firstRoundPositions = getMatchPositions(0, firstRoundMatchCount);
                    const totalHeight = firstRoundPositions[firstRoundPositions.length - 1] + matchHeight + headerHeight + 24;
                    const totalWidth = sortedRounds.length * (matchWidth + roundGap);

                    // Scaled dimensions for wrapper to eliminate empty space
                    const scaledWidth = totalWidth * bracketZoom;
                    const scaledHeight = totalHeight * bracketZoom;

                    return (
                      <div style={{ width: scaledWidth, height: scaledHeight, overflow: 'hidden' }}>
                        <div style={{ transform: `scale(${bracketZoom})`, transformOrigin: 'top left' }}>
                          <div className="relative" style={{ width: totalWidth, height: totalHeight }}>
                          {/* SVG Layer for connecting lines */}
                          <svg
                            className="absolute inset-0 pointer-events-none"
                            style={{ width: totalWidth, height: totalHeight }}
                          >
                            {sortedRounds.slice(0, -1).map((round, roundIdx) => {
                              const currentMatches = matchesByRound[round]?.sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0)) || [];
                              const nextRound = sortedRounds[roundIdx + 1];
                              const nextMatches = matchesByRound[nextRound]?.sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0)) || [];
                              const currentPositions = getMatchPositions(roundIdx, currentMatches.length);
                              const nextPositions = getMatchPositions(roundIdx + 1, nextMatches.length);

                              const xStart = roundIdx * (matchWidth + roundGap) + matchWidth;
                              const xEnd = (roundIdx + 1) * (matchWidth + roundGap);
                              const xMid = (xStart + xEnd) / 2;

                              return currentMatches.map((match, matchIdx) => {
                                const nextMatchIdx = Math.floor(matchIdx / 2);
                                const yStart = headerHeight + currentPositions[matchIdx] + matchHeight / 2;
                                const yEnd = headerHeight + nextPositions[nextMatchIdx] + matchHeight / 2;

                                return (
                                  <g key={`${round}-${matchIdx}`}>
                                    {/* Horizontal line from match to midpoint */}
                                    <line
                                      x1={xStart}
                                      y1={yStart}
                                      x2={xMid}
                                      y2={yStart}
                                      stroke="#d4d4d4"
                                      strokeWidth="2"
                                    />
                                    {/* Vertical line to connect pair */}
                                    {matchIdx % 2 === 0 && matchIdx + 1 < currentMatches.length && (
                                      <line
                                        x1={xMid}
                                        y1={yStart}
                                        x2={xMid}
                                        y2={headerHeight + currentPositions[matchIdx + 1] + matchHeight / 2}
                                        stroke="#d4d4d4"
                                        strokeWidth="2"
                                      />
                                    )}
                                    {/* Horizontal line from midpoint to next match */}
                                    {matchIdx % 2 === 0 && (
                                      <line
                                        x1={xMid}
                                        y1={yEnd}
                                        x2={xEnd}
                                        y2={yEnd}
                                        stroke="#d4d4d4"
                                        strokeWidth="2"
                                      />
                                    )}
                                  </g>
                                );
                              });
                            })}
                          </svg>

                          {/* Match boxes layer */}
                          {sortedRounds.map((round, roundIdx) => {
                            const isFirstRound = round === firstRound;
                            const roundMatches = matchesByRound[round]?.sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0)) || [];
                            const positions = getMatchPositions(roundIdx, roundMatches.length);
                            const xPos = roundIdx * (matchWidth + roundGap);

                            return (
                              <div key={round} className="absolute" style={{ left: xPos, top: 0, width: matchWidth }}>
                                <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide text-center py-2" style={{ height: headerHeight }}>
                                  {roundLabels[round] || round}
                                </div>
                                {roundMatches.map((m, matchIdx) => {
                                  const isBye = m.status === 'completed' && m.winner && (!m.player1 || !m.player2);
                                  const editingMatches = editBracketMode && bracketSwapMode === 'matches';
                                  const editingPlayers = editBracketMode && bracketSwapMode === 'players';
                                  const matchSelectable = isFirstRound && editingMatches;
                                  const slotEditable = isFirstRound && editingPlayers;
                                  const isMatchSelected = matchSelectable && swapFirstMatchId === m.id;
                                  const boxClickable = matchSelectable || (!editBracketMode && m.player1 && m.player2);

                                  // Click-to-select player slot
                                  const PlayerSlot = ({ player, slot, isWinner, playerName }) => {
                                    const isSlotSelected = slotEditable && swapFirstSlot?.matchId === m.id && swapFirstSlot?.slot === slot;
                                    return (
                                      <div
                                        onClick={slotEditable ? (e) => { e.stopPropagation(); handleTournamentSlotClick(m, slot); } : undefined}
                                        className={`px-3 py-1.5 flex items-center justify-between ${slot === 'p1' ? 'border-b border-neutral-200' : ''} ${
                                          isSlotSelected ? 'bg-amber-100 ring-2 ring-inset ring-amber-300' : (isWinner ? 'bg-green-50' : '')
                                        } ${slotEditable ? 'cursor-pointer hover:bg-amber-50' : ''}`}
                                        style={{ height: matchHeight / 2 }}
                                      >
                                        <span className={`text-sm truncate flex-1 ${isWinner ? 'font-semibold text-green-700' : player ? 'text-neutral-700' : 'text-neutral-300 italic'}`}>
                                          {player ? playerName : (isBye ? 'BYE' : '')}
                                        </span>
                                        {m.sets?.length > 0 && player && (
                                          <div className="flex gap-1 ml-2">
                                            {m.sets.map((s, idx) => {
                                              const myGames = slot === 'p1' ? s.player1_games : s.player2_games;
                                              const oppGames = slot === 'p1' ? s.player2_games : s.player1_games;
                                              const won = myGames > oppGames;
                                              return (
                                                <span key={idx} className={`w-5 h-5 flex items-center justify-center rounded text-xs font-semibold ${won ? 'bg-green-500 text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                                                  {myGames}
                                                </span>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  };

                                  return (
                                    <div
                                      key={m.id}
                                      onClick={() => {
                                        if (matchSelectable) {
                                          handleTournamentMatchClick(m);
                                        } else if (!editBracketMode && m.player1 && m.player2) {
                                          handleOpenScore(m);
                                        }
                                      }}
                                      className={`absolute bg-white rounded-xl overflow-hidden transition-colors border ${
                                        isMatchSelected ? 'border-amber-500 ring-2 ring-amber-300 bg-amber-50' : 'border-neutral-200'
                                      } ${boxClickable ? `cursor-pointer ${matchSelectable ? 'hover:border-amber-400' : 'hover:bg-rose-50 hover:border-rose-300'}` : ''} ${isBye ? 'opacity-60' : ''}`}
                                      style={{
                                        top: headerHeight + positions[matchIdx],
                                        left: 0,
                                        width: matchWidth,
                                        height: matchHeight
                                      }}
                                    >
                                      <PlayerSlot
                                        player={m.player1}
                                        slot="p1"
                                        isWinner={m.winner?.id === m.player1?.id}
                                        playerName={m.player1_name || m.player1?.full_name}
                                      />
                                      <PlayerSlot
                                        player={m.player2}
                                        slot="p2"
                                        isWinner={m.winner?.id === m.player2?.id}
                                        playerName={m.player2_name || m.player2?.full_name}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-neutral-400">
                <Trophy className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Nema mečeva. Dodajte igrače i generisite žrijeb.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Players Modal */}
      {showAddPlayer && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between flex-shrink-0">
              <h2 className="font-semibold text-neutral-900">Dodaj igrače</h2>
              <button onClick={() => { setShowAddPlayer(false); setSelectedPlayers([]); setPlayerSeeds({}); setPlayerSearchQuery(''); }} className="p-1.5 hover:bg-neutral-100 rounded-lg">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <div className="px-4 pt-4 pb-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Pretraži igrače..."
                  value={playerSearchQuery}
                  onChange={(e) => setPlayerSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {availablePlayers.length > 0 ? (
                <div className="space-y-1">
                  {availablePlayers.filter(p =>
                    !playerSearchQuery ||
                    `${p.first_name} ${p.last_name}`.toLowerCase().includes(playerSearchQuery.toLowerCase())
                  ).map(p => (
                    <div
                      key={p.id}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                        selectedPlayers.includes(p.id) ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-50'
                      }`}
                    >
                      <label className="flex items-center gap-3 flex-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedPlayers.includes(p.id)}
                          onChange={() => togglePlayerSelection(p.id)}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                          selectedPlayers.includes(p.id) ? 'border-white bg-white' : 'border-neutral-300'
                        }`}>
                          {selectedPlayers.includes(p.id) && <Check className="w-3 h-3 text-neutral-900" />}
                        </div>
                        <span className="flex-1">{p.first_name} {p.last_name}</span>
                      </label>
                      {selectedPlayers.includes(p.id) && (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="number"
                            min="1"
                            max="32"
                            placeholder="—"
                            value={playerSeeds[p.id] || ''}
                            onChange={(e) => setPlayerSeeds(prev => ({ ...prev, [p.id]: e.target.value ? parseInt(e.target.value) : null }))}
                            className="w-12 bg-white/20 border border-white/30 rounded-lg px-2 py-1 text-sm text-white text-center focus:outline-none focus:bg-white/30 placeholder-white/50"
                          />
                          <span className="text-xs text-white/70">nos.</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-neutral-400 text-sm">
                  Svi igrači su već dodati na turnir
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-neutral-100 flex-shrink-0">
              <button
                onClick={handleAddPlayers}
                disabled={selectedPlayers.length === 0}
                className="w-full bg-neutral-900 hover:bg-neutral-800 text-white py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
              >
                Dodaj {selectedPlayers.length > 0 ? `(${selectedPlayers.length})` : ''} na turnir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace Player Modal */}
      {showReplaceModal && replacePlayer && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <h2 className="font-semibold text-neutral-900 mb-4">
              Zamijeni igrača
            </h2>
            <p className="text-sm text-neutral-600 mb-4">
              Zamjena: <span className="font-medium">{replacePlayer.first_name} {replacePlayer.last_name}</span>
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Novi igrač</label>
              <select
                value={newPlayerId}
                onChange={(e) => setNewPlayerId(e.target.value)}
                className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
              >
                <option value="">Izaberi igrača...</option>
                {allPlayers
                  .filter(p => !players.find(tp => tp.id === p.id))
                  .map(p => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                  ))
                }
              </select>
              <p className="text-xs text-neutral-500 mt-1">
                Novi igrač preuzima poziciju u žrijebu i sve mečeve
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowReplaceModal(false); setReplacePlayer(null); setNewPlayerId(''); }}
                className="flex-1 px-4 py-2.5 border border-neutral-200 text-neutral-700 rounded-xl text-sm font-medium hover:bg-neutral-50 transition-colors"
              >
                Otkaži
              </button>
              <button
                onClick={handleReplacePlayer}
                disabled={!newPlayerId}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Zamijeni
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tournament Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
              <h2 className="font-semibold text-neutral-900">Uredi turnir</h2>
              <button onClick={() => setShowEditModal(false)} className="p-1.5 hover:bg-neutral-100 rounded-lg">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <form onSubmit={handleSaveEdit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Naziv</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Datum početka</label>
                <input
                  type="date"
                  value={editForm.start_date}
                  onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Lokacija</label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                />
              </div>
              {user?.is_super_admin && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">Ukupno bodova</label>
                  <input
                    type="number"
                    value={editForm.total_points}
                    onChange={(e) => setEditForm({ ...editForm, total_points: e.target.value === '' ? '' : parseInt(e.target.value) })}
                    className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                  />
                </div>
              )}
              {user?.role === 'super_admin' && availableAdmins.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Dodijeli adminima</label>
                  <div className="space-y-2 max-h-32 overflow-y-auto border border-neutral-200 rounded-xl p-3">
                    {availableAdmins.map(admin => (
                      <label key={admin.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editForm.assigned_admin_ids.includes(admin.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditForm({ ...editForm, assigned_admin_ids: [...editForm.assigned_admin_ids, admin.id] });
                            } else {
                              setEditForm({ ...editForm, assigned_admin_ids: editForm.assigned_admin_ids.filter(id => id !== admin.id) });
                            }
                          }}
                          className="w-4 h-4 rounded border-neutral-300"
                        />
                        <span className="text-sm text-neutral-700">{admin.username}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">Odabrani admini mogu unositi rezultate za ovaj turnir</p>
                </div>
              )}
              <button
                type="submit"
                className="w-full bg-neutral-900 hover:bg-neutral-800 text-white py-2.5 rounded-xl text-sm font-medium transition-colors"
              >
                Sačuvaj izmjene
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-neutral-900 mb-2">Obriši turnir?</h2>
            <p className="text-sm text-neutral-500 mb-6">
              Da li ste sigurni da želite obrisati turnir "{tournament.name}"? Ova akcija se ne može poništiti.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-neutral-200 text-neutral-700 rounded-xl text-sm font-medium hover:bg-neutral-50 transition-colors"
              >
                Otkaži
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Obriši
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Score Entry Modal */}
      {showScoreModal && selectedMatch && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={() => { setShowScoreModal(false); setSelectedMatch(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
              <h2 className="font-semibold text-neutral-900 text-sm">Unesi rezultat</h2>
              <button onClick={() => { setShowScoreModal(false); setSelectedMatch(null); }} className="p-1 hover:bg-neutral-100 rounded-lg">
                <X className="w-4 h-4 text-neutral-400" />
              </button>
            </div>
            <div className="p-4">
              {/* Players Header */}
              <div className="flex items-center justify-between mb-4 text-xs">
                <div className="flex-1 text-center font-medium text-neutral-700 truncate px-2">
                  {selectedMatch.player1_name || selectedMatch.player1?.full_name || 'Igrač 1'}
                </div>
                <div className="w-8"></div>
                <div className="flex-1 text-center font-medium text-neutral-700 truncate px-2">
                  {selectedMatch.player2_name || selectedMatch.player2?.full_name || 'Igrač 2'}
                </div>
              </div>

              {/* Compact Sets Input */}
              <div className="space-y-2">
                {scoreSets.map((set, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-xs text-neutral-400 w-6">S{idx + 1}</span>
                    <div className="flex-1 flex items-center justify-center gap-1">
                      <button
                        onClick={() => {
                          const newSets = [...scoreSets];
                          if (newSets[idx].player1 > 0) newSets[idx].player1--;
                          setScoreSets(newSets);
                        }}
                        className="w-7 h-7 rounded bg-neutral-100 text-neutral-500 hover:bg-neutral-200 text-sm font-medium"
                      >-</button>
                      <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-200 flex items-center justify-center text-lg font-bold text-neutral-900">
                        {set.player1}
                      </div>
                      <button
                        onClick={() => {
                          const newSets = [...scoreSets];
                          newSets[idx].player1++;
                          setScoreSets(newSets);
                        }}
                        className="w-7 h-7 rounded bg-neutral-100 text-neutral-500 hover:bg-neutral-200 text-sm font-medium"
                      >+</button>
                    </div>
                    <span className="text-neutral-300 font-bold">:</span>
                    <div className="flex-1 flex items-center justify-center gap-1">
                      <button
                        onClick={() => {
                          const newSets = [...scoreSets];
                          if (newSets[idx].player2 > 0) newSets[idx].player2--;
                          setScoreSets(newSets);
                        }}
                        className="w-7 h-7 rounded bg-neutral-100 text-neutral-500 hover:bg-neutral-200 text-sm font-medium"
                      >-</button>
                      <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-200 flex items-center justify-center text-lg font-bold text-neutral-900">
                        {set.player2}
                      </div>
                      <button
                        onClick={() => {
                          const newSets = [...scoreSets];
                          newSets[idx].player2++;
                          setScoreSets(newSets);
                        }}
                        className="w-7 h-7 rounded bg-neutral-100 text-neutral-500 hover:bg-neutral-200 text-sm font-medium"
                      >+</button>
                    </div>
                    {scoreSets.length > 1 && (
                      <button
                        onClick={() => removeSet(idx)}
                        className="w-6 h-6 rounded bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-500 text-xs"
                      >×</button>
                    )}
                  </div>
                ))}
                {scoreSets.length < 5 && (
                  <button
                    onClick={addSet}
                    className="w-full py-2 border border-dashed border-neutral-300 rounded-lg text-xs text-neutral-500 hover:border-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    + Dodaj set
                  </button>
                )}
              </div>

              {/* Quick Scores */}
              <div className="mt-4 pt-3 border-t border-neutral-100 space-y-3">
                <div>
                  <div className="text-xs text-neutral-400 mb-1.5">Brzi unos - igrač 1 dobija set:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {['6-0', '6-1', '6-2', '6-3', '6-4', '7-5', '7-6'].map(score => (
                      <button
                        key={`p1-${score}`}
                        onClick={() => {
                          const [p1, p2] = score.split('-').map(Number);
                          const newSets = [...scoreSets];
                          const emptySetIdx = newSets.findIndex(s => s.player1 === 0 && s.player2 === 0);
                          if (emptySetIdx !== -1) {
                            newSets[emptySetIdx] = { player1: p1, player2: p2 };
                          } else if (newSets.length < 5) {
                            newSets.push({ player1: p1, player2: p2 });
                          }
                          setScoreSets(newSets);
                        }}
                        className="px-2 py-1 text-xs font-mono bg-neutral-50 border border-neutral-200 rounded hover:bg-green-50 hover:border-green-300 transition-colors"
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-neutral-400 mb-1.5">Brzi unos - igrač 2 dobija set:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {['0-6', '1-6', '2-6', '3-6', '4-6', '5-7', '6-7'].map(score => (
                      <button
                        key={`p2-${score}`}
                        onClick={() => {
                          const [p1, p2] = score.split('-').map(Number);
                          const newSets = [...scoreSets];
                          const emptySetIdx = newSets.findIndex(s => s.player1 === 0 && s.player2 === 0);
                          if (emptySetIdx !== -1) {
                            newSets[emptySetIdx] = { player1: p1, player2: p2 };
                          } else if (newSets.length < 5) {
                            newSets.push({ player1: p1, player2: p2 });
                          }
                          setScoreSets(newSets);
                        }}
                        className="px-2 py-1 text-xs font-mono bg-neutral-50 border border-neutral-200 rounded hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Forfeit Section */}
              {selectedMatch?.status !== 'completed' && (
                <div className="mt-4 pt-3 border-t border-neutral-100">
                  <div className="text-xs text-neutral-400 mb-2">Predaja meča:</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => {
                        const playerId = selectedMatch.player1?.id || selectedMatch.player1_id;
                        const hasSets = scoreSets.some(s => s.player1 > 0 || s.player2 > 0);
                        handleForfeit(playerId, hasSets ? 'retirement' : 'walkover');
                      }}
                      className="px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-xl text-xs font-medium transition-colors"
                    >
                      {selectedMatch.player1_name || selectedMatch.player1?.full_name || 'Igrač 1'} predao
                    </button>
                    <button
                      onClick={() => {
                        const playerId = selectedMatch.player2?.id || selectedMatch.player2_id;
                        const hasSets = scoreSets.some(s => s.player1 > 0 || s.player2 > 0);
                        handleForfeit(playerId, hasSets ? 'retirement' : 'walkover');
                      }}
                      className="px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-xl text-xs font-medium transition-colors"
                    >
                      {selectedMatch.player2_name || selectedMatch.player2?.full_name || 'Igrač 2'} predao
                    </button>
                  </div>
                </div>
              )}

              <div className="flex gap-2 mt-4">
                {selectedMatch?.status === 'completed' && (selectedMatch?.sets?.length > 0 || selectedMatch?.forfeit_type) && (
                  <button
                    onClick={handleResetScore}
                    className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 py-2.5 rounded-xl text-sm font-medium transition-colors"
                  >
                    Obriši rezultat
                  </button>
                )}
                <button
                  onClick={handleSaveScore}
                  className="flex-1 bg-neutral-900 hover:bg-neutral-800 text-white py-2.5 rounded-xl text-sm font-medium transition-colors"
                >
                  Sačuvaj
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seeding Modal */}
      {showSeedingModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between flex-shrink-0">
              <h2 className="font-semibold text-neutral-900">Postavljanje nosilaca</h2>
              <button onClick={() => setShowSeedingModal(false)} className="p-1.5 hover:bg-neutral-100 rounded-lg">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Bye Strategy Selection */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Strategija raspodjele slobodnih mjesta
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-neutral-200 cursor-pointer hover:bg-neutral-50 transition-colors">
                    <input
                      type="radio"
                      name="byeStrategy"
                      value="top_seeds"
                      checked={byeStrategy === 'top_seeds'}
                      onChange={(e) => setByeStrategy(e.target.value)}
                      className="w-4 h-4 text-neutral-900"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-neutral-900">Nosioci dobijaju slobodna mjesta (ATP standard)</div>
                      <div className="text-xs text-neutral-500">Slobodna mjesta dobijaju nosioci po redu (1, 2, 3...)</div>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-neutral-200 cursor-pointer hover:bg-neutral-50 transition-colors">
                    <input
                      type="radio"
                      name="byeStrategy"
                      value="balanced"
                      checked={byeStrategy === 'balanced'}
                      onChange={(e) => setByeStrategy(e.target.value)}
                      className="w-4 h-4 text-neutral-900"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-neutral-900">Ravnomjerna raspodjela</div>
                      <div className="text-xs text-neutral-500">Ravnomjerna raspodjela slobodnih mjesta kroz sve kvartale</div>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-neutral-200 cursor-pointer hover:bg-neutral-50 transition-colors">
                    <input
                      type="radio"
                      name="byeStrategy"
                      value="random"
                      checked={byeStrategy === 'random'}
                      onChange={(e) => setByeStrategy(e.target.value)}
                      className="w-4 h-4 text-neutral-900"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-neutral-900">Nasumična raspodjela</div>
                      <div className="text-xs text-neutral-500">Nasumična raspodjela slobodnih mjesta</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Players Seeding */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-3">
                  Dodjela nosiostva (maksimalno {Math.floor(players.length / 4)} nosilaca)
                </label>
                <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                  {players.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-xl">
                      <div className="flex-1">
                        <div className="font-medium text-neutral-900">
                          {p.first_name} {p.last_name}
                        </div>
                        {p.seed && (
                          <div className="text-xs text-rose-500 mt-0.5">Trenutni nosilac: #{p.seed}</div>
                        )}
                      </div>
                      <input
                        type="number"
                        min="1"
                        max={Math.floor(players.length / 4)}
                        placeholder="Br."
                        value={playerSeeds[p.id] || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setPlayerSeeds(prev => ({
                            ...prev,
                            [p.id]: value ? parseInt(value) : undefined
                          }));
                        }}
                        className="w-20 px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-3 bg-blue-50 rounded-xl">
                  <div className="text-xs text-blue-700">
                    <strong>Napomena:</strong> Nosioci moraju biti uzastopni (1, 2, 3...).
                    Maksimalan broj nosilaca je 25% od ukupnog broja igrača (ATP pravilo).
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-neutral-100 flex gap-3 flex-shrink-0">
              <button
                onClick={() => setShowSeedingModal(false)}
                className="flex-1 px-4 py-2.5 bg-neutral-100 text-neutral-700 rounded-xl text-sm font-medium hover:bg-neutral-200 transition-colors"
              >
                Otkaži
              </button>
              <button
                onClick={handleSaveSeeds}
                className="flex-1 px-4 py-2.5 bg-neutral-900 text-white rounded-xl text-sm font-medium hover:bg-neutral-800 transition-colors"
              >
                Sačuvaj nosioce
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bracket Preview Modal */}
      {showPreview && bracketPreview && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl shadow-xl max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between flex-shrink-0">
              <h2 className="font-semibold text-neutral-900">Pregled žrijeba</h2>
              <button onClick={() => setShowPreview(false)} className="p-1.5 hover:bg-neutral-100 rounded-lg">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Bracket Info */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-neutral-50 rounded-xl p-4">
                  <div className="text-xs text-neutral-500 mb-1">Veličina žrijeba</div>
                  <div className="text-2xl font-semibold text-neutral-900">{bracketPreview.bracket_size}</div>
                </div>
                <div className="bg-neutral-50 rounded-xl p-4">
                  <div className="text-xs text-neutral-500 mb-1">Broj igrača</div>
                  <div className="text-2xl font-semibold text-neutral-900">{bracketPreview.num_players}</div>
                </div>
                <div className="bg-neutral-50 rounded-xl p-4">
                  <div className="text-xs text-neutral-500 mb-1">Slobodnih mjesta</div>
                  <div className="text-2xl font-semibold text-rose-600">{bracketPreview.num_byes}</div>
                </div>
                <div className="bg-neutral-50 rounded-xl p-4">
                  <div className="text-xs text-neutral-500 mb-1">Nosilaca</div>
                  <div className="text-2xl font-semibold text-neutral-900">{bracketPreview.seed_config_available}</div>
                </div>
              </div>

              {/* Bye Strategy Display */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="text-sm font-medium text-blue-900 mb-1">
                  Strategija slobodnih mjesta: {
                    bracketPreview.bye_strategy === 'top_seeds' ? 'Nosioci dobijaju slobodna mjesta (ATP standard)' :
                    bracketPreview.bye_strategy === 'balanced' ? 'Ravnomjerna raspodjela' :
                    'Nasumična raspodjela'
                  }
                </div>
                <div className="text-xs text-blue-700">
                  {bracketPreview.bye_strategy === 'top_seeds' && 'Slobodna mjesta dobijaju nosioci po redu (1, 2, 3...)'}
                  {bracketPreview.bye_strategy === 'balanced' && 'Ravnomjerna raspodjela slobodnih mjesta kroz sve kvartale'}
                  {bracketPreview.bye_strategy === 'random' && 'Nasumična raspodjela slobodnih mjesta'}
                </div>
              </div>

              {/* First Round Matches Preview */}
              <div>
                <h3 className="text-sm font-medium text-neutral-700 mb-3">Prvo kolo - pregled mečeva</h3>
                <div className="grid sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto pr-2">
                  {bracketPreview.first_round_matches?.map((match, idx) => (
                    <div
                      key={idx}
                      className={`border-2 rounded-xl overflow-hidden ${
                        match.is_bye ? 'border-rose-200 bg-rose-50' : 'border-neutral-200 bg-white'
                      }`}
                    >
                      <div className="px-3 py-2 bg-neutral-100 border-b border-neutral-200">
                        <div className="text-xs font-medium text-neutral-500">Meč #{match.match_number}</div>
                      </div>
                      <div className="p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            {match.player1 ? (
                              <>
                                <div className="font-medium text-neutral-900">{match.player1.name}</div>
                                {match.player1.seed && (
                                  <div className="text-xs text-rose-500 font-medium">#{match.player1.seed} nosilac</div>
                                )}
                              </>
                            ) : (
                              <div className="text-neutral-400 italic text-sm">-</div>
                            )}
                          </div>
                        </div>
                        <div className="text-center text-xs text-neutral-400 font-medium">VS</div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            {match.player2 ? (
                              <>
                                <div className="font-medium text-neutral-900">{match.player2.name}</div>
                                {match.player2.seed && (
                                  <div className="text-xs text-rose-500 font-medium">#{match.player2.seed} nosilac</div>
                                )}
                              </>
                            ) : (
                              <div className="text-neutral-400 italic text-sm">-</div>
                            )}
                          </div>
                        </div>
                        {match.is_bye && (
                          <div className="mt-2 pt-2 border-t border-rose-200">
                            <div className="text-xs text-rose-600 font-medium">BYE - Automatski prolaz</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-neutral-100 flex gap-3 flex-shrink-0">
              <button
                onClick={() => setShowPreview(false)}
                className="flex-1 px-4 py-2.5 bg-neutral-100 text-neutral-700 rounded-xl text-sm font-medium hover:bg-neutral-200 transition-colors"
              >
                Zatvori
              </button>
              <button
                onClick={handleGenerateBracket}
                className="flex-1 px-4 py-2.5 bg-neutral-900 text-white rounded-xl text-sm font-medium hover:bg-neutral-800 transition-colors flex items-center justify-center gap-2"
              >
                <PlayCircle className="w-4 h-4" /> Potvrdi i generiši žrijeb
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MATCH DETAIL (SCORING) ====================
const MatchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [sets, setSets] = useState([{ player1: 0, player2: 0 }, { player1: 0, player2: 0 }, { player1: 0, player2: 0 }]);

  useEffect(() => {
    matchesAPI.get(id).then(res => {
      setMatch(res.data);
      if (res.data.sets && res.data.sets.length > 0) {
        setSets(res.data.sets.map(s => ({ player1: s.player1_games, player2: s.player2_games })));
      }
    }).catch(() => navigate(-1));
  }, [id, navigate]);

  const handleSave = async () => {
    await matchesAPI.updateScore(id, sets.map((s, i) => ({
      set_number: i + 1,
      player1_games: s.player1,
      player2_games: s.player2
    })));
    navigate(-1);
  };

  if (!match) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-6 w-6 border-2 border-neutral-300 border-t-neutral-600" />
    </div>
  );

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 text-sm">
        <ArrowLeft className="w-4 h-4" /> Nazad
      </button>

      <div className="bg-white rounded-2xl border border-neutral-200/60 overflow-hidden">
        <div className="bg-neutral-900 text-white p-6 text-center">
          <div className="text-sm text-neutral-400 mb-4">{match.round}</div>
          <div className="flex items-center justify-center gap-6">
            <div className="text-center">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-2">
                <span className="text-lg font-semibold">{match.player1_name?.split(' ').map(n => n[0]).join('')}</span>
              </div>
              <div className="text-sm font-medium">{match.player1_name}</div>
            </div>
            <div className="text-2xl font-semibold text-neutral-500">vs</div>
            <div className="text-center">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-2">
                <span className="text-lg font-semibold">{match.player2_name?.split(' ').map(n => n[0]).join('')}</span>
              </div>
              <div className="text-sm font-medium">{match.player2_name}</div>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <h3 className="font-medium text-neutral-900">Unos rezultata</h3>
          <div className="space-y-3">
            {sets.map((set, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="text-sm text-neutral-500 w-14">Set {idx + 1}</span>
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <input
                    type="number"
                    min="0"
                    max="15"
                    value={set.player1}
                    onChange={(e) => {
                      const newSets = [...sets];
                      newSets[idx].player1 = e.target.value === '' ? '' : parseInt(e.target.value);
                      setSets(newSets);
                    }}
                    className="border border-neutral-200 rounded-xl px-4 py-2.5 text-center font-medium text-lg focus:outline-none focus:border-neutral-300"
                  />
                  <input
                    type="number"
                    min="0"
                    max="15"
                    value={set.player2}
                    onChange={(e) => {
                      const newSets = [...sets];
                      newSets[idx].player2 = e.target.value === '' ? '' : parseInt(e.target.value);
                      setSets(newSets);
                    }}
                    className="border border-neutral-200 rounded-xl px-4 py-2.5 text-center font-medium text-lg focus:outline-none focus:border-neutral-300"
                  />
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={handleSave}
            className="w-full bg-neutral-900 hover:bg-neutral-800 text-white py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" /> Sačuvaj rezultat
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== LEAGUES ====================
const Leagues = () => {
  const [leagues, setLeagues] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    points_per_win: 2,
    groups_count: 1,
    players_advancing_per_group: 2,
    ranking_points_per_win: 0
  });
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    leaguesAPI.getAll().then(res => setLeagues(res.data)).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    await leaguesAPI.create(form);
    const res = await leaguesAPI.getAll();
    setLeagues(res.data);
    setShowModal(false);
    setForm({ name: '', start_date: '', end_date: '', points_per_win: 2, groups_count: 1, players_advancing_per_group: 2, ranking_points_per_win: 0 });
  };

  const statusLabels = {
    upcoming: 'Predstojeća',
    group_stage: 'Grupna faza',
    knockout: 'Nokaut faza',
    finished: 'Završena',
    archived: 'Arhivirana',
  };

  const statusColors = {
    upcoming: 'bg-blue-50 text-blue-600',
    group_stage: 'bg-amber-50 text-amber-600',
    knockout: 'bg-purple-50 text-purple-600',
    finished: 'bg-green-50 text-green-600',
    archived: 'bg-neutral-100 text-neutral-500',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Lige</h1>
          <p className="text-neutral-500 mt-1">Upravljanje ligaškim takmičenjima</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-xl text-sm hover:bg-neutral-800 transition-colors"
        >
          <Plus className="w-4 h-4" /> Nova liga
        </button>
      </div>

      {/* Leagues List */}
      {leagues.length > 0 ? (
        <div className="bg-white rounded-2xl border border-neutral-200/60 divide-y divide-neutral-100">
          {leagues.map((l) => (
            <div
              key={l.id}
              onClick={() => navigate(`/panel/leagues/${l.id}`)}
              className="px-4 sm:px-5 py-3 sm:py-4 hover:bg-neutral-50 transition-colors cursor-pointer"
            >
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <span className="text-neutral-900 font-medium text-sm sm:text-base">{l.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-md whitespace-nowrap ${statusColors[l.status]}`}>
                  {statusLabels[l.status] || l.status}
                </span>
              </div>
              <div className="text-xs sm:text-sm text-neutral-500 mt-1">
                <span className="hidden sm:inline">{l.start_date} – {l.end_date} · {l.points_per_win} bod/pob. · {l.groups_count > 1 ? `${l.groups_count} grupe` : 'Bez grupa'} · {l.player_count || 0} igrača</span>
                <span className="sm:hidden flex flex-wrap gap-x-1">
                  <span>{l.start_date} – {l.end_date}</span>
                  <span>· {l.groups_count > 1 ? `${l.groups_count} gr.` : '1 gr.'}</span>
                  <span>· {l.player_count || 0} igr.</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-neutral-400">
          <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nema liga</p>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl flex flex-col max-h-[90vh]">
            <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between flex-shrink-0">
              <h2 className="font-semibold text-neutral-900">Nova liga</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-neutral-100 rounded-xl transition-colors">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1.5">Naziv</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-neutral-50 border-0 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-all"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5">Početak</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="w-full bg-neutral-50 border-0 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5">Završetak</label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="w-full bg-neutral-50 border-0 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-all"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5">Grupe</label>
                  <select
                    value={form.groups_count}
                    onChange={(e) => setForm({ ...form, groups_count: parseInt(e.target.value) })}
                    className="w-full bg-neutral-50 border-0 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-all"
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="4">4</option>
                    <option value="8">8</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5">Prolazi/grupi</label>
                  <input
                    type="number"
                    value={form.players_advancing_per_group}
                    onChange={(e) => setForm({ ...form, players_advancing_per_group: e.target.value === '' ? '' : parseInt(e.target.value) })}
                    className="w-full bg-neutral-50 border-0 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-all"
                    min="1"
                  />
                </div>
              </div>

              {user?.is_super_admin && (
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-neutral-100">
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1.5">Bod/pobjedi</label>
                    <input
                      type="number"
                      value={form.points_per_win}
                      onChange={(e) => setForm({ ...form, points_per_win: e.target.value === '' ? '' : parseInt(e.target.value) })}
                      className="w-full bg-neutral-50 border-0 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-all"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1.5">Rang bod/pob</label>
                    <input
                      type="number"
                      value={form.ranking_points_per_win}
                      onChange={(e) => setForm({ ...form, ranking_points_per_win: e.target.value === '' ? '' : parseInt(e.target.value) })}
                      className="w-full bg-neutral-50 border-0 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-all"
                      min="0"
                    />
                  </div>
                </div>
              )}

              <button
                type="submit"
                onClick={handleCreate}
                className="w-full bg-neutral-900 hover:bg-neutral-800 text-white py-3 rounded-xl text-sm font-semibold transition-colors"
              >
                Kreiraj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== LEAGUE DETAIL ====================
const LeagueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [league, setLeague] = useState(null);
  const [matches, setMatches] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPenaltyModal, setShowPenaltyModal] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [penaltyPlayer, setPenaltyPlayer] = useState(null);
  const [penaltyAmount, setPenaltyAmount] = useState(0);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replacePlayer, setReplacePlayer] = useState(null);
  const [newPlayerId, setNewPlayerId] = useState('');
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [scoreSets, setScoreSets] = useState([{ player1: 0, player2: 0 }]);
  const [filterGroup, setFilterGroup] = useState('');
  const [filterRound, setFilterRound] = useState('1'); // Default to round 1
  const [showAllRounds, setShowAllRounds] = useState(false);
  const [roundsInfo, setRoundsInfo] = useState({ rounds: [], groups: [] });
  const [viewMode, setViewMode] = useState('groups'); // 'groups' or 'knockout'
  const [knockoutMatches, setKnockoutMatches] = useState([]);
  const [knockoutZoom, setKnockoutZoom] = useState(0.9); // Default 90% zoom
  const [bracketEditMode, setBracketEditMode] = useState(false);
  const [bracketSwapMode, setBracketSwapMode] = useState('matches'); // 'matches' | 'players'
  const [swapFirstMatchId, setSwapFirstMatchId] = useState(null);
  const [swapFirstSlot, setSwapFirstSlot] = useState(null); // { matchId, slot } in player mode
  const [editForm, setEditForm] = useState({
    name: '', start_date: '', end_date: '', points_per_win: 2,
    groups_count: 1, players_advancing_per_group: 2, status: 'upcoming',
    knockout_total_points: 500, ranking_points_per_win: 0, rules: '',
    assigned_admin_ids: []
  });
  const [availableAdmins, setAvailableAdmins] = useState([]);

  // Load initial data (league, players, rounds info) - only on mount
  const loadInitialData = useCallback(async () => {
    try {
      // OPTIMIZED: Use getDropdown instead of getAll (no ranking queries)
      const [lRes, pRes, riRes] = await Promise.all([
        leaguesAPI.get(id),
        playersAPI.getDropdown(),
        leaguesAPI.getRoundsInfo(id),
      ]);
      const leagueData = lRes.data;
      setLeague(leagueData);
      setAllPlayers(pRes.data);
      setRoundsInfo(riRes.data || { rounds: [], groups: [] });

      // Load knockout bracket matches if knockout tournament exists
      if (leagueData.knockout_tournament?.id) {
        try {
          const bracketRes = await tournamentsAPI.getBracket(leagueData.knockout_tournament.id);
          setKnockoutMatches(bracketRes.data?.matches || []);
        } catch {
          setKnockoutMatches([]);
        }
      }
    } catch {
      navigate('/panel/leagues');
    }
  }, [id, navigate]);

  // Load matches only - called when filters change
  const loadMatches = useCallback(async () => {
    const filters = {};
    if (filterGroup) filters.group = filterGroup;
    if (showAllRounds) {
      filters.all = true;
    } else {
      filters.round = filterRound;
    }
    try {
      const mRes = await leaguesAPI.getMatches(id, filters);
      setMatches(Array.isArray(mRes.data) ? mRes.data : []);
    } catch {
      setMatches([]);
    }
  }, [id, filterGroup, filterRound, showAllRounds]);

  // Full reload after actions (add player, remove player, etc.)
  const loadData = useCallback(async () => {
    await loadInitialData();
    await loadMatches();
  }, [loadInitialData, loadMatches]);

  // Load initial data on mount
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load matches when filters change OR when league is loaded
  useEffect(() => {
    if (league) loadMatches();
  }, [league, loadMatches]);

  // Get league players with group info
  const leaguePlayers = league?.league_players || [];
  const players = leaguePlayers.map(lp => ({
    id: lp.player?.id,
    leaguePlayerId: lp.id,
    first_name: lp.player?.first_name,
    last_name: lp.player?.last_name,
    groupId: lp.group,
    group_name: lp.group_name,
    point_deduction: lp.point_deduction || 0,
  }));

  const groups = league?.groups || [];
  const availablePlayers = allPlayers.filter(p => !players.find(lp => lp.id === p.id));

  // Group matches by round
  const matchesByRound = matches.reduce((acc, match) => {
    const round = match.round_number || 0;
    if (!acc[round]) acc[round] = [];
    acc[round].push(match);
    return acc;
  }, {});

  const handleAddPlayers = async () => {
    for (const playerId of selectedPlayers) {
      await leaguesAPI.addPlayer(id, playerId, selectedGroup);
    }
    await loadData();
    setShowAddPlayer(false);
    setSelectedPlayers([]);
    setSelectedGroup(null);
  };

  const handleRemovePlayer = async (playerId) => {
    await leaguesAPI.removePlayer(id, playerId);
    await loadData();
  };

  const handleAssignGroup = async (leaguePlayerId, groupId) => {
    await leaguesAPI.assignGroup(id, leaguePlayerId, groupId || null);
    await loadData();
  };

  const handleUpdatePenalty = async () => {
    if (penaltyPlayer) {
      await leaguesAPI.updatePenalty(id, penaltyPlayer.leaguePlayerId, penaltyAmount);
      await loadData();
      setShowPenaltyModal(false);
      setPenaltyPlayer(null);
      setPenaltyAmount(0);
    }
  };

  const openPenaltyModal = (player) => {
    setPenaltyPlayer(player);
    setPenaltyAmount(player.point_deduction || 0);
    setShowPenaltyModal(true);
  };

  const openReplaceModal = (player) => {
    setReplacePlayer(player);
    setNewPlayerId('');
    setShowReplaceModal(true);
  };

  const handleReplacePlayer = async () => {
    if (!replacePlayer || !newPlayerId) return;
    try {
      const res = await leaguesAPI.replacePlayer(id, replacePlayer.id, newPlayerId);
      toast.success(res.data.message || 'Igrač zamijenjen');
      await loadData();
      setShowReplaceModal(false);
      setReplacePlayer(null);
      setNewPlayerId('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri zamjeni igrača');
    }
  };

  const handleWithdrawPlayer = async (player) => {
    const confirmed = await confirm({
      title: 'Povuci igrača',
      message: `Da li ste sigurni da želite povući ${player.first_name} ${player.last_name} iz lige? Mečevi ostaju, ali možete obrisati rezultate ručno.`,
      confirmText: 'Povuci',
      cancelText: 'Odustani'
    });
    if (!confirmed) return;
    try {
      const res = await leaguesAPI.withdrawPlayer(id, player.id);
      toast.success(res.data.message || 'Igrač povučen');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri povlačenju igrača');
    }
  };

  // Check if group stage is locked (knockout has started)
  const isGroupStageLocked = league?.status === 'knockout' || league?.status === 'finished';

  // Match functions
  const handleGenerateMatches = async () => {
    const confirmed = await confirm({
      title: 'Generiši mečeve',
      message: 'Ovo će obrisati postojeće mečeve i generisati nove. Da li ste sigurni?',
      confirmText: 'Generiši',
      cancelText: 'Odustani'
    });
    if (!confirmed) return;
    try {
      const res = await leaguesAPI.generateMatches(id);
      toast.success(res.data.message || 'Mečevi generisani');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri generisanju mečeva');
    }
  };

  const handleOpenScore = (match) => {
    // Prevent editing group stage matches when knockout has started
    if (match.round === 'group' && isGroupStageLocked) {
      toast.error('Grupna faza je zaključana - knockout faza je već počela');
      return;
    }

    setSelectedMatch(match);
    if (match.sets && match.sets.length > 0) {
      setScoreSets(match.sets.map(s => ({ player1: s.player1_games || 0, player2: s.player2_games || 0 })));
    } else {
      setScoreSets([{ player1: 0, player2: 0 }]);
    }
    setShowScoreModal(true);
  };

  const handleToggleBracketEdit = () => {
    setBracketEditMode(prev => !prev);
    setSwapFirstMatchId(null);
    setSwapFirstSlot(null);
  };

  const handleSetSwapMode = (mode) => {
    setBracketSwapMode(mode);
    setSwapFirstMatchId(null);
    setSwapFirstSlot(null);
  };

  const reloadKnockoutBracket = async () => {
    if (!league?.knockout_tournament?.id) return;
    try {
      const bracketRes = await tournamentsAPI.getBracket(league.knockout_tournament.id);
      setKnockoutMatches(bracketRes.data?.matches || []);
    } catch {
      // keep current view on error
    }
  };

  const handleBracketMatchClick = async (match) => {
    if (!bracketEditMode) {
      if (match.player1 && match.player2) handleOpenScore(match);
      return;
    }
    // edit mode: select first, then on second click trigger atomic swap
    if (swapFirstMatchId === null) {
      setSwapFirstMatchId(match.id);
      return;
    }
    if (swapFirstMatchId === match.id) {
      setSwapFirstMatchId(null);
      return;
    }
    const firstMatch = knockoutMatches.find(m => m.id === swapFirstMatchId);
    if (!firstMatch) {
      setSwapFirstMatchId(null);
      return;
    }
    if (firstMatch.round !== match.round) {
      toast.error('Možeš zamijeniti samo mečeve iz istog kola');
      setSwapFirstMatchId(null);
      return;
    }
    const confirmed = await confirm({
      title: 'Zamijeniti pozicije mečeva?',
      message: 'Ovo ne mijenja protivnike u parovima, samo gdje se njihovi pobjednici dalje sretnu u žrijebu.',
      confirmText: 'Zamijeni',
      cancelText: 'Odustani'
    });
    if (!confirmed) {
      setSwapFirstMatchId(null);
      return;
    }
    try {
      await tournamentsAPI.swapMatches(league.knockout_tournament.id, swapFirstMatchId, match.id);
      toast.success('Mečevi zamijenjeni');
      await reloadKnockoutBracket();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri zamjeni');
    } finally {
      setSwapFirstMatchId(null);
    }
  };

  const handleBracketSlotClick = async (match, slot) => {
    // player-swap mode: pick first slot, then second slot -> swap the two players
    if (swapFirstSlot === null) {
      setSwapFirstSlot({ matchId: match.id, slot });
      return;
    }
    if (swapFirstSlot.matchId === match.id && swapFirstSlot.slot === slot) {
      setSwapFirstSlot(null);
      return;
    }
    const firstMatch = knockoutMatches.find(m => m.id === swapFirstSlot.matchId);
    if (!firstMatch) {
      setSwapFirstSlot(null);
      return;
    }
    if (firstMatch.round !== match.round) {
      toast.error('Igrače možeš mijenjati samo unutar istog kola');
      setSwapFirstSlot(null);
      return;
    }
    const confirmed = await confirm({
      title: 'Zamijeniti ova dva igrača?',
      message: 'Dva izabrana igrača zamijeniće mjesta u žrijebu.',
      confirmText: 'Zamijeni',
      cancelText: 'Odustani'
    });
    if (!confirmed) {
      setSwapFirstSlot(null);
      return;
    }
    try {
      await tournamentsAPI.swapPlayers(
        league.knockout_tournament.id,
        swapFirstSlot.matchId, swapFirstSlot.slot,
        match.id, slot
      );
      toast.success('Igrači zamijenjeni');
      await reloadKnockoutBracket();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri zamjeni');
    } finally {
      setSwapFirstSlot(null);
    }
  };

  const addSet = () => {
    if (scoreSets.length < 3) {
      setScoreSets([...scoreSets, { player1: 0, player2: 0 }]);
    }
  };

  const removeSet = (idx) => {
    if (scoreSets.length > 1) {
      setScoreSets(scoreSets.filter((_, i) => i !== idx));
    }
  };

  const handleSaveScore = async () => {
    try {
      const validSets = scoreSets.filter(s => s.player1 > 0 || s.player2 > 0);
      await matchesAPI.updateScore(selectedMatch.id, validSets.map((s, i) => ({
        set_number: i + 1,
        player1_games: s.player1,
        player2_games: s.player2
      })));
      await loadData();
      setShowScoreModal(false);
      setSelectedMatch(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri čuvanju rezultata');
    }
  };

  const handleResetScore = async () => {
    if (!selectedMatch?.id) return;
    try {
      await matchesAPI.resetScore(selectedMatch.id);
      await loadData();
      setShowScoreModal(false);
      setSelectedMatch(null);
      toast.success('Rezultat uspješno obrisan');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri brisanju rezultata');
    }
  };

  const handleForfeit = async (playerId, forfeitType) => {
    if (!selectedMatch?.id || !playerId) return;
    try {
      // First save any entered sets (for retirement with partial score)
      const validSets = scoreSets.filter(s => s.player1 > 0 || s.player2 > 0);
      if (validSets.length > 0) {
        await matchesAPI.updateScore(selectedMatch.id, validSets.map((s, i) => ({
          set_number: i + 1,
          player1_games: s.player1,
          player2_games: s.player2
        })));
      }
      // Then record the forfeit
      await matchesAPI.forfeit(selectedMatch.id, playerId, forfeitType);
      await loadData();
      setShowScoreModal(false);
      setSelectedMatch(null);
      toast.success(forfeitType === 'walkover' ? 'Walkover zabilježen' : 'Predaja zabilježena');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri bilježenju predaje');
    }
  };

  const handleEdit = async () => {
    setEditForm({
      name: league.name,
      start_date: league.start_date,
      end_date: league.end_date,
      points_per_win: league.points_per_win,
      groups_count: league.groups_count,
      players_advancing_per_group: league.players_advancing_per_group,
      knockout_total_points: league.knockout_total_points || 500,
      ranking_points_per_win: league.ranking_points_per_win || 0,
      status: league.status,
      rules: league.rules || '',
      assigned_admin_ids: league.assigned_admins?.map(a => a.id) || []
    });
    // Load available admins for super_admin
    if (user?.is_super_admin) {
      try {
        const res = await usersAPI.getAll();
        // Filter to only show regular admins (not super_admin)
        setAvailableAdmins((res.data || []).filter(u => u.role === 'admin'));
      } catch (err) {
        console.error('Error loading admins:', err);
      }
    }
    setShowEditModal(true);
  };

  const handleStartKnockout = async () => {
    const confirmed = await confirm({
      title: 'Pokreni nokaut fazu',
      message: 'Ovo će kreirati nokaut turnir sa igračima koji su prošli grupnu fazu. Da li ste sigurni?',
      confirmText: 'Pokreni',
      cancelText: 'Odustani'
    });
    if (!confirmed) return;
    try {
      const res = await leaguesAPI.startKnockout(id);
      toast.success(res.data.message || 'Nokaut faza započeta');
      await loadData();
      setViewMode('knockout');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri pokretanju nokaut faze');
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    await leaguesAPI.update(id, editForm);
    await loadData();
    setShowEditModal(false);
  };

  const handleDelete = async () => {
    await leaguesAPI.delete(id);
    navigate('/panel/leagues');
  };

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayers(prev =>
      prev.includes(playerId)
        ? prev.filter(pid => pid !== playerId)
        : [...prev, playerId]
    );
  };

  const statusLabels = {
    upcoming: 'Predstojeća',
    group_stage: 'Grupna faza',
    knockout: 'Nokaut faza',
    finished: 'Završena',
    archived: 'Arhivirana',
  };

  const statusColors = {
    upcoming: 'bg-blue-50 text-blue-600',
    group_stage: 'bg-amber-50 text-amber-600',
    knockout: 'bg-purple-50 text-purple-600',
    finished: 'bg-green-50 text-green-600',
    archived: 'bg-neutral-100 text-neutral-500',
  };

  if (!league) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-6 w-6 border-2 border-neutral-300 border-t-neutral-600" />
    </div>
  );

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/panel/leagues')} className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 text-sm">
        <ArrowLeft className="w-4 h-4" /> Nazad na lige
      </button>

      {/* League Header */}
      <div className="bg-white rounded-2xl border border-neutral-200/60 p-6">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${statusColors[league.status]}`}>
                {statusLabels[league.status] || league.status}
              </span>
              <span className="text-sm text-neutral-500">{league.points_per_win} bod/pobjedi</span>
            </div>
            <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">{league.name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-neutral-500 text-sm">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" /> {league.start_date} – {league.end_date}
              </span>
            </div>
          </div>
          {user?.is_super_admin && (
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={handleEdit}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-neutral-200 text-neutral-700 rounded-xl text-sm hover:bg-neutral-50 transition-colors"
              >
                <Edit className="w-4 h-4" /> Uredi
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-neutral-200 text-red-600 rounded-xl text-sm hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" /> Obriši
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Players */}
        <div className="bg-white rounded-2xl border border-neutral-200/60 overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
            <h2 className="font-medium text-neutral-900">Igrači ({players.length})</h2>
            {league.status === 'upcoming' && user?.is_super_admin && (
              <button
                onClick={() => setShowAddPlayer(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-neutral-100 text-neutral-700 rounded-lg text-sm hover:bg-neutral-200 transition-colors"
              >
                <Plus className="w-4 h-4" /> Dodaj
              </button>
            )}
          </div>
          <div className="divide-y divide-neutral-100 max-h-96 overflow-y-auto">
            {players.map((p, idx) => (
              <div key={p.id} className="flex items-center gap-3 px-5 py-3 hover:bg-neutral-50 group">
                <div className="w-7 h-7 rounded-lg bg-neutral-100 flex items-center justify-center text-xs font-medium text-neutral-500">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <span className="text-neutral-900">{p.first_name} {p.last_name}</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    {groups.length > 0 && league.status === 'upcoming' && user?.is_super_admin && (
                      <select
                        value={p.groupId || ''}
                        onChange={(e) => handleAssignGroup(p.leaguePlayerId, e.target.value)}
                        className="text-xs border border-neutral-200 rounded px-1.5 py-0.5 bg-white"
                      >
                        <option value="">Bez grupe</option>
                        {groups.map(g => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                    )}
                    {groups.length > 0 && league.status !== 'upcoming' && p.groupName && (
                      <span className="text-xs text-neutral-500">{p.groupName}</span>
                    )}
                    {p.point_deduction > 0 && (
                      <span className="text-xs text-red-500">-{p.point_deduction} bod</span>
                    )}
                  </div>
                </div>
                {user?.is_super_admin && (
                  <div className="flex items-center gap-1">
                    {league.status === 'upcoming' && (
                      <>
                        <button
                          onClick={() => openPenaltyModal(p)}
                          className="p-1.5 text-neutral-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all sm:opacity-0 sm:group-hover:opacity-100"
                          title="Kazna"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRemovePlayer(p.id)}
                          className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all sm:opacity-0 sm:group-hover:opacity-100"
                          title="Ukloni"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {/* Zamjena i povlačenje - dostupno i za aktivne lige */}
                    <button
                      onClick={() => openReplaceModal(p)}
                      className="p-1.5 text-neutral-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all sm:opacity-0 sm:group-hover:opacity-100"
                      title="Zamijeni igrača"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    {league.status === 'group_stage' && !league.knockout_tournament && (
                      <button
                        onClick={() => handleWithdrawPlayer(p)}
                        className="p-1.5 text-neutral-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-all sm:opacity-0 sm:group-hover:opacity-100"
                        title="Povuci iz lige"
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {players.length === 0 && (
              <div className="px-5 py-8 text-center text-neutral-400 text-sm">Nema igrača</div>
            )}
          </div>
        </div>

        {/* Standings / Knockout View */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-neutral-200/60 overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('groups')}
                  className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${viewMode === 'groups' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
                >
                  Grupna
                </button>
                <button
                  onClick={() => setViewMode('knockout')}
                  className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${viewMode === 'knockout' ? 'bg-purple-600 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
                >
                  Nokaut
                </button>
              </div>
              {!league.knockout_tournament && league.status !== 'finished' && (() => {
                const totalMatches = roundsInfo.total_matches || 0;
                const completedMatches = roundsInfo.completed_matches || 0;
                const allCompleted = totalMatches > 0 && completedMatches === totalMatches;
                return (
                  <div className="flex items-center gap-2 sm:gap-3">
                    {totalMatches > 0 && (
                      <span className={`text-xs ${allCompleted ? 'text-green-600' : 'text-amber-600'}`}>
                        {completedMatches}/{totalMatches}
                      </span>
                    )}
                    {user?.is_super_admin && (
                      <button
                        onClick={handleStartKnockout}
                        disabled={!allCompleted}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                          allCompleted
                            ? 'bg-purple-600 text-white hover:bg-purple-700'
                            : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                        }`}
                      >
                        <Trophy className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Nokaut
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
            {viewMode === 'groups' && (
              <div className="flex items-center gap-2">
                <p className="text-xs text-neutral-500">Rangiranje: Bodovi → Međusobni duel → Set razlika → Gem razlika</p>
                {isGroupStageLocked && (
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Zaključano
                  </span>
                )}
              </div>
            )}
            {viewMode === 'knockout' && league.knockout_tournament && (
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs text-purple-600">Nokaut faza • {league.knockout_tournament.total_points} rang bodova za pobjednika</p>
                {user?.is_super_admin && knockoutMatches.length > 0 && (
                  <button
                    onClick={handleToggleBracketEdit}
                    className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                      bracketEditMode
                        ? 'bg-amber-500 text-white hover:bg-amber-600'
                        : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    }`}
                  >
                    {bracketEditMode ? 'Izlaz iz uređivanja' : 'Uredi zrijeb'}
                  </button>
                )}
                {bracketEditMode && (
                  <>
                    <span className="inline-flex rounded-lg bg-neutral-100 p-0.5">
                      <button
                        onClick={() => handleSetSwapMode('players')}
                        className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
                          bracketSwapMode === 'players' ? 'bg-white text-amber-700 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
                        }`}
                      >
                        Igrači
                      </button>
                      <button
                        onClick={() => handleSetSwapMode('matches')}
                        className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
                          bracketSwapMode === 'matches' ? 'bg-white text-amber-700 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
                        }`}
                      >
                        Mečevi
                      </button>
                    </span>
                    <span className="text-xs text-amber-700">
                      {bracketSwapMode === 'players'
                        ? (swapFirstSlot ? 'Sad klikni drugog igrača iz istog kola' : 'Klikni dva igrača da im zamijeniš mjesta')
                        : (swapFirstMatchId ? 'Sad klikni drugi meč iz istog kola za zamjenu' : 'Klikni dva meča iz istog kola da im zamijeniš pozicije')}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="p-5">
            {viewMode === 'groups' ? (
              // Group standings view
              league.standings && typeof league.standings === 'object' && Object.keys(league.standings).length > 0 ? (
                <div className="space-y-6">
                  {Object.entries(league.standings).map(([groupName, groupStandings]) => (
                    <div key={groupName}>
                      {Object.keys(league.standings).length > 1 && (
                        <h3 className="font-medium text-neutral-700 mb-3">{groupName}</h3>
                      )}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs text-neutral-500 uppercase border-b border-neutral-100">
                              <th className="text-left py-2 px-2 w-8">#</th>
                              <th className="text-left py-2 px-2">Igrač</th>
                              <th className="text-center py-2 px-2 w-10">M</th>
                              <th className="text-center py-2 px-2 w-10">P</th>
                              <th className="text-center py-2 px-2 w-10">I</th>
                              <th className="text-center py-2 px-2 w-16">Setovi</th>
                              <th className="text-center py-2 px-2 w-16">Gemovi</th>
                              <th className="text-right py-2 px-2 w-14">Bod</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.isArray(groupStandings) && groupStandings.map((s, idx) => (
                              <tr key={s.player?.id || idx} className={`border-b border-neutral-50 ${idx < league.players_advancing_per_group ? 'bg-green-50/50' : ''}`}>
                                <td className={`py-2.5 px-2 font-medium ${idx < league.players_advancing_per_group ? 'text-green-600' : 'text-neutral-500'}`}>
                                  {s.rank || idx + 1}
                                </td>
                                <td className="py-2.5 px-2 text-neutral-900">
                                  {s.player?.first_name} {s.player?.last_name}
                                  {s.point_deduction > 0 && <span className="text-red-500 text-xs ml-1">(-{s.point_deduction})</span>}
                                </td>
                                <td className="py-2.5 px-2 text-center text-neutral-600">{s.matches_played}</td>
                                <td className="py-2.5 px-2 text-center text-green-600 font-medium">{s.wins}</td>
                                <td className="py-2.5 px-2 text-center text-red-500">{s.losses}</td>
                                <td className="py-2.5 px-2 text-center text-neutral-600">
                                  <span className="text-green-600">{s.sets_won}</span>-<span className="text-red-500">{s.sets_lost}</span>
                                  <span className="text-xs text-neutral-400 ml-1">({s.set_diff >= 0 ? '+' : ''}{s.set_diff})</span>
                                </td>
                                <td className="py-2.5 px-2 text-center text-neutral-600">
                                  <span className="text-green-600">{s.games_won}</span>-<span className="text-red-500">{s.games_lost}</span>
                                  <span className="text-xs text-neutral-400 ml-1">({s.game_diff >= 0 ? '+' : ''}{s.game_diff})</span>
                                </td>
                                <td className="py-2.5 px-2 text-right font-semibold text-neutral-900">{s.total_points}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {Object.keys(league.standings).length > 1 && (
                        <p className="text-xs text-green-600 mt-2">Zeleno = prolazi dalje ({league.players_advancing_per_group} iz grupe)</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-neutral-400">
                  <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Nema podataka o tabeli. Dodajte igrače i odigrajte mečeve.</p>
                </div>
              )
            ) : (
              // Knockout bracket view with SVG lines
              league.knockout_tournament ? (
                <div>
                  {knockoutMatches.length > 0 ? (
                    (() => {
                      const roundLabels = {
                        'round64': '1. kolo',
                        'round32': 'Šesnaestina finala',
                        'round16': 'Osmina finala',
                        'quarterfinal': 'Četvrtfinale',
                        'semifinal': 'Polufinale',
                        'final': 'Finale'
                      };
                      const roundOrder = ['round64', 'round32', 'round16', 'quarterfinal', 'semifinal', 'final'];
                      const koMatchesByRound = knockoutMatches.reduce((acc, m) => {
                        if (!acc[m.round]) acc[m.round] = [];
                        acc[m.round].push(m);
                        return acc;
                      }, {});
                      const sortedRounds = Object.keys(koMatchesByRound).sort((a, b) => roundOrder.indexOf(a) - roundOrder.indexOf(b));
                      const firstRound = sortedRounds[0];
                      const firstRoundMatchCount = koMatchesByRound[firstRound]?.length || 1;

                      // Match box dimensions
                      const matchWidth = 208;
                      const matchHeight = 80;
                      const roundGap = 56;
                      const headerHeight = 32;

                      // Calculate vertical spacing for each round
                      const getMatchPositions = (roundIndex, matchCount) => {
                        const positions = [];
                        if (roundIndex === 0) {
                          const gap = 24;
                          for (let i = 0; i < matchCount; i++) {
                            positions.push(i * (matchHeight + gap));
                          }
                        } else {
                          const prevMatchCount = matchCount * 2;
                          const prevPositions = getMatchPositions(roundIndex - 1, prevMatchCount);
                          for (let i = 0; i < matchCount; i++) {
                            const topMatch = prevPositions[i * 2];
                            const bottomMatch = prevPositions[i * 2 + 1];
                            positions.push((topMatch + bottomMatch + matchHeight) / 2 - matchHeight / 2);
                          }
                        }
                        return positions;
                      };

                      const firstRoundPositions = getMatchPositions(0, firstRoundMatchCount);
                      const totalHeight = firstRoundPositions[firstRoundPositions.length - 1] + matchHeight + headerHeight + 24;
                      const totalWidth = sortedRounds.length * (matchWidth + roundGap);

                      const scaledWidth = totalWidth * knockoutZoom;
                      const scaledHeight = totalHeight * knockoutZoom;

                      return (
                        <>
                          {/* Zoom controls */}
                          <div className="flex items-center justify-end gap-2 mb-3">
                            <span className="text-xs text-neutral-500">Zoom:</span>
                            <button
                              onClick={() => setKnockoutZoom(Math.max(0.5, knockoutZoom - 0.1))}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-600 text-sm font-medium"
                            >
                              −
                            </button>
                            <span className="text-xs text-neutral-600 w-10 text-center">{Math.round(knockoutZoom * 100)}%</span>
                            <button
                              onClick={() => setKnockoutZoom(Math.min(1.5, knockoutZoom + 0.1))}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-600 text-sm font-medium"
                            >
                              +
                            </button>
                          </div>
                          <div className="overflow-x-auto overflow-y-auto border border-neutral-200 rounded-xl bg-neutral-50 p-4">
                            <div style={{ width: scaledWidth, height: scaledHeight, overflow: 'hidden' }}>
                              <div style={{ transform: `scale(${knockoutZoom})`, transformOrigin: 'top left' }}>
                                <div className="relative" style={{ width: totalWidth, height: totalHeight }}>
                                  {/* SVG Layer for connecting lines */}
                                  <svg
                                    className="absolute inset-0 pointer-events-none"
                                    style={{ width: totalWidth, height: totalHeight }}
                                  >
                                    {sortedRounds.slice(0, -1).map((round, roundIdx) => {
                                      const currentMatches = koMatchesByRound[round]?.sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0)) || [];
                                      const nextRound = sortedRounds[roundIdx + 1];
                                      const nextMatches = koMatchesByRound[nextRound]?.sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0)) || [];
                                      const currentPositions = getMatchPositions(roundIdx, currentMatches.length);
                                      const nextPositions = getMatchPositions(roundIdx + 1, nextMatches.length);

                                      const xStart = roundIdx * (matchWidth + roundGap) + matchWidth;
                                      const xEnd = (roundIdx + 1) * (matchWidth + roundGap);
                                      const xMid = (xStart + xEnd) / 2;

                                      return currentMatches.map((match, matchIdx) => {
                                        const nextMatchIdx = Math.floor(matchIdx / 2);
                                        const yStart = headerHeight + currentPositions[matchIdx] + matchHeight / 2;
                                        const yEnd = headerHeight + nextPositions[nextMatchIdx] + matchHeight / 2;

                                        return (
                                          <g key={`${round}-${matchIdx}`}>
                                            {/* Horizontal line from match to midpoint */}
                                            <line
                                              x1={xStart}
                                              y1={yStart}
                                              x2={xMid}
                                              y2={yStart}
                                              stroke="#d4d4d4"
                                              strokeWidth="2"
                                            />
                                            {/* Vertical line to connect pair */}
                                            {matchIdx % 2 === 0 && matchIdx + 1 < currentMatches.length && (
                                              <line
                                                x1={xMid}
                                                y1={yStart}
                                                x2={xMid}
                                                y2={headerHeight + currentPositions[matchIdx + 1] + matchHeight / 2}
                                                stroke="#d4d4d4"
                                                strokeWidth="2"
                                              />
                                            )}
                                            {/* Horizontal line from midpoint to next match */}
                                            {matchIdx % 2 === 0 && (
                                              <line
                                                x1={xMid}
                                                y1={yEnd}
                                                x2={xEnd}
                                                y2={yEnd}
                                                stroke="#d4d4d4"
                                                strokeWidth="2"
                                              />
                                            )}
                                          </g>
                                        );
                                      });
                                    })}
                                  </svg>

                                  {/* Match boxes layer */}
                                  {sortedRounds.map((round, roundIdx) => {
                                    const roundMatches = koMatchesByRound[round]?.sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0)) || [];
                                    const positions = getMatchPositions(roundIdx, roundMatches.length);
                                    const xPos = roundIdx * (matchWidth + roundGap);

                                    return (
                                      <div key={round} className="absolute" style={{ left: xPos, top: 0, width: matchWidth }}>
                                        <div className="text-xs font-medium text-neutral-400 uppercase tracking-wide text-center py-2" style={{ height: headerHeight }}>
                                          {roundLabels[round] || round}
                                        </div>
                                        {roundMatches.map((m, matchIdx) => {
                                          const p1 = m.player1;
                                          const p2 = m.player2;
                                          const winnerId = m.winner?.id || m.winner;
                                          const isBye = m.status === 'completed' && m.winner && (!p1 || !p2);
                                          const p1Name = p1 ? (m.player1_name || p1.full_name || `${p1.first_name} ${p1.last_name}`) : null;
                                          const p2Name = p2 ? (m.player2_name || p2.full_name || `${p2.first_name} ${p2.last_name}`) : null;

                                          const editingMatches = bracketEditMode && bracketSwapMode === 'matches';
                                          const editingPlayers = bracketEditMode && bracketSwapMode === 'players';
                                          const isMatchSelected = editingMatches && swapFirstMatchId === m.id;
                                          const isP1Selected = editingPlayers && swapFirstSlot?.matchId === m.id && swapFirstSlot?.slot === 'p1';
                                          const isP2Selected = editingPlayers && swapFirstSlot?.matchId === m.id && swapFirstSlot?.slot === 'p2';
                                          const boxClickable = editingMatches || (!bracketEditMode && p1 && p2);
                                          return (
                                            <div
                                              key={m.id}
                                              onClick={() => {
                                                if (editingMatches) {
                                                  handleBracketMatchClick(m);
                                                } else if (!bracketEditMode && p1 && p2) {
                                                  handleOpenScore(m);
                                                }
                                              }}
                                              className={`absolute bg-white rounded-xl overflow-hidden transition-colors border ${
                                                isMatchSelected
                                                  ? 'border-amber-500 ring-2 ring-amber-300 bg-amber-50'
                                                  : 'border-neutral-200'
                                              } ${boxClickable ? `cursor-pointer ${editingMatches ? 'hover:border-amber-400' : 'hover:bg-rose-50 hover:border-rose-300'}` : ''} ${isBye ? 'opacity-60' : ''}`}
                                              style={{
                                                top: headerHeight + positions[matchIdx],
                                                left: 0,
                                                width: matchWidth,
                                                height: matchHeight
                                              }}
                                            >
                                              <div
                                                onClick={editingPlayers ? (e) => { e.stopPropagation(); handleBracketSlotClick(m, 'p1'); } : undefined}
                                                className={`px-3 flex items-center justify-between border-b border-neutral-200 ${
                                                  isP1Selected ? 'bg-amber-100 ring-2 ring-inset ring-amber-300' : (winnerId === p1?.id ? 'bg-green-50' : '')
                                                } ${editingPlayers ? 'cursor-pointer hover:bg-amber-50' : ''}`}
                                                style={{ height: matchHeight / 2 }}
                                              >
                                                <span className={`text-sm truncate flex-1 ${winnerId === p1?.id ? 'font-semibold text-green-700' : p1 ? 'text-neutral-700' : 'text-neutral-300 italic'}`}>
                                                  {p1 ? p1Name : (isBye ? 'BYE' : '')}
                                                </span>
                                                {m.sets?.length > 0 && p1 && (
                                                  <div className="flex gap-1 ml-2">
                                                    {m.sets.map((s, idx) => {
                                                      const won = s.player1_games > s.player2_games;
                                                      return (
                                                        <span key={idx} className={`w-5 h-5 flex items-center justify-center rounded text-xs font-semibold ${won ? 'bg-green-500 text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                                                          {s.player1_games}
                                                        </span>
                                                      );
                                                    })}
                                                  </div>
                                                )}
                                              </div>
                                              <div
                                                onClick={editingPlayers ? (e) => { e.stopPropagation(); handleBracketSlotClick(m, 'p2'); } : undefined}
                                                className={`px-3 flex items-center justify-between ${
                                                  isP2Selected ? 'bg-amber-100 ring-2 ring-inset ring-amber-300' : (winnerId === p2?.id ? 'bg-green-50' : '')
                                                } ${editingPlayers ? 'cursor-pointer hover:bg-amber-50' : ''}`}
                                                style={{ height: matchHeight / 2 }}
                                              >
                                                <span className={`text-sm truncate flex-1 ${winnerId === p2?.id ? 'font-semibold text-green-700' : p2 ? 'text-neutral-700' : 'text-neutral-300 italic'}`}>
                                                  {p2 ? p2Name : (isBye ? 'BYE' : '')}
                                                </span>
                                                {m.sets?.length > 0 && p2 && (
                                                  <div className="flex gap-1 ml-2">
                                                    {m.sets.map((s, idx) => {
                                                      const won = s.player2_games > s.player1_games;
                                                      return (
                                                        <span key={idx} className={`w-5 h-5 flex items-center justify-center rounded text-xs font-semibold ${won ? 'bg-green-500 text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                                                          {s.player2_games}
                                                        </span>
                                                      );
                                                    })}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()
                  ) : (
                    <div className="text-center py-12 text-neutral-400">
                      <Trophy className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Učitavam nokaut žrijeb...</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-neutral-400">
                  <Trophy className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Nokaut faza nije još započeta.</p>
                  {league.status === 'group_stage' && (
                    <p className="text-xs mt-2">Završite grupnu fazu i kliknite "Započni nokaut fazu" da kreirate nokaut turnir.</p>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Matches Section */}
      <div className="bg-white rounded-2xl border border-neutral-200/60 overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-100">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-medium text-neutral-900">Mečevi ({matches.length})</h2>
            {league.status === 'upcoming' && user?.is_super_admin && (
              <button
                onClick={handleGenerateMatches}
                disabled={players.length < 2}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-neutral-900 text-white rounded-lg text-sm hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-4 h-4" /> Generiši mečeve
              </button>
            )}
          </div>
          {/* Filters - styled like public site */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Round filter buttons */}
            {roundsInfo.rounds.length > 0 && (
              <>
                {roundsInfo.rounds.map(r => (
                  <button
                    key={r.round}
                    onClick={() => { setShowAllRounds(false); setFilterRound(String(r.round)); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      !showAllRounds && filterRound === String(r.round)
                        ? 'bg-emerald-600 text-white'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                  >
                    Kolo {r.round}
                  </button>
                ))}
                <button
                  onClick={() => setShowAllRounds(true)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    showAllRounds
                      ? 'bg-emerald-600 text-white'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  Sva kola
                </button>
              </>
            )}
            {/* Group filter buttons */}
            {groups.length > 1 && (
              <>
                {roundsInfo.rounds.length > 0 && <span className="text-neutral-300">|</span>}
                <button
                  onClick={() => setFilterGroup('')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    !filterGroup
                      ? 'bg-purple-600 text-white'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  Sve grupe
                </button>
                {groups.map(g => (
                  <button
                    key={g.id}
                    onClick={() => setFilterGroup(String(g.id))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filterGroup === String(g.id)
                        ? 'bg-purple-600 text-white'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                  >
                    {g.name}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
        <div className="p-5">
          {matches.length > 0 ? (
            <div className="space-y-6">
              {Object.entries(matchesByRound).sort(([a], [b]) => Number(a) - Number(b)).map(([roundNum, roundMatches]) => {
                // Group matches by league_group_name within each round
                const matchesByGroup = roundMatches.reduce((acc, m) => {
                  const groupName = m.league_group_name || 'Mečevi';
                  if (!acc[groupName]) acc[groupName] = [];
                  acc[groupName].push(m);
                  return acc;
                }, {});
                const groupNames = Object.keys(matchesByGroup).sort();
                const hasMultipleGroups = groupNames.length > 1;

                return (
                <div key={roundNum}>
                  <h3 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-neutral-100 flex items-center justify-center text-xs">{roundNum}</span>
                    Kolo {roundNum}
                    <span className="text-neutral-400 font-normal">
                      ({roundMatches.filter(m => m.status === 'completed').length}/{roundMatches.length})
                    </span>
                  </h3>
                  <div className={hasMultipleGroups ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-1.5'}>
                    {groupNames.map(groupName => (
                      <div key={groupName}>
                        {hasMultipleGroups && (
                          <div className="text-[10px] font-medium text-neutral-500 uppercase tracking-wide mb-2">{groupName}</div>
                        )}
                        <div className="space-y-1.5">
                          {matchesByGroup[groupName].map(match => {
                            const isCompleted = match.status === 'completed';
                            const winnerId = match.winner?.id || match.winner;
                            const p1 = match.player1_detail || match.player1;
                            const p2 = match.player2_detail || match.player2;
                            const p1Won = winnerId === (match.player1?.id || p1?.id);
                            const p2Won = winnerId === (match.player2?.id || p2?.id);
                            return (
                              <div
                                key={match.id}
                                className={`rounded-lg border transition-colors group relative ${isCompleted ? 'bg-white border-neutral-200' : 'bg-neutral-50 border-neutral-100 hover:border-neutral-200'}`}
                              >
                                {/* Edit button - positioned absolutely */}
                                {isCompleted && (
                                  <button
                                    onClick={() => handleOpenScore(match)}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 sm:opacity-0 sm:group-hover:opacity-100 p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded transition-all z-10"
                                    title="Izmijeni rezultat"
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {!isCompleted && (
                                  <button
                                    onClick={() => handleOpenScore(match)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-neutral-900 text-white rounded text-[10px] font-medium hover:bg-neutral-800 transition-colors z-10"
                                  >
                                    Unesi
                                  </button>
                                )}
                                {/* Player 1 row */}
                                <div className={`flex items-center justify-between px-3 py-1.5 pr-10 ${p1Won ? 'bg-green-50 rounded-t-lg' : ''}`}>
                                  <span className={`text-xs sm:text-sm truncate flex-1 ${p1Won ? 'font-semibold text-green-700' : 'text-neutral-700'}`}>
                                    {p1?.first_name?.[0]}. {p1?.last_name}
                                  </span>
                                  {isCompleted && match.sets?.length > 0 && (
                                    <div className="flex gap-1.5 ml-2">
                                      {match.sets.map((s, i) => (
                                        <span key={i} className={`text-xs font-mono w-4 text-center ${s.player1_games > s.player2_games ? 'font-bold text-neutral-900' : 'text-neutral-400'}`}>
                                          {s.player1_games}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {isCompleted && match.forfeit_type && !match.sets?.length && (
                                    <span className={`text-[10px] font-medium ml-2 ${p1Won ? 'text-green-600' : 'text-neutral-400'}`}>
                                      {p1Won ? 'W.O.' : ''}
                                    </span>
                                  )}
                                </div>
                                {/* Player 2 row */}
                                <div className={`flex items-center justify-between px-3 py-1.5 pr-10 border-t border-neutral-100 ${p2Won ? 'bg-green-50 rounded-b-lg' : ''}`}>
                                  <span className={`text-xs sm:text-sm truncate flex-1 ${p2Won ? 'font-semibold text-green-700' : 'text-neutral-700'}`}>
                                    {p2?.first_name?.[0]}. {p2?.last_name}
                                  </span>
                                  {isCompleted && match.sets?.length > 0 && (
                                    <div className="flex gap-1.5 ml-2">
                                      {match.sets.map((s, i) => (
                                        <span key={i} className={`text-xs font-mono w-4 text-center ${s.player2_games > s.player1_games ? 'font-bold text-neutral-900' : 'text-neutral-400'}`}>
                                          {s.player2_games}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {isCompleted && match.forfeit_type && !match.sets?.length && (
                                    <span className={`text-[10px] font-medium ml-2 ${p2Won ? 'text-green-600' : 'text-neutral-400'}`}>
                                      {p2Won ? 'W.O.' : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-neutral-400">
              <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nema mečeva.</p>
              <p className="text-xs mt-1">Dodajte igrače pa kliknite "Generiši mečeve"</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Players Modal */}
      {showAddPlayer && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between flex-shrink-0">
              <h2 className="font-semibold text-neutral-900">Dodaj igrače</h2>
              <button onClick={() => { setShowAddPlayer(false); setSelectedPlayers([]); setSelectedGroup(null); setPlayerSearchQuery(''); }} className="p-1.5 hover:bg-neutral-100 rounded-lg">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            {groups.length > 0 && (
              <div className="px-6 py-3 border-b border-neutral-100">
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Dodaj u grupu</label>
                <select
                  value={selectedGroup || ''}
                  onChange={(e) => setSelectedGroup(e.target.value || null)}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                >
                  <option value="">Bez grupe</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="px-4 pt-4 pb-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Pretraži igrače..."
                  value={playerSearchQuery}
                  onChange={(e) => setPlayerSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {availablePlayers.length > 0 ? (
                <div className="space-y-1">
                  {availablePlayers.filter(p =>
                    !playerSearchQuery ||
                    `${p.first_name} ${p.last_name}`.toLowerCase().includes(playerSearchQuery.toLowerCase())
                  ).map(p => (
                    <label
                      key={p.id}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-colors ${
                        selectedPlayers.includes(p.id) ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPlayers.includes(p.id)}
                        onChange={() => togglePlayerSelection(p.id)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                        selectedPlayers.includes(p.id) ? 'border-white bg-white' : 'border-neutral-300'
                      }`}>
                        {selectedPlayers.includes(p.id) && <Check className="w-3 h-3 text-neutral-900" />}
                      </div>
                      <span className="flex-1">{p.first_name} {p.last_name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-neutral-400 text-sm">
                  Svi igrači su već dodati u ligu
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-neutral-100 flex-shrink-0">
              <button
                onClick={handleAddPlayers}
                disabled={selectedPlayers.length === 0}
                className="w-full bg-neutral-900 hover:bg-neutral-800 text-white py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
              >
                Dodaj {selectedPlayers.length > 0 ? `(${selectedPlayers.length})` : ''} u ligu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Penalty Modal */}
      {showPenaltyModal && penaltyPlayer && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <h2 className="font-semibold text-neutral-900 mb-4">Kazna - {penaltyPlayer.first_name} {penaltyPlayer.last_name}</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Oduzeti bodovi</label>
              <input
                type="number"
                value={penaltyAmount}
                onChange={(e) => setPenaltyAmount(e.target.value === '' ? '' : parseInt(e.target.value))}
                className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                min="0"
              />
              <p className="text-xs text-neutral-500 mt-1">Unesite 0 da uklonite kaznu</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowPenaltyModal(false); setPenaltyPlayer(null); }}
                className="flex-1 px-4 py-2.5 border border-neutral-200 text-neutral-700 rounded-xl text-sm font-medium hover:bg-neutral-50 transition-colors"
              >
                Otkaži
              </button>
              <button
                onClick={handleUpdatePenalty}
                className="flex-1 px-4 py-2.5 bg-neutral-900 text-white rounded-xl text-sm font-medium hover:bg-neutral-800 transition-colors"
              >
                Sačuvaj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace Player Modal */}
      {showReplaceModal && replacePlayer && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            <h2 className="font-semibold text-neutral-900 mb-4">
              Zamijeni igrača
            </h2>
            <p className="text-sm text-neutral-600 mb-4">
              Zamjena: <span className="font-medium">{replacePlayer.first_name} {replacePlayer.last_name}</span>
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Novi igrač</label>
              <select
                value={newPlayerId}
                onChange={(e) => setNewPlayerId(e.target.value)}
                className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
              >
                <option value="">Izaberi igrača...</option>
                {allPlayers
                  .filter(p => !players.find(lp => lp.id === p.id))
                  .map(p => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                  ))
                }
              </select>
              <p className="text-xs text-neutral-500 mt-1">
                Novi igrač preuzima poziciju u grupi i sve mečeve
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowReplaceModal(false); setReplacePlayer(null); setNewPlayerId(''); }}
                className="flex-1 px-4 py-2.5 border border-neutral-200 text-neutral-700 rounded-xl text-sm font-medium hover:bg-neutral-50 transition-colors"
              >
                Otkaži
              </button>
              <button
                onClick={handleReplacePlayer}
                disabled={!newPlayerId}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Zamijeni
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit League Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-semibold text-neutral-900">Uredi ligu</h2>
              <button onClick={() => setShowEditModal(false)} className="p-1.5 hover:bg-neutral-100 rounded-lg">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <form onSubmit={handleSaveEdit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Naziv</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">Datum početka</label>
                  <input
                    type="date"
                    value={editForm.start_date}
                    onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                    className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">Datum završetka</label>
                  <input
                    type="date"
                    value={editForm.end_date}
                    onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                    className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                    required
                  />
                </div>
              </div>
              {user?.is_super_admin && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1.5">Bod/pobjedi (tabela)</label>
                      <input
                        type="number"
                        value={editForm.points_per_win}
                        onChange={(e) => setEditForm({ ...editForm, points_per_win: e.target.value === '' ? '' : parseInt(e.target.value) })}
                        className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1.5">Nokaut bodovi</label>
                      <input
                        type="number"
                        value={editForm.knockout_total_points}
                        onChange={(e) => setEditForm({ ...editForm, knockout_total_points: e.target.value === '' ? '' : parseInt(e.target.value) })}
                        className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                        min="0"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">Rang bodovi po pobjedi</label>
                    <input
                      type="number"
                      value={editForm.ranking_points_per_win}
                      onChange={(e) => setEditForm({ ...editForm, ranking_points_per_win: e.target.value === '' ? '' : parseInt(e.target.value) })}
                      className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                      min="0"
                      placeholder="0 = bez rang bodova"
                    />
                    <p className="text-xs text-neutral-500 mt-1">Bodovi za rang listu za svaku pobjedu u ligi (0 = bez bodova)</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">Broj grupa</label>
                  <select
                    value={editForm.groups_count}
                    onChange={(e) => setEditForm({ ...editForm, groups_count: parseInt(e.target.value) })}
                    className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300 bg-white"
                  >
                    <option value="1">1 grupa (bez podjele)</option>
                    <option value="2">2 grupe (ATP Finals)</option>
                    <option value="4">4 grupe (UEFA/FIBA)</option>
                    <option value="8">8 grupa (FIFA Svjetsko)</option>
                  </select>
                  <p className="text-xs text-neutral-500 mt-1">
                    Samo ove kombinacije omogućavaju pravičan knockout žrijeb
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">Prolazi/grupe</label>
                  <input
                    type="number"
                    value={editForm.players_advancing_per_group}
                    onChange={(e) => setEditForm({ ...editForm, players_advancing_per_group: e.target.value === '' ? '' : parseInt(e.target.value) })}
                    className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                    min="1"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
                >
                  <option value="upcoming">Predstojeća</option>
                  <option value="group_stage">Grupna faza</option>
                  <option value="knockout">Nokaut faza</option>
                  <option value="finished">Završena</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Pravilnik</label>
                <textarea
                  value={editForm.rules}
                  onChange={(e) => setEditForm({ ...editForm, rules: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300 min-h-[200px]"
                  placeholder="Unesite pravilnik lige (svako pravilo u novi red, npr: 1. Mečevi se igraju...)"
                />
                <p className="text-xs text-neutral-500 mt-1">Prikazuje se igračima na javnom prikazu lige</p>
              </div>
              {user?.is_super_admin && availableAdmins.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">Dodijeli admine</label>
                  <div className="border border-neutral-200 rounded-xl p-3 max-h-[150px] overflow-y-auto space-y-2">
                    {availableAdmins.map(admin => (
                      <label key={admin.id} className="flex items-center gap-2 cursor-pointer hover:bg-neutral-50 p-1.5 rounded-lg">
                        <input
                          type="checkbox"
                          checked={editForm.assigned_admin_ids.includes(admin.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditForm({ ...editForm, assigned_admin_ids: [...editForm.assigned_admin_ids, admin.id] });
                            } else {
                              setEditForm({ ...editForm, assigned_admin_ids: editForm.assigned_admin_ids.filter(id => id !== admin.id) });
                            }
                          }}
                          className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-500"
                        />
                        <span className="text-sm text-neutral-700">{admin.username}</span>
                        <span className="text-xs text-neutral-400">{admin.email}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">Odabrani admini mogu unositi rezultate za ovu ligu</p>
                </div>
              )}
              <button
                type="submit"
                className="w-full bg-neutral-900 hover:bg-neutral-800 text-white py-2.5 rounded-xl text-sm font-medium transition-colors"
              >
                Sačuvaj izmjene
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-neutral-900 mb-2">Obriši ligu?</h2>
            <p className="text-sm text-neutral-500 mb-6">
              Da li ste sigurni da želite obrisati ligu "{league.name}"? Ova akcija se ne može poništiti.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-neutral-200 text-neutral-700 rounded-xl text-sm font-medium hover:bg-neutral-50 transition-colors"
              >
                Otkaži
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Obriši
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Score Entry Modal */}
      {showScoreModal && selectedMatch && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={() => { setShowScoreModal(false); setSelectedMatch(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
              <h2 className="font-semibold text-neutral-900 text-sm">Unesi rezultat</h2>
              <button onClick={() => { setShowScoreModal(false); setSelectedMatch(null); }} className="p-1 hover:bg-neutral-100 rounded-lg">
                <X className="w-4 h-4 text-neutral-400" />
              </button>
            </div>
            <div className="p-4">
              {/* Players Header */}
              <div className="flex items-center justify-between mb-4 text-xs">
                <div className="flex-1 text-center font-medium text-neutral-700 truncate px-2">
                  {selectedMatch.player1_name || selectedMatch.player1?.full_name || selectedMatch.player1_detail?.first_name + ' ' + selectedMatch.player1_detail?.last_name || 'Igrač 1'}
                </div>
                <div className="w-8"></div>
                <div className="flex-1 text-center font-medium text-neutral-700 truncate px-2">
                  {selectedMatch.player2_name || selectedMatch.player2?.full_name || selectedMatch.player2_detail?.first_name + ' ' + selectedMatch.player2_detail?.last_name || 'Igrač 2'}
                </div>
              </div>

              {/* Compact Sets Input */}
              <div className="space-y-2">
                {scoreSets.map((set, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-xs text-neutral-400 w-6">S{idx + 1}</span>
                    <div className="flex-1 flex items-center justify-center gap-1">
                      <button
                        onClick={() => {
                          const newSets = [...scoreSets];
                          if (newSets[idx].player1 > 0) newSets[idx].player1--;
                          setScoreSets(newSets);
                        }}
                        className="w-7 h-7 rounded bg-neutral-100 text-neutral-500 hover:bg-neutral-200 text-sm font-medium"
                      >-</button>
                      <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-200 flex items-center justify-center text-lg font-bold text-neutral-900">
                        {set.player1}
                      </div>
                      <button
                        onClick={() => {
                          const newSets = [...scoreSets];
                          newSets[idx].player1++;
                          setScoreSets(newSets);
                        }}
                        className="w-7 h-7 rounded bg-neutral-100 text-neutral-500 hover:bg-neutral-200 text-sm font-medium"
                      >+</button>
                    </div>
                    <span className="text-neutral-300 font-bold">:</span>
                    <div className="flex-1 flex items-center justify-center gap-1">
                      <button
                        onClick={() => {
                          const newSets = [...scoreSets];
                          if (newSets[idx].player2 > 0) newSets[idx].player2--;
                          setScoreSets(newSets);
                        }}
                        className="w-7 h-7 rounded bg-neutral-100 text-neutral-500 hover:bg-neutral-200 text-sm font-medium"
                      >-</button>
                      <div className="w-10 h-10 rounded-lg bg-neutral-50 border border-neutral-200 flex items-center justify-center text-lg font-bold text-neutral-900">
                        {set.player2}
                      </div>
                      <button
                        onClick={() => {
                          const newSets = [...scoreSets];
                          newSets[idx].player2++;
                          setScoreSets(newSets);
                        }}
                        className="w-7 h-7 rounded bg-neutral-100 text-neutral-500 hover:bg-neutral-200 text-sm font-medium"
                      >+</button>
                    </div>
                    {scoreSets.length > 1 && (
                      <button
                        onClick={() => removeSet(idx)}
                        className="w-6 h-6 rounded bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-500 text-xs"
                      >×</button>
                    )}
                  </div>
                ))}
                {scoreSets.length < 5 && (
                  <button
                    onClick={addSet}
                    className="w-full py-2 border border-dashed border-neutral-300 rounded-lg text-xs text-neutral-500 hover:border-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    + Dodaj set
                  </button>
                )}
              </div>

              {/* Quick Scores */}
              <div className="mt-4 pt-3 border-t border-neutral-100 space-y-3">
                <div>
                  <div className="text-xs text-neutral-400 mb-1.5">Brzi unos - igrač 1 dobija set:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {['6-0', '6-1', '6-2', '6-3', '6-4', '7-5', '7-6'].map(score => (
                      <button
                        key={`p1-${score}`}
                        onClick={() => {
                          const [p1, p2] = score.split('-').map(Number);
                          const newSets = [...scoreSets];
                          const emptySetIdx = newSets.findIndex(s => s.player1 === 0 && s.player2 === 0);
                          if (emptySetIdx !== -1) {
                            newSets[emptySetIdx] = { player1: p1, player2: p2 };
                          } else if (newSets.length < 5) {
                            newSets.push({ player1: p1, player2: p2 });
                          }
                          setScoreSets(newSets);
                        }}
                        className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-medium hover:bg-green-100 transition-colors"
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-neutral-400 mb-1.5">Brzi unos - igrač 2 dobija set:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {['0-6', '1-6', '2-6', '3-6', '4-6', '5-7', '6-7'].map(score => (
                      <button
                        key={`p2-${score}`}
                        onClick={() => {
                          const [p1, p2] = score.split('-').map(Number);
                          const newSets = [...scoreSets];
                          const emptySetIdx = newSets.findIndex(s => s.player1 === 0 && s.player2 === 0);
                          if (emptySetIdx !== -1) {
                            newSets[emptySetIdx] = { player1: p1, player2: p2 };
                          } else if (newSets.length < 5) {
                            newSets.push({ player1: p1, player2: p2 });
                          }
                          setScoreSets(newSets);
                        }}
                        className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium hover:bg-blue-100 transition-colors"
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Forfeit Section */}
              {selectedMatch?.status !== 'completed' && (
                <div className="mt-4 pt-3 border-t border-neutral-100">
                  <div className="text-xs text-neutral-400 mb-2">Predaja meča:</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => {
                        const playerId = selectedMatch.player1?.id || selectedMatch.player1_id;
                        const hasSets = scoreSets.some(s => s.player1 > 0 || s.player2 > 0);
                        handleForfeit(playerId, hasSets ? 'retirement' : 'walkover');
                      }}
                      className="px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-xl text-xs font-medium transition-colors"
                    >
                      {selectedMatch.player1_name || selectedMatch.player1?.full_name || 'Igrač 1'} predao
                    </button>
                    <button
                      onClick={() => {
                        const playerId = selectedMatch.player2?.id || selectedMatch.player2_id;
                        const hasSets = scoreSets.some(s => s.player1 > 0 || s.player2 > 0);
                        handleForfeit(playerId, hasSets ? 'retirement' : 'walkover');
                      }}
                      className="px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-xl text-xs font-medium transition-colors"
                    >
                      {selectedMatch.player2_name || selectedMatch.player2?.full_name || 'Igrač 2'} predao
                    </button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 mt-4 pt-3 border-t border-neutral-100">
                {selectedMatch?.status === 'completed' && (selectedMatch?.sets?.length > 0 || selectedMatch?.forfeit_type) && (
                  <button
                    onClick={handleResetScore}
                    className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
                  >
                    Obriši
                  </button>
                )}
                <button
                  onClick={() => { setShowScoreModal(false); setSelectedMatch(null); }}
                  className="flex-1 px-3 py-2 border border-neutral-200 text-neutral-600 rounded-lg text-sm hover:bg-neutral-50 transition-colors"
                >
                  Otkaži
                </button>
                <button
                  onClick={handleSaveScore}
                  className="flex-1 px-3 py-2 bg-neutral-900 text-white rounded-lg text-sm font-medium hover:bg-neutral-800 transition-colors"
                >
                  Sačuvaj
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== HEAD TO HEAD ====================
const HeadToHead = () => {
  const [players, setPlayers] = useState([]);
  const [player1, setPlayer1] = useState(null);
  const [player2, setPlayer2] = useState(null);
  const [h2hData, setH2hData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchPlayer1, setSearchPlayer1] = useState('');
  const [searchPlayer2, setSearchPlayer2] = useState('');
  const [showDropdown1, setShowDropdown1] = useState(false);
  const [showDropdown2, setShowDropdown2] = useState(false);

  useEffect(() => {
    playersAPI.getAllPublic().then(res => {
      setPlayers(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (player1 && player2) {
      setLoading(true);
      matchesAPI.getHeadToHead(player1.id, player2.id)
        .then(res => {
          setH2hData(res.data);
        })
        .catch(() => {
          setH2hData(null);
        })
        .finally(() => setLoading(false));
    } else {
      setH2hData(null);
    }
  }, [player1, player2]);

  const filteredPlayers1 = players.filter(p =>
    `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().includes(searchPlayer1.toLowerCase())
  );

  const filteredPlayers2 = players.filter(p =>
    `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().includes(searchPlayer2.toLowerCase()) &&
    p.id !== player1?.id
  );

  const selectPlayer1 = (p) => {
    setPlayer1(p);
    setSearchPlayer1('');
    setShowDropdown1(false);
  };

  const selectPlayer2 = (p) => {
    setPlayer2(p);
    setSearchPlayer2('');
    setShowDropdown2(false);
  };

  const removePlayer1 = () => {
    setPlayer1(null);
    setH2hData(null);
  };

  const removePlayer2 = () => {
    setPlayer2(null);
    setH2hData(null);
  };

  const formatMatchDate = (dateStr) => {
    if (!dateStr) return 'Datum nije poznat';
    const date = new Date(dateStr);
    return date.toLocaleDateString('sr-Latn-ME', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicHeader current="h2h" />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-16 bg-gradient-to-br from-rose-50 via-white to-neutral-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-1 md:mb-2">Head to Head</h1>
          <p className="text-base md:text-lg text-neutral-500">Uporedi direktne mečeve bilo koja dva igrača</p>
        </div>
      </section>

      {/* Player Selection */}
      <section className="py-6 md:py-8 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-stretch gap-4">
            {/* Player 1 Selection */}
            <div className="flex-1 relative">
              {player1 ? (
                <div className="h-full bg-white rounded-2xl border-2 border-neutral-200 p-6 flex flex-col items-center justify-center text-center relative group hover:border-rose-300 transition-colors">
                  <button
                    onClick={removePlayer1}
                    className="absolute top-3 right-3 text-neutral-400 hover:text-neutral-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  {player1.photo ? (
                    <img src={player1.photo} alt={player1.full_name} className="w-20 h-20 rounded-full object-cover mb-4 ring-4 ring-neutral-100" />
                  ) : (
                    <div className="w-20 h-20 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white text-xl font-bold mb-4 ring-4 ring-neutral-100">
                      {player1.first_name[0]}{player1.last_name[0]}
                    </div>
                  )}
                  <h3 className="font-bold text-xl text-neutral-900 mb-1">{player1.full_name}</h3>
                  <p className="text-sm text-neutral-500">{player1.country || 'Crna Gora'}</p>
                </div>
              ) : (
                <div className="h-full bg-white rounded-2xl border-2 border-dashed border-neutral-300 p-6 flex flex-col items-center justify-center">
                  <Users className="w-12 h-12 text-neutral-300 mb-4" />
                  <p className="text-sm font-medium text-neutral-500 mb-4">Izaberi prvog igrača</p>
                  <div className="w-full relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <input
                      type="text"
                      placeholder="Pretraži..."
                      value={searchPlayer1}
                      onChange={(e) => setSearchPlayer1(e.target.value)}
                      onFocus={() => setShowDropdown1(true)}
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    />
                  </div>
                  {showDropdown1 && (
                    <div className="absolute z-10 w-full mt-2 top-full bg-white border border-neutral-200 rounded-xl shadow-lg max-h-80 overflow-y-auto">
                      {filteredPlayers1.slice(0, 10).map(p => (
                        <button
                          key={p.id}
                          onClick={() => selectPlayer1(p)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-rose-50 transition-colors text-left border-b border-neutral-100 last:border-0"
                        >
                          {p.photo ? (
                            <img src={p.photo} alt={p.full_name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              {p.first_name[0]}{p.last_name[0]}
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-neutral-900 text-sm">{p.full_name}</div>
                            <div className="text-xs text-neutral-500">{p.country || 'Crna Gora'}</div>
                          </div>
                        </button>
                      ))}
                      {filteredPlayers1.length === 0 && (
                        <div className="p-6 text-center text-neutral-400 text-sm">Nema rezultata</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* VS Divider */}
            <div className="flex items-center justify-center md:px-4">
              <div className="bg-gradient-to-br from-rose-500 to-rose-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-xl font-black shadow-lg">
                VS
              </div>
            </div>

            {/* Player 2 Selection */}
            <div className="flex-1 relative">
              {player2 ? (
                <div className="h-full bg-white rounded-2xl border-2 border-neutral-200 p-6 flex flex-col items-center justify-center text-center relative group hover:border-rose-300 transition-colors">
                  <button
                    onClick={removePlayer2}
                    className="absolute top-3 right-3 text-neutral-400 hover:text-neutral-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  {player2.photo ? (
                    <img src={player2.photo} alt={player2.full_name} className="w-20 h-20 rounded-full object-cover mb-4 ring-4 ring-neutral-100" />
                  ) : (
                    <div className="w-20 h-20 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white text-xl font-bold mb-4 ring-4 ring-neutral-100">
                      {player2.first_name[0]}{player2.last_name[0]}
                    </div>
                  )}
                  <h3 className="font-bold text-xl text-neutral-900 mb-1">{player2.full_name}</h3>
                  <p className="text-sm text-neutral-500">{player2.country || 'Crna Gora'}</p>
                </div>
              ) : (
                <div className="h-full bg-white rounded-2xl border-2 border-dashed border-neutral-300 p-6 flex flex-col items-center justify-center">
                  <Users className="w-12 h-12 text-neutral-300 mb-4" />
                  <p className="text-sm font-medium text-neutral-500 mb-4">Izaberi drugog igrača</p>
                  <div className="w-full relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <input
                      type="text"
                      placeholder="Pretraži..."
                      value={searchPlayer2}
                      onChange={(e) => setSearchPlayer2(e.target.value)}
                      onFocus={() => setShowDropdown2(true)}
                      disabled={!player1}
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent disabled:bg-neutral-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  {showDropdown2 && player1 && (
                    <div className="absolute z-10 w-full mt-2 top-full bg-white border border-neutral-200 rounded-xl shadow-lg max-h-80 overflow-y-auto">
                      {filteredPlayers2.slice(0, 10).map(p => (
                        <button
                          key={p.id}
                          onClick={() => selectPlayer2(p)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-rose-50 transition-colors text-left border-b border-neutral-100 last:border-0"
                        >
                          {p.photo ? (
                            <img src={p.photo} alt={p.full_name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              {p.first_name[0]}{p.last_name[0]}
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-neutral-900 text-sm">{p.full_name}</div>
                            <div className="text-xs text-neutral-500">{p.country || 'Crna Gora'}</div>
                          </div>
                        </button>
                      ))}
                      {filteredPlayers2.length === 0 && (
                        <div className="p-6 text-center text-neutral-400 text-sm">Nema rezultata</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* H2H Results */}
      {loading && (
        <section className="py-6 md:py-8 px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <RefreshCw className="w-8 h-8 mx-auto mb-3 text-neutral-400 animate-spin" />
            <p className="text-neutral-500">Učitavanje...</p>
          </div>
        </section>
      )}

      {!loading && h2hData && (
        <>
          {/* H2H Stats */}
          <section className="py-6 md:py-12 px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="bg-gradient-to-br from-neutral-900 via-neutral-800 to-rose-900 rounded-3xl p-8 md:p-12 shadow-2xl">
                <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                  {/* Player 1 Stats */}
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-4 mb-4">
                      {player1.photo ? (
                        <img src={player1.photo} alt={player1.full_name} className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover ring-4 ring-white/20" />
                      ) : (
                        <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 rounded-full flex items-center justify-center text-white text-lg font-bold ring-4 ring-white/20">
                          {player1.first_name[0]}{player1.last_name[0]}
                        </div>
                      )}
                      <div>
                        <h3 className="text-white font-bold text-lg md:text-xl">{player1.full_name}</h3>
                        <p className="text-white/60 text-sm">{player1.country || 'Crna Gora'}</p>
                      </div>
                    </div>
                    <div className="inline-flex flex-col items-center bg-white/10 backdrop-blur-sm rounded-2xl px-8 py-4 border border-white/20">
                      <div className="text-5xl md:text-6xl font-black text-white mb-1">{h2hData.player1_wins}</div>
                      <div className="text-sm font-medium text-white/80 uppercase tracking-wider">Pobjeda</div>
                    </div>
                  </div>

                  {/* VS Badge */}
                  <div className="flex flex-col items-center gap-3">
                    <div className="bg-white text-neutral-900 w-20 h-20 rounded-full flex items-center justify-center text-2xl font-black shadow-xl">
                      VS
                    </div>
                    <div className="text-white/60 text-sm font-medium">
                      {h2hData.total_matches} {h2hData.total_matches === 1 ? 'meč' : h2hData.total_matches < 5 ? 'meča' : 'mečeva'}
                    </div>
                  </div>

                  {/* Player 2 Stats */}
                  <div className="flex-1 text-center md:text-right">
                    <div className="flex items-center justify-center md:justify-end gap-4 mb-4">
                      <div className="md:order-2">
                        {player2.photo ? (
                          <img src={player2.photo} alt={player2.full_name} className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover ring-4 ring-white/20" />
                        ) : (
                          <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 rounded-full flex items-center justify-center text-white text-lg font-bold ring-4 ring-white/20">
                            {player2.first_name[0]}{player2.last_name[0]}
                          </div>
                        )}
                      </div>
                      <div className="md:order-1">
                        <h3 className="text-white font-bold text-lg md:text-xl">{player2.full_name}</h3>
                        <p className="text-white/60 text-sm">{player2.country || 'Crna Gora'}</p>
                      </div>
                    </div>
                    <div className="inline-flex flex-col items-center bg-white/10 backdrop-blur-sm rounded-2xl px-8 py-4 border border-white/20">
                      <div className="text-5xl md:text-6xl font-black text-white mb-1">{h2hData.player2_wins}</div>
                      <div className="text-sm font-medium text-white/80 uppercase tracking-wider">Pobjeda</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Match History */}
          {h2hData.matches && h2hData.matches.length > 0 && (
            <section className="py-6 md:py-8 px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-neutral-900">Svi mečevi</h2>
                  <span className="text-sm text-neutral-500">{h2hData.matches.length} {h2hData.matches.length === 1 ? 'meč' : h2hData.matches.length < 5 ? 'meča' : 'mečeva'}</span>
                </div>
                <div className="space-y-2">
                  {h2hData.matches.map((match) => {
                    const isPlayer1Winner = match.winner?.id === player1.id;
                    const isPlayer2Winner = match.winner?.id === player2.id;
                    const p1IsFirst = match.player1?.id === player1.id;

                    return (
                      <div key={match.id} className="bg-white rounded-xl border border-neutral-200 hover:border-rose-200 hover:shadow-sm transition-all">
                        <div className="p-4 md:p-5">
                          {/* Tournament Info */}
                          <div className="flex items-center justify-between mb-3 pb-3 border-b border-neutral-100">
                            <div className="flex items-center gap-2">
                              <Trophy className="w-4 h-4 text-rose-500 flex-shrink-0" />
                              <span className="text-xs md:text-sm font-medium text-neutral-700 truncate">
                                {match.tournament_name || match.league_name || 'Turnir'}
                              </span>
                              {match.round && (
                                <>
                                  <span className="hidden md:inline text-neutral-300">•</span>
                                  <span className="hidden md:inline text-xs text-neutral-500">
                                    {match.round === 'final' ? 'Finale' :
                                     match.round === 'semifinal' ? 'Polufinale' :
                                     match.round === 'quarterfinal' ? 'Četvrtfinale' :
                                     match.round}
                                  </span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-neutral-500">
                              <Calendar className="w-3.5 h-3.5" />
                              <span className="hidden md:inline">{formatMatchDate(match.match_date)}</span>
                              <span className="md:hidden">{match.match_date ? new Date(match.match_date).toLocaleDateString('sr-Latn-ME', { day: 'numeric', month: 'short' }) : '-'}</span>
                            </div>
                          </div>

                          {/* Score */}
                          <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
                            <div className="space-y-2.5">
                              <div className="flex items-center gap-3">
                                <div className="w-5 flex justify-center">
                                  {isPlayer1Winner ? (
                                    <Trophy className="w-4 h-4 text-rose-500" />
                                  ) : null}
                                </div>
                                <span className={`flex-1 text-sm md:text-base ${isPlayer1Winner ? 'font-bold text-neutral-900' : 'text-neutral-600'}`}>
                                  {player1.full_name}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="w-5 flex justify-center">
                                  {isPlayer2Winner ? (
                                    <Trophy className="w-4 h-4 text-rose-500" />
                                  ) : null}
                                </div>
                                <span className={`flex-1 text-sm md:text-base ${isPlayer2Winner ? 'font-bold text-neutral-900' : 'text-neutral-600'}`}>
                                  {player2.full_name}
                                </span>
                              </div>
                            </div>

                            <div className="flex flex-col gap-2.5 text-right">
                              <div className="flex gap-2 font-mono text-sm md:text-base font-semibold">
                                {match.sets?.map((s, i) => (
                                  <span key={i} className={`w-8 h-8 flex items-center justify-center rounded ${isPlayer1Winner ? 'bg-rose-50 text-rose-700' : 'bg-neutral-100 text-neutral-600'}`}>
                                    {p1IsFirst ? s.player1_games : s.player2_games}
                                  </span>
                                ))}
                              </div>
                              <div className="flex gap-2 font-mono text-sm md:text-base font-semibold">
                                {match.sets?.map((s, i) => (
                                  <span key={i} className={`w-8 h-8 flex items-center justify-center rounded ${isPlayer2Winner ? 'bg-rose-50 text-rose-700' : 'bg-neutral-100 text-neutral-600'}`}>
                                    {p1IsFirst ? s.player2_games : s.player1_games}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {h2hData.total_matches === 0 && (
            <section className="py-12 px-4 md:px-6">
              <div className="max-w-4xl mx-auto text-center">
                <Trophy className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                <p className="text-lg text-neutral-500">Ovi igrači se još nisu susreli</p>
              </div>
            </section>
          )}
        </>
      )}

      {!loading && !h2hData && player1 && player2 && (
        <section className="py-12 px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-rose-300" />
            <p className="text-lg text-neutral-500">Greška pri učitavanju podataka</p>
          </div>
        </section>
      )}

      {!player1 && !player2 && (
        <section className="py-12 px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Users className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
            <p className="text-lg text-neutral-500">Izaberi dva igrača da vidiš njihove direktne mečeve</p>
          </div>
        </section>
      )}

      <PublicFooter />
    </div>
  );
};

// ==================== PUBLIC RANKINGS ====================
const PublicRankings = () => {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadRankings = useCallback(() => {
    return rankingsAPI.getAll()
      .then(res => setRankings(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    loadRankings().finally(() => setLoading(false));
  }, [loadRankings]);

  const filteredRankings = rankings.filter(r => r.current_rank && r.total_points > 0);

  return (
    <>
      <SEO
        title="Rang Lista | TenisCG.me"
        description="Rang lista rekreativnih tenisera Crne Gore. ATP stil bodovanja, sistem 365 dana. Provjeri svoje mjesto na rang listi."
        url="/rankings"
      />
      <PullToRefresh onRefresh={loadRankings}>
        <div className="min-h-screen bg-white">
          <PublicHeader current="rankings" />

          <section className="pt-24 md:pt-32 pb-8 md:pb-16 bg-gradient-to-br from-rose-50 via-white to-neutral-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-1 md:mb-2">Rang Lista</h1>
              <p className="text-base md:text-lg text-neutral-500">ATP stil bodovanja • Sistem 365 dana</p>
            </div>
          </section>

      <section className="py-8 md:py-12 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          {loading ? (
            <SkeletonTable rows={8} />
          ) : filteredRankings.length === 0 ? (
            <EmptyState
              icon={BarChart3}
              title="Nema rangiranih igrača"
              description="Rang lista će biti dostupna nakon što se odigraju turniri."
            />
          ) : (
          <div className="border border-neutral-200 rounded-2xl overflow-hidden bg-white">
            {/* Header - hide on mobile */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-neutral-50 border-b border-neutral-200 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
              <div className="col-span-1">Rang</div>
              <div className="col-span-7">Igrač</div>
              <div className="col-span-4 text-right">Bodovi</div>
            </div>
            {filteredRankings.map((r, idx) => (
              <div key={r.id || idx}
                onClick={() => r.player?.id && navigate(`/players/${r.player.id}`)}
                className={`stagger-item flex items-center gap-3 md:gap-4 px-4 md:px-6 py-4 md:py-5 hover:bg-rose-50/50 transition-colors cursor-pointer ${idx !== filteredRankings.length - 1 ? 'border-b border-neutral-100' : ''}`}
                style={{ animationDelay: `${idx * 0.03}s` }}>
                <div className="flex-shrink-0">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${
                    idx === 0 ? 'bg-gradient-to-br from-amber-400 to-rose-400 text-white' :
                    idx === 1 ? 'bg-neutral-300 text-neutral-700' :
                    idx === 2 ? 'bg-amber-600 text-white' :
                    'bg-neutral-100 text-neutral-600'
                  }`}>{r.current_rank}</span>
                </div>
                {r.player?.photo ? (
                  <img src={r.player.photo} alt={r.player.full_name} className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                    {(r.player?.first_name || '?')[0]}{(r.player?.last_name || '?')[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-900 text-sm md:text-base truncate">{r.player?.full_name || 'Nepoznat'}</div>
                  <div className="text-xs md:text-sm text-neutral-500">{r.player?.country || 'Crna Gora'}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-lg md:text-xl font-bold text-neutral-900">{r.total_points}</span>
                  <span className="text-xs text-neutral-400 ml-1 md:hidden">bod.</span>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </section>

          <PublicFooter />
        </div>
      </PullToRefresh>
    </>
  );
};

// ==================== PUBLIC POWER INDEX ====================
const PublicPowerIndex = () => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadRatings = useCallback(() => {
    return powerIndexAPI.getAll()
      .then(res => setRatings(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    loadRatings().finally(() => setLoading(false));
  }, [loadRatings]);

  // Color for a Power Index value: high = strong (rose), mid = neutral, low = muted
  const piColor = (pi) => {
    if (pi >= 7.5) return 'text-rose-500';
    if (pi >= 5) return 'text-neutral-900';
    return 'text-neutral-500';
  };

  return (
    <>
      <SEO
        title="Power Index | TenisCG.me"
        description="Power Index - rejting stvarne jačine rekreativnih tenisera Crne Gore na skali 1.0-10.0, izračunat iz svih odigranih mečeva."
        url="/power-index"
      />
      <PullToRefresh onRefresh={loadRatings}>
        <div className="min-h-screen bg-white">
          <PublicHeader current="power-index" />

          <section className="pt-24 md:pt-32 pb-8 md:pb-16 bg-gradient-to-br from-rose-50 via-white to-neutral-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-1 md:mb-2">Power Index</h1>
              <p className="text-base md:text-lg text-neutral-500">Rejting stvarne jačine igrača • Skala 1.0–10.0</p>
            </div>
          </section>

          <section className="py-8 md:py-12 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              {/* Explainer */}
              <div className="mb-6 bg-neutral-50 border border-neutral-200 rounded-2xl p-4 md:p-5">
                <p className="text-sm text-neutral-600 leading-relaxed">
                  <span className="font-semibold text-neutral-900">Power Index (PI)</span> mjeri koliko je igrač
                  stvarno dobar — izračunat iz svih odigranih mečeva, uzimajući u obzir jačinu protivnika i
                  ubjedljivost rezultata. Za razliku od rang liste, ne zavisi od broja odigranih turnira.
                  <span className="text-neutral-400"> Zvjezdica <span className="text-amber-500 font-bold">*</span> označava provizoran rejting — igrač još nije odigrao dovoljno mečeva da bi rejting bio potpuno pouzdan.</span>
                </p>
              </div>

              {loading ? (
                <SkeletonTable rows={8} />
              ) : ratings.length === 0 ? (
                <EmptyState
                  icon={BarChart3}
                  title="Nema rangiranih igrača"
                  description="Power Index će biti dostupan nakon što se odigraju mečevi."
                />
              ) : (
                <div className="border border-neutral-200 rounded-2xl overflow-hidden bg-white">
                  {/* Header - hide on mobile */}
                  <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-neutral-50 border-b border-neutral-200 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    <div className="col-span-1">#</div>
                    <div className="col-span-8">Igrač</div>
                    <div className="col-span-3 text-right">Power Index</div>
                  </div>
                  {ratings.map((r, idx) => (
                    <div key={r.id || idx}
                      onClick={() => r.player?.id && navigate(`/players/${r.player.id}`)}
                      className={`stagger-item flex items-center gap-3 md:gap-4 px-4 md:px-6 py-4 md:py-5 hover:bg-rose-50/50 transition-colors cursor-pointer ${idx !== ratings.length - 1 ? 'border-b border-neutral-100' : ''}`}
                      style={{ animationDelay: `${idx * 0.03}s` }}>
                      <div className="flex-shrink-0">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${
                          idx === 0 ? 'bg-gradient-to-br from-amber-400 to-rose-400 text-white' :
                          idx === 1 ? 'bg-neutral-300 text-neutral-700' :
                          idx === 2 ? 'bg-amber-600 text-white' :
                          'bg-neutral-100 text-neutral-600'
                        }`}>{r.index_rank}</span>
                      </div>
                      {r.player?.photo ? (
                        <img src={r.player.photo} alt={r.player.full_name} className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                          {(r.player?.first_name || '?')[0]}{(r.player?.last_name || '?')[0]}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-neutral-900 text-sm md:text-base truncate flex items-center gap-2">
                          {r.player?.full_name || 'Nepoznat'}
                          {r.is_provisional && (
                            <span className="text-amber-500 font-bold flex-shrink-0" title="Provizoran rejting">*</span>
                          )}
                        </div>
                        <div className="text-xs md:text-sm text-neutral-500">{r.player?.country || 'Crna Gora'}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-lg md:text-xl font-bold ${piColor(r.power_index)}`}>
                          {Number(r.power_index).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <PublicFooter />
        </div>
      </PullToRefresh>
    </>
  );
};

// ==================== PUBLIC TOURNAMENTS ====================
const PublicTournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    tournamentsAPI.getAllPublic()
      .then((res) => {
        // Filter out league knockout tournaments from regular tournaments
        const tData = Array.isArray(res.data) ? res.data.filter(t => !t.is_league_knockout) : [];
        setTournaments(tData);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const pastTournaments = tournaments.filter(t => t.status === 'finished' || t.status === 'archived');
  const upcomingTournaments = tournaments.filter(t => t.status === 'upcoming');
  const ongoingTournament = tournaments.find(t => t.status === 'ongoing');

  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicHeader current="tournaments" />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-16 bg-gradient-to-br from-rose-50 via-white to-neutral-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-1 md:mb-2">Turniri</h1>
          <p className="text-base md:text-lg text-neutral-500">Svi turniri rekreativnog tenisa u Crnoj Gori</p>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-4xl mx-auto space-y-6">
            <Skeleton className="h-4 w-32 mb-4" />
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-4 md:p-6 border border-neutral-200">
                <div className="flex items-center gap-4">
                  <Skeleton className="w-10 h-10 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-2/3" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                  <Skeleton className="h-8 w-16" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Live Tournament */}
      {!loading && ongoingTournament && (
        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div
              onClick={() => navigate(`/tournaments/${ongoingTournament.id}`)}
              className="bg-neutral-900 rounded-2xl p-5 md:p-8 text-white cursor-pointer hover:bg-neutral-800 transition-colors"
            >
              <div className="flex items-center gap-3 mb-3 md:mb-4">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <span className="text-xs md:text-sm font-medium text-neutral-400 uppercase tracking-wider">Turnir u toku</span>
              </div>
              <h2 className="text-xl md:text-2xl font-bold mb-2">{ongoingTournament.name}</h2>
              <div className="flex flex-wrap items-center gap-3 md:gap-4 text-neutral-400 text-xs md:text-sm">
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" /> {ongoingTournament.location}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" /> {ongoingTournament.start_date}
                </span>
              </div>
              <div className="mt-4 md:mt-6 flex items-center gap-2 text-sm text-neutral-300">
                Pogledaj žrijeb <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Tournaments */}
      {!loading && upcomingTournaments.length > 0 && (
        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xs md:text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4 md:mb-6">Predstojeći turniri</h2>
            <div className="grid gap-3 md:gap-4">
              {upcomingTournaments.map((t) => (
                <div
                  key={t.id}
                  onClick={() => navigate(`/tournaments/${t.id}`)}
                  className="flex items-center justify-between p-4 md:p-6 bg-neutral-50 rounded-xl hover:bg-rose-50/70 transition-colors cursor-pointer border border-transparent hover:border-rose-100"
                >
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base md:text-lg font-semibold text-neutral-900 truncate">{t.name}</h3>
                    <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-1 text-xs md:text-sm text-neutral-500">
                      <span className="flex items-center gap-1.5">
                        <MapPin className="w-3 h-3 md:w-4 md:h-4" /> {t.location}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3 md:w-4 md:h-4" /> {t.start_date}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 md:gap-4 flex-shrink-0 ml-2">
                    <div className="text-right">
                      <div className="text-base md:text-lg font-bold text-neutral-900">{t.total_points || 0}</div>
                      <div className="text-xs text-neutral-500">bodova</div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-neutral-400 hidden md:block" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Past Tournaments */}
      {!loading && <section className="py-8 md:py-12 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xs md:text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4 md:mb-6">Završeni turniri</h2>
          {pastTournaments.length > 0 ? (
            <div className="border border-neutral-200 rounded-xl overflow-hidden">
              {pastTournaments.map((t, idx) => (
                <div
                  key={t.id}
                  onClick={() => navigate(`/tournaments/${t.id}`)}
                  className={`flex items-center justify-between p-4 md:p-5 hover:bg-rose-50/50 transition-colors cursor-pointer ${idx !== pastTournaments.length - 1 ? 'border-b border-neutral-100' : ''}`}
                >
                  <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Trophy className="w-4 h-4 md:w-5 md:h-5 text-rose-400" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-neutral-900 text-sm md:text-base truncate">{t.name}</h3>
                      <div className="flex items-center gap-2 md:gap-3 mt-0.5 text-xs md:text-sm text-neutral-500">
                        <span className="truncate">{t.location}</span>
                        <span className="hidden md:inline">•</span>
                        <span className="hidden md:inline">{t.start_date}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 md:gap-6 flex-shrink-0 ml-2">
                    {t.winner_name && (
                      <div className="text-right hidden sm:block">
                        <div className="text-xs text-neutral-400">Pobjednik</div>
                        <div className="font-medium text-neutral-900 text-sm md:text-base">{t.winner_name}</div>
                      </div>
                    )}
                    <ArrowRight className="w-5 h-5 text-neutral-300" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Trophy}
              title="Nema završenih turnira"
              description="Završeni turniri će biti prikazani ovdje"
            />
          )}
        </div>
      </section>}

      <PublicFooter />
    </div>
  );
};

// ==================== PUBLIC LEAGUES ====================
const PublicLeagues = () => {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    leaguesAPI.getAllPublic()
      .then((res) => {
        setLeagues(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeLeagues = leagues.filter(l => l.status === 'group_stage' || l.status === 'knockout');
  const upcomingLeagues = leagues.filter(l => l.status === 'upcoming');
  const pastLeagues = leagues.filter(l => l.status === 'finished' || l.status === 'archived');

  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicHeader current="leagues" />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-16 bg-gradient-to-br from-purple-50 via-white to-neutral-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-1 md:mb-2">Lige</h1>
          <p className="text-base md:text-lg text-neutral-500">Sve lige rekreativnog tenisa u Crnoj Gori</p>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-4xl mx-auto space-y-6">
            <Skeleton className="h-4 w-32 mb-4" />
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-4 md:p-6 border border-neutral-200">
                <div className="flex items-center gap-4">
                  <Skeleton className="w-10 h-10 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-2/3" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                  <Skeleton className="h-8 w-16" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Active Leagues */}
      {!loading && activeLeagues.length > 0 && (
        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xs md:text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4 md:mb-6">Lige u toku</h2>
            <div className="grid gap-3 md:gap-4">
              {activeLeagues.map((l) => (
                <div
                  key={l.id}
                  onClick={() => navigate(`/leagues/${l.id}`)}
                  className="flex items-center justify-between p-4 md:p-6 bg-purple-50/70 rounded-xl hover:bg-purple-100/70 transition-colors cursor-pointer border border-purple-100"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-purple-500"></span>
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-md ${l.status === 'knockout' ? 'bg-purple-200 text-purple-700' : 'bg-amber-200 text-amber-700'}`}>
                        {l.status === 'knockout' ? 'Nokaut faza' : 'Grupna faza'}
                      </span>
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-neutral-900 truncate">{l.name}</h3>
                    <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-1 text-xs md:text-sm text-neutral-500">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3 md:w-4 md:h-4" /> {l.start_date} – {l.end_date}
                      </span>
                      <span>{l.player_count || 0} igrača</span>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-purple-400 flex-shrink-0 ml-2" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Leagues */}
      {!loading && upcomingLeagues.length > 0 && (
        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xs md:text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4 md:mb-6">Predstojeće lige</h2>
            <div className="grid gap-3 md:gap-4">
              {upcomingLeagues.map((l) => (
                <div
                  key={l.id}
                  onClick={() => navigate(`/leagues/${l.id}`)}
                  className="flex items-center justify-between p-4 md:p-6 bg-neutral-50 rounded-xl hover:bg-purple-50/70 transition-colors cursor-pointer border border-transparent hover:border-purple-100"
                >
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base md:text-lg font-semibold text-neutral-900 truncate">{l.name}</h3>
                    <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-1 text-xs md:text-sm text-neutral-500">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3 md:w-4 md:h-4" /> {l.start_date} – {l.end_date}
                      </span>
                      <span>{l.player_count || 0} igrača</span>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-neutral-400 flex-shrink-0 ml-2" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Past Leagues */}
      {!loading && <section className="py-8 md:py-12 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xs md:text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4 md:mb-6">Završene lige</h2>
          {pastLeagues.length > 0 ? (
            <div className="border border-purple-200 rounded-xl overflow-hidden">
              {pastLeagues.map((l, idx) => (
                <div
                  key={l.id}
                  onClick={() => navigate(`/leagues/${l.id}`)}
                  className={`flex items-center justify-between p-4 md:p-5 hover:bg-purple-50/50 transition-colors cursor-pointer ${idx !== pastLeagues.length - 1 ? 'border-b border-purple-100' : ''}`}
                >
                  <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Medal className="w-4 h-4 md:w-5 md:h-5 text-purple-500" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-neutral-900 text-sm md:text-base truncate">{l.name}</h3>
                      <div className="flex flex-wrap items-center gap-2 md:gap-3 mt-0.5 text-xs md:text-sm text-neutral-500">
                        <span>{l.start_date} – {l.end_date}</span>
                        <span className="hidden md:inline">•</span>
                        <span className="hidden md:inline">{l.player_count || 0} igrača</span>
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-purple-300 flex-shrink-0" />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Medal}
              title="Nema završenih liga"
              description="Završene lige će biti prikazane ovdje"
            />
          )}
        </div>
      </section>}

      <PublicFooter />
    </div>
  );
};

// ==================== PUBLIC LEAGUE DETAIL ====================
const PublicLeagueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [matches, setMatches] = useState([]);
  const [knockoutMatches, setKnockoutMatches] = useState([]);
  const [viewMode, setViewMode] = useState('groups');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterRound, setFilterRound] = useState('1');
  const [showAllRounds, setShowAllRounds] = useState(false);
  const [roundsInfo, setRoundsInfo] = useState({ rounds: [], groups: [] });
  const [showRules, setShowRules] = useState(false);
  // Default 70% on mobile, 100% on desktop
  const [bracketZoom, setBracketZoom] = useState(0.9);

  // Load league data and rounds info on mount
  useEffect(() => {
    const loadLeague = async () => {
      try {
        const [lRes, riRes] = await Promise.all([
          leaguesAPI.getPublic(id),
          leaguesAPI.getRoundsInfo(id),
        ]);
        setLeague(lRes.data);
        setRoundsInfo(riRes.data || { rounds: [], groups: [] });

        // Load knockout matches if exists
        if (lRes.data.knockout_tournament?.id) {
          try {
            const bracketRes = await tournamentsAPI.getBracket(lRes.data.knockout_tournament.id);
            setKnockoutMatches(bracketRes.data?.matches || []);
          } catch {
            setKnockoutMatches([]);
          }
        }
      } catch {
        navigate('/leagues');
      }
    };
    loadLeague();
  }, [id, navigate]);

  // Load matches when filters change
  useEffect(() => {
    const loadMatches = async () => {
      const filters = {};
      if (filterGroup) filters.group = filterGroup;
      if (showAllRounds) {
        filters.all = true;
      } else {
        filters.round = filterRound;
      }
      const mRes = await leaguesAPI.getMatches(id, filters);
      setMatches(Array.isArray(mRes.data) ? mRes.data : []);
    };
    if (league) loadMatches();
  }, [id, league, filterGroup, filterRound, showAllRounds]);

  if (!league) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <RefreshCw className="w-6 h-6 text-neutral-400 animate-spin" />
    </div>
  );

  // Group matches by round
  const matchesByRound = matches.reduce((acc, match) => {
    const round = match.round_number || 0;
    if (!acc[round]) acc[round] = [];
    acc[round].push(match);
    return acc;
  }, {});

  const statusLabels = {
    upcoming: 'Predstojeća',
    group_stage: 'Grupna faza',
    knockout: 'Nokaut faza',
    finished: 'Završena',
  };

  const roundLabels = {
    'round64': '1. kolo',
    'round32': 'Šesnaestina finala',
    'round16': 'Osmina finala',
    'quarterfinal': 'Četvrtfinale',
    'semifinal': 'Polufinale',
    'final': 'Finale'
  };
  const roundOrder = ['round64', 'round32', 'round16', 'quarterfinal', 'semifinal', 'final'];

  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicHeader current="tournaments" />

      {/* Hero */}
      <section className="pt-20 md:pt-24 pb-8 md:pb-12 bg-gradient-to-br from-purple-50 via-white to-neutral-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <button onClick={() => navigate('/leagues')} className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 text-sm mb-4 md:mb-6">
            <ArrowLeft className="w-4 h-4" /> Nazad na lige
          </button>
          <div className="flex items-center gap-3 mb-2">
            <span className={`px-3 py-1 rounded-lg text-sm font-medium ${league.status === 'knockout' ? 'bg-purple-100 text-purple-700' : league.status === 'finished' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {statusLabels[league.status] || league.status}
            </span>
            <span className="text-sm text-neutral-500">{league.points_per_win} bod/pobjedi</span>
          </div>
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">{league.name}</h1>
          <div className="flex items-center gap-4 text-neutral-500">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" /> {league.start_date} – {league.end_date}
            </span>
            <span>{league.player_count || 0} igrača</span>
          </div>
        </div>
      </section>

      {/* View Switcher */}
      <section className="py-6 px-6 border-b border-neutral-200 bg-white">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setViewMode('groups')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'groups' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
            >
              Grupna faza
            </button>
            <button
              onClick={() => setViewMode('knockout')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'knockout' ? 'bg-purple-600 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
            >
              Nokaut faza
            </button>
          </div>
          {league.rules && (
            <button
              onClick={() => setShowRules(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors border border-amber-200"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Pravilnik</span>
            </button>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          {viewMode === 'groups' ? (
            <div className="space-y-8">
              {/* Standings */}
              {league.standings && Object.keys(league.standings).length > 0 && (
                <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-neutral-100">
                    <h2 className="font-semibold text-neutral-900">Tabela</h2>
                  </div>
                  <div className="p-6">
                    {Object.entries(league.standings).map(([groupName, groupStandings]) => (
                      <div key={groupName} className="mb-6 last:mb-0">
                        {Object.keys(league.standings).length > 1 && (
                          <h3 className="font-medium text-neutral-700 mb-3">{groupName}</h3>
                        )}
                        <div className="-mx-3 sm:mx-0">
                          <table className="w-full text-xs sm:text-sm">
                            <thead>
                              <tr className="text-xs text-neutral-500 uppercase border-b border-neutral-100">
                                <th className="text-left py-1.5 sm:py-2 px-1.5 sm:px-2 w-6 sm:w-8">#</th>
                                <th className="text-left py-1.5 sm:py-2 px-1.5 sm:px-2">Igrač</th>
                                <th className="text-center py-1.5 sm:py-2 px-1 sm:px-2 w-7 sm:w-10">M</th>
                                <th className="text-center py-1.5 sm:py-2 px-1 sm:px-2 w-7 sm:w-10">P</th>
                                <th className="text-center py-1.5 sm:py-2 px-1 sm:px-2 w-7 sm:w-10">I</th>
                                <th className="text-center py-1.5 sm:py-2 px-1 sm:px-2 w-12 sm:w-16">Set</th>
                                <th className="text-right py-1.5 sm:py-2 px-1.5 sm:px-2 w-10 sm:w-14">Bod</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Array.isArray(groupStandings) && groupStandings.map((s, idx) => (
                                <tr key={s.player?.id || idx} className={`border-b border-neutral-50 ${idx < league.players_advancing_per_group ? 'bg-green-50/50' : ''}`}>
                                  <td className={`py-2 sm:py-2.5 px-1.5 sm:px-2 font-medium ${idx < league.players_advancing_per_group ? 'text-green-600' : 'text-neutral-500'}`}>
                                    {s.rank || idx + 1}
                                  </td>
                                  <td className="py-2 sm:py-2.5 px-1.5 sm:px-2 text-neutral-900">
                                    <span className="sm:hidden">{s.player?.first_name?.[0]}. {s.player?.last_name}</span>
                                    <span className="hidden sm:inline">{s.player?.first_name} {s.player?.last_name}</span>
                                  </td>
                                  <td className="py-2 sm:py-2.5 px-1 sm:px-2 text-center text-neutral-600">{s.matches_played}</td>
                                  <td className="py-2 sm:py-2.5 px-1 sm:px-2 text-center text-green-600 font-medium">{s.wins}</td>
                                  <td className="py-2 sm:py-2.5 px-1 sm:px-2 text-center text-red-500">{s.losses}</td>
                                  <td className="py-2 sm:py-2.5 px-1 sm:px-2 text-center text-neutral-600">
                                    {s.sets_won}-{s.sets_lost}
                                  </td>
                                  <td className="py-2 sm:py-2.5 px-1.5 sm:px-2 text-right font-semibold text-neutral-900">{s.total_points}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Group Stage Matches */}
              {(roundsInfo.rounds?.length > 0 || Object.keys(matchesByRound).length > 0) && (
                <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-neutral-100">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <h2 className="font-semibold text-neutral-900">Rezultati po kolima</h2>
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Round filter buttons */}
                        {roundsInfo.rounds?.map(r => (
                          <button
                            key={r.round}
                            onClick={() => { setFilterRound(String(r.round)); setShowAllRounds(false); }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              !showAllRounds && filterRound === String(r.round)
                                ? 'bg-emerald-600 text-white'
                                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                            }`}
                          >
                            Kolo {r.round}
                          </button>
                        ))}
                        <button
                          onClick={() => setShowAllRounds(true)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            showAllRounds
                              ? 'bg-emerald-600 text-white'
                              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                          }`}
                        >
                          Sva kola
                        </button>
                        {/* Group filter */}
                        {roundsInfo.groups?.length > 1 && (
                          <>
                            <span className="text-neutral-300">|</span>
                            <button
                              onClick={() => setFilterGroup('')}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                !filterGroup
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                              }`}
                            >
                              Sve grupe
                            </button>
                            {roundsInfo.groups.map(g => (
                              <button
                                key={g.id}
                                onClick={() => setFilterGroup(g.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                  filterGroup === g.id
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                                }`}
                              >
                                {g.name}
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="p-6 space-y-6">
                    {Object.entries(matchesByRound).sort(([a], [b]) => Number(a) - Number(b)).map(([roundNum, roundMatches]) => {
                      // Group matches by league_group_name within each round
                      const matchesByGroup = roundMatches.reduce((acc, m) => {
                        const groupName = m.league_group_name || 'Mečevi';
                        if (!acc[groupName]) acc[groupName] = [];
                        acc[groupName].push(m);
                        return acc;
                      }, {});
                      const groupNames = Object.keys(matchesByGroup).sort();
                      const hasMultipleGroups = groupNames.length > 1;

                      return (
                      <div key={roundNum}>
                        <h3 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-neutral-100 flex items-center justify-center text-xs">{roundNum}</span>
                          Kolo {roundNum}
                        </h3>
                        <div className={hasMultipleGroups ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-1.5'}>
                          {groupNames.map(groupName => (
                            <div key={groupName}>
                              {hasMultipleGroups && (
                                <div className="text-[10px] font-medium text-neutral-500 uppercase tracking-wide mb-2">{groupName}</div>
                              )}
                              <div className="space-y-1.5">
                                {matchesByGroup[groupName].map(match => {
                                  const isCompleted = match.status === 'completed';
                                  const winnerId = match.winner?.id || match.winner;
                                  const p1 = match.player1 || match.player1_detail;
                                  const p2 = match.player2 || match.player2_detail;
                                  const p1Won = winnerId === p1?.id;
                                  const p2Won = winnerId === p2?.id;
                                  return (
                                    <div key={match.id} className={`rounded-lg border ${isCompleted ? 'bg-white border-neutral-200' : 'bg-neutral-50 border-neutral-100'}`}>
                                      {/* Player 1 row */}
                                      <div className={`flex items-center justify-between px-3 py-1.5 ${p1Won ? 'bg-green-50 rounded-t-lg' : ''}`}>
                                        <span className={`text-xs sm:text-sm truncate flex-1 ${p1Won ? 'font-semibold text-green-700' : 'text-neutral-700'}`}>
                                          {p1?.first_name?.[0]}. {p1?.last_name}
                                        </span>
                                        {isCompleted && match.sets?.length > 0 && (
                                          <div className="flex gap-1.5 ml-2">
                                            {match.sets.map((s, i) => (
                                              <span key={i} className={`text-xs font-mono w-4 text-center ${s.player1_games > s.player2_games ? 'font-bold text-neutral-900' : 'text-neutral-400'}`}>
                                                {s.player1_games}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      {/* Player 2 row */}
                                      <div className={`flex items-center justify-between px-3 py-1.5 border-t border-neutral-100 ${p2Won ? 'bg-green-50 rounded-b-lg' : ''}`}>
                                        <span className={`text-xs sm:text-sm truncate flex-1 ${p2Won ? 'font-semibold text-green-700' : 'text-neutral-700'}`}>
                                          {p2?.first_name?.[0]}. {p2?.last_name}
                                        </span>
                                        {isCompleted && match.sets?.length > 0 && (
                                          <div className="flex gap-1.5 ml-2">
                                            {match.sets.map((s, i) => (
                                              <span key={i} className={`text-xs font-mono w-4 text-center ${s.player2_games > s.player1_games ? 'font-bold text-neutral-900' : 'text-neutral-400'}`}>
                                                {s.player2_games}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {!isCompleted && (
                                          <span className="text-[10px] text-neutral-400 ml-2">-</span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )})}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Knockout view - styled like PublicTournamentDetail
            <div>
              {/* Header with zoom controls */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider">Nokaut žrijeb</h2>
                  {league.knockout_tournament && (
                    <p className="text-xs text-purple-600 mt-1">{league.knockout_tournament.total_points} rang bodova za pobjednika</p>
                  )}
                </div>
                {knockoutMatches.length > 0 && (
                  <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
                    <button
                      onClick={() => setBracketZoom(z => Math.max(0.3, z - 0.1))}
                      className="p-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-neutral-600 hover:text-neutral-900"
                      title="Umanji"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setBracketZoom(1)}
                      className="px-2 py-1 text-xs font-medium text-neutral-600 hover:text-neutral-900 min-w-[3rem]"
                      title="Resetuj zoom"
                    >
                      {Math.round(bracketZoom * 100)}%
                    </button>
                    <button
                      onClick={() => setBracketZoom(z => Math.min(1.5, z + 0.1))}
                      className="p-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-neutral-600 hover:text-neutral-900"
                      title="Uvećaj"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto overflow-y-auto border border-neutral-200 rounded-xl bg-neutral-50 p-4">
                {league.knockout_tournament && knockoutMatches.length > 0 ? (
                  (() => {
                    const knockoutByRound = knockoutMatches.reduce((acc, m) => {
                      if (!acc[m.round]) acc[m.round] = [];
                      acc[m.round].push(m);
                      return acc;
                    }, {});
                    const sortedKoRounds = Object.keys(knockoutByRound).sort((a, b) => roundOrder.indexOf(a) - roundOrder.indexOf(b));
                    const firstKoRound = sortedKoRounds[0];
                    const firstKoRoundMatchCount = knockoutByRound[firstKoRound]?.length || 1;

                    // Match box dimensions
                    const matchWidth = 200;
                    const matchHeight = 60;
                    const roundGap = 48;
                    const headerHeight = 28;

                    // Calculate vertical spacing for each round
                    const getMatchPositions = (roundIndex, matchCount) => {
                      const positions = [];
                      if (roundIndex === 0) {
                        const gap = 20;
                        for (let i = 0; i < matchCount; i++) {
                          positions.push(i * (matchHeight + gap));
                        }
                      } else {
                        const prevMatchCount = matchCount * 2;
                        const prevPositions = getMatchPositions(roundIndex - 1, prevMatchCount);
                        for (let i = 0; i < matchCount; i++) {
                          const topMatch = prevPositions[i * 2];
                          const bottomMatch = prevPositions[i * 2 + 1];
                          positions.push((topMatch + bottomMatch + matchHeight) / 2 - matchHeight / 2);
                        }
                      }
                      return positions;
                    };

                    const firstRoundPositions = getMatchPositions(0, firstKoRoundMatchCount);
                    const totalHeight = firstRoundPositions[firstRoundPositions.length - 1] + matchHeight + headerHeight + 20;
                    const totalWidth = sortedKoRounds.length * (matchWidth + roundGap);

                    const scaledWidth = totalWidth * bracketZoom;
                    const scaledHeight = totalHeight * bracketZoom;

                    return (
                      <div style={{ width: scaledWidth, height: scaledHeight, overflow: 'hidden' }}>
                        <div style={{ transform: `scale(${bracketZoom})`, transformOrigin: 'top left', transition: 'transform 0.2s ease' }}>
                          <div className="relative" style={{ width: totalWidth, height: totalHeight }}>
                            {/* SVG Layer for connecting lines */}
                            <svg
                              className="absolute inset-0 pointer-events-none"
                              style={{ width: totalWidth, height: totalHeight }}
                            >
                              {sortedKoRounds.slice(0, -1).map((round, roundIdx) => {
                                const currentMatches = knockoutByRound[round]?.sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0)) || [];
                                const nextRound = sortedKoRounds[roundIdx + 1];
                                const nextMatches = knockoutByRound[nextRound]?.sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0)) || [];
                                const currentPositions = getMatchPositions(roundIdx, currentMatches.length);
                                const nextPositions = getMatchPositions(roundIdx + 1, nextMatches.length);

                                const xStart = roundIdx * (matchWidth + roundGap) + matchWidth;
                                const xEnd = (roundIdx + 1) * (matchWidth + roundGap);
                                const xMid = (xStart + xEnd) / 2;

                                return currentMatches.map((match, matchIdx) => {
                                  const nextMatchIdx = Math.floor(matchIdx / 2);
                                  const yStart = headerHeight + currentPositions[matchIdx] + matchHeight / 2;
                                  const yEnd = headerHeight + nextPositions[nextMatchIdx] + matchHeight / 2;

                                  return (
                                    <g key={`${round}-${matchIdx}`}>
                                      {/* Horizontal line from match to midpoint */}
                                      <line
                                        x1={xStart}
                                        y1={yStart}
                                        x2={xMid}
                                        y2={yStart}
                                        stroke="#d4d4d4"
                                        strokeWidth="2"
                                      />
                                      {/* Vertical line to connect pair */}
                                      {matchIdx % 2 === 0 && matchIdx + 1 < currentMatches.length && (
                                        <line
                                          x1={xMid}
                                          y1={yStart}
                                          x2={xMid}
                                          y2={headerHeight + currentPositions[matchIdx + 1] + matchHeight / 2}
                                          stroke="#d4d4d4"
                                          strokeWidth="2"
                                        />
                                      )}
                                      {/* Horizontal line from midpoint to next match */}
                                      {matchIdx % 2 === 0 && (
                                        <line
                                          x1={xMid}
                                          y1={yEnd}
                                          x2={xEnd}
                                          y2={yEnd}
                                          stroke="#d4d4d4"
                                          strokeWidth="2"
                                        />
                                      )}
                                    </g>
                                  );
                                });
                              })}
                            </svg>

                            {/* Match boxes layer */}
                            {sortedKoRounds.map((round, roundIdx) => {
                              const roundMatches = knockoutByRound[round]?.sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0)) || [];
                              const positions = getMatchPositions(roundIdx, roundMatches.length);
                              const xPos = roundIdx * (matchWidth + roundGap);

                              return (
                                <div key={round} className="absolute" style={{ left: xPos, top: 0, width: matchWidth }}>
                                  <div className="text-xs font-semibold text-neutral-500 uppercase text-center py-1" style={{ height: headerHeight }}>
                                    {roundLabels[round] || round}
                                  </div>
                                  {roundMatches.map((m, matchIdx) => {
                                    const p1 = m.player1;
                                    const p2 = m.player2;
                                    const winnerId = m.winner?.id || m.winner;
                                    const isBye = m.status === 'completed' && m.winner && (!p1 || !p2);

                                    return (
                                      <div
                                        key={m.id}
                                        className={`absolute bg-white rounded-lg overflow-hidden border border-neutral-200 shadow-sm ${isBye ? 'opacity-60' : ''}`}
                                        style={{
                                          top: headerHeight + positions[matchIdx],
                                          left: 0,
                                          width: matchWidth,
                                          height: matchHeight
                                        }}
                                      >
                                        <div className={`px-2 flex items-center justify-between border-b border-neutral-100 ${winnerId === p1?.id ? 'bg-rose-50' : ''}`} style={{ height: matchHeight / 2 }}>
                                          <span className={`text-xs truncate flex-1 ${winnerId === p1?.id ? 'font-semibold text-rose-700' : p1 ? 'text-neutral-700' : 'text-neutral-300 italic'}`}>
                                            {p1 ? `${p1.first_name} ${p1.last_name}` : (isBye ? 'BYE' : '')}
                                          </span>
                                          {m.sets?.length > 0 && p1 && (
                                            <div className="flex gap-0.5 ml-1">
                                              {m.sets.map((s, idx) => {
                                                const won = s.player1_games > s.player2_games;
                                                return (
                                                  <span key={idx} className={`w-4 h-4 flex items-center justify-center rounded text-[10px] font-semibold ${won ? 'bg-rose-500 text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                                                    {s.player1_games}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                        <div className={`px-2 flex items-center justify-between ${winnerId === p2?.id ? 'bg-rose-50' : ''}`} style={{ height: matchHeight / 2 }}>
                                          <span className={`text-xs truncate flex-1 ${winnerId === p2?.id ? 'font-semibold text-rose-700' : p2 ? 'text-neutral-700' : 'text-neutral-300 italic'}`}>
                                            {p2 ? `${p2.first_name} ${p2.last_name}` : (isBye ? 'BYE' : '')}
                                          </span>
                                          {m.sets?.length > 0 && p2 && (
                                            <div className="flex gap-0.5 ml-1">
                                              {m.sets.map((s, idx) => {
                                                const won = s.player2_games > s.player1_games;
                                                return (
                                                  <span key={idx} className={`w-4 h-4 flex items-center justify-center rounded text-[10px] font-semibold ${won ? 'bg-rose-500 text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                                                    {s.player2_games}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-12 text-neutral-400">
                    <Trophy className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Nokaut faza nije još započeta.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Rules Modal */}
      {showRules && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowRules(false)}>
          <div
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 bg-gradient-to-r from-amber-50 to-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-neutral-900">Pravilnik</h2>
                  <p className="text-xs text-neutral-500">{league.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowRules(false)}
                className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Content */}
            <div className="px-6 py-5 overflow-y-auto max-h-[calc(85vh-80px)]">
              <div className="prose prose-sm prose-neutral max-w-none">
                {league.rules?.split('\n').map((line, i) => {
                  const trimmed = line.trim();
                  if (!trimmed) return <br key={i} />;
                  // Check if it's a numbered rule
                  const isNumbered = /^\d+\./.test(trimmed);
                  if (isNumbered) {
                    return (
                      <div key={i} className="flex gap-3 mb-3">
                        <span className="text-amber-600 font-semibold shrink-0">{trimmed.match(/^\d+/)[0]}.</span>
                        <p className="text-neutral-700 leading-relaxed m-0">{trimmed.replace(/^\d+\.\s*/, '')}</p>
                      </div>
                    );
                  }
                  return <p key={i} className="text-neutral-700 leading-relaxed mb-3">{trimmed}</p>;
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <PublicFooter />
    </div>
  );
};

// ==================== PUBLIC TOURNAMENT DETAIL ====================
const PublicTournamentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [viewMode, setViewMode] = useState('bracket'); // 'rounds' or 'bracket'
  // Default 70% on mobile, 100% on desktop
  const [bracketZoom, setBracketZoom] = useState(0.9);

  useEffect(() => {
    // Use bracket endpoint - returns tournament + its matches in one call
    tournamentsAPI.getBracket(id).then(res => {
      setTournament(res.data.tournament);
      const matchesData = Array.isArray(res.data.matches) ? res.data.matches : [];
      setMatches(matchesData);
      // Set default selected round to the first round with matches
      if (matchesData.length > 0) {
        const roundOrder = ['round64', 'round32', 'round16', 'quarterfinal', 'semifinal', 'final'];
        const availableRounds = [...new Set(matchesData.map(m => m.round))];
        const sortedRounds = roundOrder.filter(r => availableRounds.includes(r));
        if (sortedRounds.length > 0) {
          setSelectedRound(sortedRounds[0]);
        }
      }
    }).catch(() => navigate('/tournaments'));
  }, [id, navigate]);

  if (!tournament) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <RefreshCw className="w-6 h-6 text-neutral-400 animate-spin" />
    </div>
  );

  // Group matches by round (using lowercase keys from backend)
  const matchesByRound = matches.reduce((acc, m) => {
    const round = m.round || 'other';
    if (!acc[round]) acc[round] = [];
    acc[round].push(m);
    return acc;
  }, {});

  // Round display names and order
  const roundLabels = {
    'final': 'Finale',
    'semifinal': 'Polufinale',
    'quarterfinal': 'Četvrtfinale',
    'round16': 'Osmina finala',
    'round32': 'Šesnaestina finala',
    'round64': 'Prvo kolo',
    'round_1': 'Prvo kolo',
    'round_2': 'Drugo kolo',
    'round_3': 'Treće kolo',
    'round_4': 'Četvrto kolo',
    'round_5': 'Peto kolo',
    'round_6': 'Šesto kolo'
  };
  const roundOrder = ['final', 'semifinal', 'quarterfinal', 'round16', 'round32', 'round64', 'round_1', 'round_2', 'round_3', 'round_4', 'round_5', 'round_6'];

  const getPlayerName = (m, playerKey) => {
    const player = m[playerKey];
    if (!player) return 'TBD';
    return player.full_name || `${player.first_name || ''} ${player.last_name || ''}`.trim() || 'TBD';
  };

  const isWinner = (m, playerKey) => {
    if (!m.winner) return false;
    const player = m[playerKey];
    if (!player) return false;
    return m.winner.id === player.id || m.winner === player.id;
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicHeader current="tournaments" />

      {/* Tournament Header */}
      <section className="pt-20 md:pt-24 pb-8 md:pb-12 px-4 md:px-6 bg-gradient-to-br from-rose-50 via-white to-neutral-50">
        <div className="max-w-4xl mx-auto">
          <Link to="/tournaments" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 text-sm mb-4 md:mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Svi turniri
          </Link>

          <div className="flex items-center gap-3 mb-4">
            {tournament.status === 'ongoing' && (
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-semibold">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                U toku
              </span>
            )}
            {tournament.status === 'finished' && (
              <span className="px-3 py-1 bg-neutral-100 text-neutral-600 rounded-full text-xs font-semibold">
                Završen
              </span>
            )}
            {tournament.status === 'upcoming' && (
              <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">
                Uskoro
              </span>
            )}
          </div>

          <h1 className="text-4xl font-bold text-neutral-900 mb-4">{tournament.name}</h1>

          <div className="flex flex-wrap items-center gap-6 text-neutral-500">
            {tournament.location && (
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" /> {tournament.location}
              </span>
            )}
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" /> {tournament.start_date}
            </span>
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4" /> {tournament.player_count || tournament.tournament_players?.length || 0} igrača
            </span>
            <span className="flex items-center gap-2">
              <Trophy className="w-4 h-4" /> {tournament.total_points || 0} bodova
            </span>
          </div>

          {tournament.winner_name && (
            <div className="mt-8 inline-flex items-center gap-4 bg-amber-50 rounded-xl px-6 py-4">
              <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-xs text-amber-600 font-medium uppercase tracking-wider">Pobjednik</div>
                <div className="text-xl font-bold text-neutral-900">{tournament.winner_name}</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Results Section - ITF Style with Round Tabs */}
      <section className="py-8 md:py-12 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          {/* View Mode Toggle & Round Tabs */}
          {matches.length > 0 && (
            <>
              {/* View mode toggle */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider">Rezultati</h2>
                <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('rounds')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === 'rounds' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`}
                  >
                    Po rundama
                  </button>
                  <button
                    onClick={() => setViewMode('bracket')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === 'bracket' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`}
                  >
                    Žrijeb
                  </button>
                </div>
              </div>

              {viewMode === 'rounds' ? (
                <>
                  {/* Round Tabs */}
                  <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
                    {(() => {
                      const roundOrder = ['round64', 'round32', 'round16', 'quarterfinal', 'semifinal', 'final'];
                      const availableRounds = roundOrder.filter(r => matchesByRound[r]);
                      return availableRounds.map(round => {
                        const roundMatches = matchesByRound[round] || [];
                        const completedCount = roundMatches.filter(m => m.status === 'completed').length;
                        const isComplete = completedCount === roundMatches.length && roundMatches.length > 0;
                        return (
                          <button
                            key={round}
                            onClick={() => setSelectedRound(round)}
                            className={`flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                              selectedRound === round
                                ? 'bg-rose-500 text-white'
                                : 'bg-white border border-neutral-200 text-neutral-600 hover:border-rose-300 hover:text-rose-600'
                            }`}
                          >
                            <span>{roundLabels[round] || round}</span>
                            {isComplete && <CheckCircle className="w-3.5 h-3.5 ml-1.5 inline-block" />}
                          </button>
                        );
                      });
                    })()}
                  </div>

                  {/* Matches for Selected Round */}
                  {selectedRound && matchesByRound[selectedRound] && (
                    <div className="space-y-3">
                      {matchesByRound[selectedRound]
                        .sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0))
                        .map((m) => {
                          const isBye = m.status === 'completed' && m.winner && (!m.player1 || !m.player2);
                          if (isBye) return null; // Hide BYE matches in round view

                          return (
                            <div key={m.id} className="bg-white border border-neutral-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                              {/* Match Card */}
                              <div className="p-4">
                                {/* Player 1 */}
                                <div className={`flex items-center justify-between py-2 ${isWinner(m, 'player1') ? '' : ''}`}>
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    {m.player1?.photo ? (
                                      <img src={m.player1.photo} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                                    ) : (
                                      <div className="w-10 h-10 bg-neutral-100 rounded-full flex items-center justify-center text-neutral-500 text-sm font-medium flex-shrink-0">
                                        {m.player1 ? `${(m.player1.first_name || '?')[0]}${(m.player1.last_name || '?')[0]}` : '?'}
                                      </div>
                                    )}
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span
                                        onClick={() => m.player1?.id && navigate(`/players/${m.player1.id}`)}
                                        className={`font-medium truncate cursor-pointer hover:text-rose-600 transition-colors ${isWinner(m, 'player1') ? 'text-rose-700' : m.player1 ? 'text-neutral-900' : 'text-neutral-300 italic'}`}
                                      >
                                        {m.player1 ? getPlayerName(m, 'player1') : 'TBD'}
                                      </span>
                                      {isWinner(m, 'player1') && <CheckCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />}
                                    </div>
                                  </div>
                                  {m.sets && m.sets.length > 0 && (
                                    <div className="flex gap-2 ml-4">
                                      {m.sets.map((s, idx) => (
                                        <span key={idx} className={`w-7 h-7 flex items-center justify-center rounded text-sm font-semibold ${
                                          s.player1_games > s.player2_games ? 'bg-rose-100 text-rose-700' : 'bg-neutral-100 text-neutral-600'
                                        }`}>
                                          {s.player1_games}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* Divider */}
                                <div className="border-t border-neutral-100 my-1"></div>

                                {/* Player 2 */}
                                <div className={`flex items-center justify-between py-2`}>
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    {m.player2?.photo ? (
                                      <img src={m.player2.photo} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                                    ) : (
                                      <div className="w-10 h-10 bg-neutral-100 rounded-full flex items-center justify-center text-neutral-500 text-sm font-medium flex-shrink-0">
                                        {m.player2 ? `${(m.player2.first_name || '?')[0]}${(m.player2.last_name || '?')[0]}` : '?'}
                                      </div>
                                    )}
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span
                                        onClick={() => m.player2?.id && navigate(`/players/${m.player2.id}`)}
                                        className={`font-medium truncate cursor-pointer hover:text-rose-600 transition-colors ${isWinner(m, 'player2') ? 'text-rose-700' : m.player2 ? 'text-neutral-900' : 'text-neutral-300 italic'}`}
                                      >
                                        {m.player2 ? getPlayerName(m, 'player2') : 'TBD'}
                                      </span>
                                      {isWinner(m, 'player2') && <CheckCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />}
                                    </div>
                                  </div>
                                  {m.sets && m.sets.length > 0 && (
                                    <div className="flex gap-2 ml-4">
                                      {m.sets.map((s, idx) => (
                                        <span key={idx} className={`w-7 h-7 flex items-center justify-center rounded text-sm font-semibold ${
                                          s.player2_games > s.player1_games ? 'bg-rose-100 text-rose-700' : 'bg-neutral-100 text-neutral-600'
                                        }`}>
                                          {s.player2_games}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Match Status Footer */}
                              {m.status !== 'completed' && (
                                <div className="px-4 py-2 bg-neutral-50 border-t border-neutral-100 text-xs text-neutral-500">
                                  {m.status === 'scheduled' && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Nije odigran</span>}
                                  {m.status === 'in_progress' && <span className="flex items-center gap-1 text-green-600"><Activity className="w-3 h-3" /> U toku</span>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </>
              ) : (
                /* Bracket View with Connecting Lines */
                <>
                  <div className="flex items-center justify-end mb-4">
                    <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
                      <button
                        onClick={() => setBracketZoom(z => Math.max(0.3, z - 0.1))}
                        className="p-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-neutral-600 hover:text-neutral-900"
                        title="Umanji"
                      >
                        <ZoomOut className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setBracketZoom(1)}
                        className="px-2 py-1 text-xs font-medium text-neutral-600 hover:text-neutral-900 min-w-[3rem]"
                        title="Resetuj zoom"
                      >
                        {Math.round(bracketZoom * 100)}%
                      </button>
                      <button
                        onClick={() => setBracketZoom(z => Math.min(1.5, z + 0.1))}
                        className="p-1.5 rounded-md hover:bg-white hover:shadow-sm transition-all text-neutral-600 hover:text-neutral-900"
                        title="Uvećaj"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto overflow-y-auto border border-neutral-200 rounded-xl bg-neutral-50 p-4">
                    {(() => {
                      const availableRounds = [...roundOrder].reverse().filter(r => matchesByRound[r]);
                      const firstRound = availableRounds[0];
                      const firstRoundMatchCount = matchesByRound[firstRound]?.length || 1;

                      // Match box dimensions
                      const matchWidth = 200; // Wider to fit full names + scores
                      const matchHeight = 60;
                      const roundGap = 48; // Gap between rounds (includes space for lines)
                      const headerHeight = 28;

                      // Calculate vertical spacing for each round
                      const getMatchPositions = (roundIndex, matchCount) => {
                        const positions = [];
                        if (roundIndex === 0) {
                          // First round: evenly spaced
                          const gap = 16;
                          for (let i = 0; i < matchCount; i++) {
                            positions.push(i * (matchHeight + gap));
                          }
                        } else {
                          // Later rounds: centered between their feeder matches
                          const prevMatchCount = matchCount * 2;
                          const prevPositions = getMatchPositions(roundIndex - 1, prevMatchCount);
                          for (let i = 0; i < matchCount; i++) {
                            const topMatch = prevPositions[i * 2];
                            const bottomMatch = prevPositions[i * 2 + 1];
                            positions.push((topMatch + bottomMatch + matchHeight) / 2 - matchHeight / 2);
                          }
                        }
                        return positions;
                      };

                      // Get total height based on first round
                      const firstRoundPositions = getMatchPositions(0, firstRoundMatchCount);
                      const totalHeight = firstRoundPositions[firstRoundPositions.length - 1] + matchHeight + headerHeight + 20;
                      const totalWidth = availableRounds.length * (matchWidth + roundGap);

                      // Scaled dimensions for wrapper to eliminate empty space
                      const scaledWidth = totalWidth * bracketZoom;
                      const scaledHeight = totalHeight * bracketZoom;

                      return (
                        <div style={{ width: scaledWidth, height: scaledHeight, overflow: 'hidden' }}>
                          <div style={{ transform: `scale(${bracketZoom})`, transformOrigin: 'top left' }}>
                            <div className="relative" style={{ width: totalWidth, height: totalHeight }}>
                            {/* SVG Layer for connecting lines */}
                            <svg
                              className="absolute inset-0 pointer-events-none"
                              style={{ width: totalWidth, height: totalHeight }}
                            >
                              {availableRounds.slice(0, -1).map((round, roundIdx) => {
                                const currentMatches = matchesByRound[round]?.sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0)) || [];
                                const nextRound = availableRounds[roundIdx + 1];
                                const nextMatches = matchesByRound[nextRound]?.sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0)) || [];
                                const currentPositions = getMatchPositions(roundIdx, currentMatches.length);
                                const nextPositions = getMatchPositions(roundIdx + 1, nextMatches.length);

                                const xStart = roundIdx * (matchWidth + roundGap) + matchWidth;
                                const xEnd = (roundIdx + 1) * (matchWidth + roundGap);
                                const xMid = (xStart + xEnd) / 2;

                                return currentMatches.map((match, matchIdx) => {
                                  const nextMatchIdx = Math.floor(matchIdx / 2);
                                  const yStart = headerHeight + currentPositions[matchIdx] + matchHeight / 2;
                                  const yEnd = headerHeight + nextPositions[nextMatchIdx] + matchHeight / 2;

                                  return (
                                    <g key={`${round}-${matchIdx}`}>
                                      {/* Horizontal line from match to midpoint */}
                                      <line
                                        x1={xStart}
                                        y1={yStart}
                                        x2={xMid}
                                        y2={yStart}
                                        stroke="#d4d4d4"
                                        strokeWidth="2"
                                      />
                                      {/* Vertical line to connect pair */}
                                      {matchIdx % 2 === 0 && matchIdx + 1 < currentMatches.length && (
                                        <line
                                          x1={xMid}
                                          y1={yStart}
                                          x2={xMid}
                                          y2={headerHeight + currentPositions[matchIdx + 1] + matchHeight / 2}
                                          stroke="#d4d4d4"
                                          strokeWidth="2"
                                        />
                                      )}
                                      {/* Horizontal line from midpoint to next match */}
                                      {matchIdx % 2 === 0 && (
                                        <line
                                          x1={xMid}
                                          y1={yEnd}
                                          x2={xEnd}
                                          y2={yEnd}
                                          stroke="#d4d4d4"
                                          strokeWidth="2"
                                        />
                                      )}
                                    </g>
                                  );
                                });
                              })}
                            </svg>

                            {/* Match boxes layer */}
                            {availableRounds.map((round, roundIdx) => {
                              const roundMatches = matchesByRound[round]?.sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0)) || [];
                              const positions = getMatchPositions(roundIdx, roundMatches.length);
                              const xPos = roundIdx * (matchWidth + roundGap);

                              return (
                                <div key={round} className="absolute" style={{ left: xPos, top: 0, width: matchWidth }}>
                                  <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2 text-center" style={{ height: headerHeight - 8 }}>
                                    {roundLabels[round] || round}
                                  </h3>
                                  {roundMatches.map((m, matchIdx) => {
                                    const isBye = m.status === 'completed' && m.winner && (!m.player1 || !m.player2);
                                    return (
                                      <div
                                        key={m.id}
                                        className={`absolute bg-white border border-neutral-200 rounded-lg overflow-hidden shadow-sm ${isBye ? 'opacity-60' : ''}`}
                                        style={{
                                          top: headerHeight + positions[matchIdx],
                                          left: 0,
                                          width: matchWidth,
                                          height: matchHeight
                                        }}
                                      >
                                        <div className={`px-2 py-1 flex items-center justify-between border-b border-neutral-100 ${isWinner(m, 'player1') ? 'bg-rose-50' : ''}`} style={{ height: matchHeight / 2 }}>
                                          <span className={`text-xs truncate flex-1 ${isWinner(m, 'player1') ? 'font-semibold text-rose-700' : m.player1 ? 'text-neutral-700' : 'text-neutral-300 italic'}`}>
                                            {m.player1 ? getPlayerName(m, 'player1') : (isBye ? 'BYE' : '')}
                                          </span>
                                          {m.sets && m.sets.length > 0 && m.player1 && (
                                            <div className="flex gap-1 ml-1">
                                              {m.sets.map((s, idx) => {
                                                const won = s.player1_games > s.player2_games;
                                                return (
                                                  <span key={idx} className={`w-4 h-4 flex items-center justify-center rounded text-[10px] font-bold ${won ? 'bg-rose-500 text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                                                    {s.player1_games}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                        <div className={`px-2 py-1 flex items-center justify-between ${isWinner(m, 'player2') ? 'bg-rose-50' : ''}`} style={{ height: matchHeight / 2 }}>
                                          <span className={`text-xs truncate flex-1 ${isWinner(m, 'player2') ? 'font-semibold text-rose-700' : m.player2 ? 'text-neutral-700' : 'text-neutral-300 italic'}`}>
                                            {m.player2 ? getPlayerName(m, 'player2') : (isBye ? 'BYE' : '')}
                                          </span>
                                          {m.sets && m.sets.length > 0 && m.player2 && (
                                            <div className="flex gap-1 ml-1">
                                              {m.sets.map((s, idx) => {
                                                const won = s.player2_games > s.player1_games;
                                                return (
                                                  <span key={idx} className={`w-4 h-4 flex items-center justify-center rounded text-[10px] font-bold ${won ? 'bg-rose-500 text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                                                    {s.player2_games}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </>
              )}
            </>
          )}

          {matches.length === 0 && (
            <div className="py-16 text-center text-neutral-400">
              <Trophy className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Rezultati još nisu dostupni</p>
            </div>
          )}
        </div>
      </section>

      {/* Players List */}
      {tournament.tournament_players && tournament.tournament_players.length > 0 && (
        <section className="py-12 px-6 bg-neutral-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-6">
              Prijavljeni igrači ({tournament.tournament_players.length})
            </h2>
            <div className="border border-neutral-200 rounded-xl bg-white overflow-hidden">
              {tournament.tournament_players.map((tp, idx) => (
                <div
                  key={tp.id}
                  onClick={() => tp.player?.id && navigate(`/players/${tp.player.id}`)}
                  className={`flex items-center gap-4 p-4 cursor-pointer hover:bg-rose-50/50 transition-colors ${idx !== tournament.tournament_players.length - 1 ? 'border-b border-neutral-100' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${tp.seed ? 'bg-rose-100 text-rose-700' : 'bg-neutral-100 text-neutral-500'}`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-neutral-900 hover:text-rose-600 transition-colors">{tp.player?.full_name || `${tp.player?.first_name} ${tp.player?.last_name}`}</span>
                    {tp.seed && <span className="ml-2 text-xs text-rose-500 font-medium">Nosilac #{tp.seed}</span>}
                  </div>
                  {tournament.winner_name === tp.player?.full_name && (
                    <div className="flex items-center gap-1.5 text-amber-600">
                      <Trophy className="w-4 h-4" />
                      <span className="text-xs font-semibold">Pobjednik</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <PublicFooter />
    </div>
  );
};

// ==================== PUBLIC NEWS ====================
const PublicNews = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    newsAPI.getAll()
      .then(res => {
        const newsData = Array.isArray(res.data) ? res.data : [];
        setNews(newsData);
        // If URL has ID, find and select that news
        if (id) {
          const found = newsData.find(n => n.id === parseInt(id));
          if (found) setSelectedNews(found);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('sr-Latn-ME', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (selectedNews) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PublicHeader current="news" />
        <div className="pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <button
              onClick={() => { setSelectedNews(null); navigate('/news'); }}
              className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Nazad na vijesti</span>
            </button>

            {selectedNews.image_url && (
              <div className="aspect-video rounded-2xl overflow-hidden mb-8 bg-neutral-200">
                <img
                  src={selectedNews.image_url}
                  alt={selectedNews.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm text-neutral-500">{formatDate(selectedNews.created_at)}</span>
              {selectedNews.tournament_name && (
                <>
                  <span className="text-neutral-300">•</span>
                  <span className="text-sm text-rose-500 font-medium">{selectedNews.tournament_name}</span>
                </>
              )}
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">{selectedNews.title}</h1>

            <div className="prose prose-neutral max-w-none">
              <p className="text-lg text-neutral-600 leading-relaxed whitespace-pre-wrap">{selectedNews.content}</p>
            </div>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicHeader current="news" />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-rose-50 via-white to-neutral-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 tracking-tight mb-4">Vijesti</h1>
          <p className="text-lg text-neutral-500 max-w-2xl">
            Najnovije vijesti, rezultati turnira i dešavanja iz svijeta rekreativnog tenisa.
          </p>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-neutral-200/60">
                  <Skeleton className="aspect-video w-full" />
                  <div className="p-5 space-y-3">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-5 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : news.length === 0 ? (
            <EmptyState
              icon={Newspaper}
              title="Nema vijesti"
              description="Trenutno nema objavljenih vijesti"
            />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.map((item, idx) => (
                <article
                  key={item.id}
                  onClick={() => navigate(`/news/${item.id}`)}
                  className="stagger-item group bg-white rounded-2xl overflow-hidden border border-neutral-200/60 hover:border-rose-200 hover:shadow-lg transition-all cursor-pointer touch-feedback"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  {item.image_url ? (
                    <LazyImage
                      src={item.image_url}
                      alt={item.title}
                      className="aspect-video group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-rose-100 to-neutral-100 flex items-center justify-center">
                      <Newspaper className="w-12 h-12 text-neutral-300" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs text-neutral-400">{formatDate(item.created_at)}</span>
                      {item.tournament_name && (
                        <>
                          <span className="text-neutral-200">•</span>
                          <span className="text-xs text-rose-500 font-medium">{item.tournament_name}</span>
                        </>
                      )}
                    </div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-rose-600 transition-colors mb-2 line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-neutral-500 line-clamp-2">{item.content}</p>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

// ==================== PUBLIC PLAYERS ====================
const PublicPlayers = () => {
  const [players, setPlayers] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      playersAPI.getAllPublic(),  // Only show active players on public site
      rankingsAPI.getAll(),
    ]).then(([pRes, rRes]) => {
      setPlayers(Array.isArray(pRes.data) ? pRes.data : []);
      setRankings(Array.isArray(rRes.data) ? rRes.data : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = players.filter(p =>
    `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  const getPlayerRankData = (playerId) => {
    const rankData = rankings.find(r => r.player?.id === playerId || r.player === playerId);
    if (!rankData) return null;
    return {
      rank: rankData.current_rank || rankings.indexOf(rankData) + 1,
      points: rankData.total_points || 0
    };
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicHeader current="players" />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-16 bg-gradient-to-br from-rose-50 via-white to-neutral-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-1 md:mb-2">Igrači</h1>
          <p className="text-base md:text-lg text-neutral-500">Svi registrovani igrači rekreativnog tenisa</p>
        </div>
      </section>

      {/* Search */}
      <section className="py-6 md:py-8 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <input
              type="text"
              placeholder="Pretraži igrače..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 md:py-4 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-300 transition-all text-base"
            />
          </div>
        </div>
      </section>

      {/* Players List */}
      <section className="pb-12 md:pb-20 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
              {[...Array(6)].map((_, i) => (
                <div key={i} className={`flex items-center gap-3 md:gap-5 p-4 md:p-5 ${i !== 5 ? 'border-b border-neutral-100' : ''}`}>
                  <Skeleton variant="avatar" className="w-10 h-10 md:w-12 md:h-12" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                  <Skeleton className="h-6 w-12" />
                </div>
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
              {filtered.map((player, idx) => {
                const rankData = getPlayerRankData(player.id);
                return (
                  <div
                    key={player.id}
                    onClick={() => navigate(`/players/${player.id}`)}
                    className={`stagger-item flex items-center gap-3 md:gap-5 p-4 md:p-5 hover:bg-rose-50/50 transition-colors cursor-pointer touch-feedback ${idx !== filtered.length - 1 ? 'border-b border-neutral-100' : ''}`}
                  >
                    {/* Avatar */}
                    {player.photo ? (
                      <img src={player.photo} alt={`${player.first_name} ${player.last_name}`} className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white font-semibold text-sm md:text-base flex-shrink-0">
                        {(player.first_name || '?')[0]}{(player.last_name || '?')[0]}
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-neutral-900 text-sm md:text-base truncate">{player.first_name} {player.last_name}</div>
                      <div className="flex items-center gap-2 md:gap-3 mt-0.5 text-xs md:text-sm text-neutral-500">
                        {player.country && <span className="truncate">{player.country}</span>}
                        <span className="hidden sm:inline">{player.handedness === 'L' ? 'Ljevak' : 'Dešnjak'}</span>
                      </div>
                    </div>

                    {/* Points */}
                    {rankData && (
                      <div className="text-right flex-shrink-0">
                        <div className="text-base md:text-lg font-bold text-neutral-900">{rankData.points}</div>
                        <div className="text-xs text-neutral-500">bod.</div>
                      </div>
                    )}
                    <ArrowRight className="w-4 h-4 text-neutral-300 flex-shrink-0 hidden sm:block" />
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon={Users}
              title="Nema igrača"
              description={search ? "Nema pronađenih igrača za datu pretragu" : "Trenutno nema registrovanih igrača"}
            />
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

// ==================== PUBLIC PLAYER DETAIL ====================
const PublicPlayerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [powerIndex, setPowerIndex] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Use optimized stats endpoint - returns player, ranking, and matches in one call
    playersAPI.getStats(id).then(res => {
      const data = res.data;
      setPlayer(data.player);
      setRanking({
        current_rank: data.current_rank,
        total_points: data.total_points,
        matches_won: data.matches_won,
        matches_lost: data.matches_lost,
        tournaments_played: data.tournaments_played,
      });
      setPowerIndex(data.power_index || null);
      setRecentMatches(Array.isArray(data.recent_matches) ? data.recent_matches : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PublicHeader current="players" />
        <section className="pt-24 md:pt-32 pb-8 md:pb-12 bg-gradient-to-br from-rose-50 via-white to-neutral-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Skeleton className="h-4 w-32 mb-6" />
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <Skeleton variant="avatar" className="w-24 h-24 md:w-32 md:h-32" />
              <div className="flex-1 space-y-3 text-center sm:text-left">
                <Skeleton className="h-8 w-48 mx-auto sm:mx-0" />
                <Skeleton className="h-4 w-64 mx-auto sm:mx-0" />
              </div>
            </div>
          </div>
        </section>
        <section className="py-8 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-4 md:p-6 border border-neutral-200 text-center">
                  <Skeleton className="h-8 w-12 mx-auto mb-2" />
                  <Skeleton className="h-3 w-16 mx-auto" />
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PublicHeader current="players" />
        <EmptyState
          icon={Users}
          title="Igrač nije pronađen"
          description="Traženi igrač ne postoji ili je obrisan"
        />
      </div>
    );
  }

  // Use API values for wins/losses (all matches), not just recent ones
  const wins = ranking?.matches_won || 0;
  const losses = ranking?.matches_lost || 0;

  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicHeader current="players" />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-12 bg-gradient-to-br from-rose-50 via-white to-neutral-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Nazad
          </button>

          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar/Photo */}
            {player.photo ? (
              <img
                src={player.photo}
                alt={player.full_name}
                className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-4 border-white shadow-lg"
              />
            ) : (
              <div className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-full flex items-center justify-center text-white text-3xl md:text-4xl font-bold border-4 border-white shadow-lg">
                {(player.first_name || '?')[0]}{(player.last_name || '?')[0]}
              </div>
            )}

            {/* Info */}
            <div className="text-center sm:text-left flex-1">
              <h1 className="text-2xl md:text-4xl font-bold text-neutral-900 mb-2">{player.full_name}</h1>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 text-sm md:text-base text-neutral-500">
                {player.country && <span>{player.country}</span>}
                {player.city && <span>• {player.city}</span>}
                <span>• {player.handedness === 'L' ? 'Ljevak' : 'Dešnjak'}</span>
                {player.age && <span>• {player.age} god.</span>}
              </div>
            </div>

            {/* Power Index badge */}
            {powerIndex && (
              <Link
                to="/power-index"
                className="flex-shrink-0 flex flex-col items-center justify-center rounded-2xl bg-neutral-900 text-white px-6 py-4 min-w-[124px] hover:bg-neutral-800 transition-colors"
              >
                <div className="text-3xl md:text-4xl font-bold leading-none">
                  {Number(powerIndex.power_index).toFixed(2)}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-neutral-400 mt-1.5">Power Index</div>
                {powerIndex.index_rank && (
                  <div className="text-xs text-rose-300 mt-1">
                    #{powerIndex.index_rank}
                    {powerIndex.is_provisional && <span className="text-amber-400 font-bold" title="Provizoran rejting"> *</span>}
                  </div>
                )}
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="py-8 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-white rounded-xl p-4 md:p-6 border border-neutral-200 text-center">
              <div className="text-2xl md:text-3xl font-bold text-neutral-900">
                {ranking?.current_rank || '-'}
              </div>
              <div className="text-xs md:text-sm text-neutral-500 mt-1">Rang</div>
            </div>
            <div className="bg-white rounded-xl p-4 md:p-6 border border-neutral-200 text-center">
              <div className="text-2xl md:text-3xl font-bold text-rose-500">
                {ranking?.total_points || 0}
              </div>
              <div className="text-xs md:text-sm text-neutral-500 mt-1">Bodova</div>
            </div>
            <div className="bg-white rounded-xl p-4 md:p-6 border border-neutral-200 text-center">
              <div className="text-2xl md:text-3xl font-bold text-green-600">{wins}</div>
              <div className="text-xs md:text-sm text-neutral-500 mt-1">Pobjeda</div>
            </div>
            <div className="bg-white rounded-xl p-4 md:p-6 border border-neutral-200 text-center">
              <div className="text-2xl md:text-3xl font-bold text-neutral-400">{losses}</div>
              <div className="text-xs md:text-sm text-neutral-500 mt-1">Poraza</div>
            </div>
          </div>
        </div>
      </section>

      {/* Bio */}
      {player.bio && (
        <section className="py-6 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-5 md:p-6 border border-neutral-200">
              <h2 className="font-semibold text-neutral-900 mb-3">O igraču</h2>
              <p className="text-neutral-600 text-sm md:text-base whitespace-pre-line">{player.bio}</p>
            </div>
          </div>
        </section>
      )}

      {/* Recent Matches */}
      {recentMatches.length > 0 && (
        <section className="py-6 md:py-8 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg md:text-xl font-bold text-neutral-900 mb-4">Posljednji mečevi</h2>
            <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
              {recentMatches.map((m, idx) => {
                const isWinner = m.winner?.id === parseInt(id);
                const opponent = m.player1?.id === parseInt(id) ? m.player2 : m.player1;
                return (
                  <div
                    key={m.id}
                    className={`p-4 md:p-5 ${idx !== recentMatches.length - 1 ? 'border-b border-neutral-100' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isWinner ? 'bg-green-500' : 'bg-red-400'}`} />
                        <div className="min-w-0">
                          <div className="font-medium text-neutral-900 text-sm md:text-base truncate">
                            vs {opponent?.full_name || 'Nepoznat'}
                          </div>
                          <div className="text-xs md:text-sm text-neutral-500">
                            {m.tournament_name || m.league_name || 'Meč'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <div className={`font-mono font-semibold text-sm md:text-base ${isWinner ? 'text-green-600' : 'text-neutral-400'}`}>
                          {isWinner ? 'P' : 'G'}
                        </div>
                        <div className="text-xs md:text-sm text-neutral-500">{m.score_summary || '-'}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <PublicFooter />
    </div>
  );
};

// ==================== PUBLIC CLUBS ====================
const PublicClubs = () => {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    clubsAPI.getActive()
      .then(res => {
        setClubs(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const surfaceLabels = { clay: 'Šljaka', hard: 'Hard', grass: 'Trava', carpet: 'Tepih', concrete: 'Beton', other: 'Ostalo' };

  return (
    <div className="min-h-screen bg-neutral-50 overflow-x-hidden">
      <PublicHeader current="clubs" />

      <section className="pt-24 md:pt-32 pb-8 md:pb-16 bg-gradient-to-br from-rose-50 via-white to-neutral-50">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-1 md:mb-2">Teniski tereni</h1>
          <p className="text-base md:text-lg text-neutral-500">Pronađi teniski teren u tvojoj blizini</p>
        </div>
      </section>

      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4">
          {loading ? (
            <div className="grid gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-neutral-200/60 p-4 flex items-center gap-4">
                  <Skeleton className="w-24 h-24 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-1/2" />
                    <Skeleton className="h-4 w-3/4" />
                    <div className="flex gap-2">
                      <Skeleton className="h-6 w-16 rounded-full" />
                      <Skeleton className="h-6 w-16 rounded-full" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : clubs.length > 0 ? (
            <div className="grid gap-4">
              {clubs.map((club, idx) => (
                <div key={club.id} onClick={() => navigate(`/clubs/${club.id}`)}
                  className="stagger-item bg-white rounded-2xl border border-neutral-200/60 p-4 flex items-center gap-3 cursor-pointer hover:border-neutral-300 transition-all touch-feedback overflow-hidden"
                  style={{ animationDelay: `${idx * 0.05}s` }}>
                  {club.main_image_url ? (
                    <LazyImage src={club.main_image_url} alt={club.name} className="w-20 h-20 md:w-24 md:h-24 rounded-xl flex-shrink-0" />
                  ) : (
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-neutral-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 md:w-8 md:h-8 text-neutral-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-neutral-900 truncate">{club.name}</h3>
                      {club.is_featured && <Star className="w-4 h-4 text-amber-500 fill-amber-500 flex-shrink-0" />}
                    </div>
                    <p className="text-sm text-neutral-500 truncate">{club.address}, {club.city}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <span className="text-xs bg-neutral-100 px-2 py-0.5 rounded-full whitespace-nowrap">{club.courts_count} teren{club.courts_count > 1 ? 'a' : ''}</span>
                      <span className="text-xs bg-neutral-100 px-2 py-0.5 rounded-full whitespace-nowrap">{surfaceLabels[club.surface] || club.surface}</span>
                      {club.has_lighting && <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap">Rasvjeta</span>}
                      {club.is_indoor && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full whitespace-nowrap">Zatvoreno</span>}
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-neutral-400 flex-shrink-0 hidden sm:block" />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={MapPin}
              title="Nema terena"
              description="Trenutno nema dostupnih teniskih terena"
            />
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

// ==================== PUBLIC CLUB DETAIL ====================
const PublicClubDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    clubsAPI.get(id).then(res => setClub(res.data)).catch(() => navigate('/clubs'));
  }, [id, navigate]);

  if (!club) return (
    <div className="min-h-screen bg-neutral-50">
      <PublicHeader current="clubs" />
      <section className="pt-20 md:pt-28">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Skeleton className="h-4 w-32 mb-4" />
          <Skeleton className="aspect-video w-full rounded-2xl mb-4" />
          <div className="space-y-3">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-neutral-200">
                  <Skeleton className="h-5 w-full mb-2" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const surfaceLabels = { clay: 'Šljaka', hard: 'Hard', grass: 'Trava', carpet: 'Tepih', concrete: 'Beton', other: 'Ostalo' };
  const images = club.images || [];

  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicHeader current="clubs" />

      <section className="pt-20 md:pt-28">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <button onClick={() => navigate('/clubs')} className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-4">
            <ArrowLeft className="w-4 h-4" /> Nazad na terene
          </button>

          {/* Image Gallery */}
          {images.length > 0 && (
            <div className="mb-6">
              <img src={images[selectedImage]?.image_url} alt={club.name} className="w-full h-64 md:h-96 object-cover rounded-2xl" />
              {images.length > 1 && (
                <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
                  {images.map((img, idx) => (
                    <img key={img.id} src={img.image_url} alt="" onClick={() => setSelectedImage(idx)}
                      className={`w-20 h-16 object-cover rounded-lg cursor-pointer flex-shrink-0 ${selectedImage === idx ? 'ring-2 ring-rose-500' : 'opacity-70 hover:opacity-100'}`} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Club Info */}
          <div className="bg-white rounded-2xl border border-neutral-200/60 p-6 md:p-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl md:text-3xl font-bold text-neutral-900">{club.name}</h1>
                  {club.is_featured && <Star className="w-5 h-5 text-amber-500 fill-amber-500" />}
                </div>
                <p className="text-neutral-500 mt-1">{club.address}, {club.city}</p>
              </div>
            </div>

            {club.description && <p className="text-neutral-600 mb-6">{club.description}</p>}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-neutral-900 mb-3">Informacije o terenima</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-neutral-500">Broj terena:</span><span className="font-medium">{club.courts_count}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-500">Podloga:</span><span className="font-medium">{surfaceLabels[club.surface]}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-500">Rasvjeta:</span><span className="font-medium">{club.has_lighting ? 'Da' : 'Ne'}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-500">Zatvoreni tereni:</span><span className="font-medium">{club.is_indoor ? 'Da' : 'Ne'}</span></div>
                  {club.working_hours && <div className="flex justify-between"><span className="text-neutral-500">Radno vrijeme:</span><span className="font-medium">{club.working_hours}</span></div>}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-neutral-900 mb-3">Kontakt</h3>
                <div className="space-y-2 text-sm">
                  {club.phone && <div className="flex items-center gap-2"><span className="text-neutral-500">Telefon:</span><a href={`tel:${club.phone}`} className="font-medium text-rose-500">{club.phone}</a></div>}
                  {club.email && <div className="flex items-center gap-2"><span className="text-neutral-500">Email:</span><a href={`mailto:${club.email}`} className="font-medium text-rose-500">{club.email}</a></div>}
                  {club.website && <div><a href={club.website} target="_blank" rel="noopener noreferrer" className="text-rose-500 hover:underline">Web stranica</a></div>}
                  {club.instagram && <div><a href={`https://instagram.com/${club.instagram.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="text-rose-500 hover:underline">Instagram</a></div>}
                  {club.google_maps_url && <div><a href={club.google_maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-rose-500 hover:underline"><MapPin className="w-4 h-4" /> Pogledaj na mapi</a></div>}
                </div>
              </div>
            </div>

            {club.price_info && (
              <div className="mt-6 pt-6 border-t border-neutral-100">
                <h3 className="font-semibold text-neutral-900 mb-2">Cijene</h3>
                <p className="text-sm text-neutral-600 whitespace-pre-line">{club.price_info}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="pb-20" />
      <PublicFooter />
    </div>
  );
};

// ==================== PRAVILNIK ====================
const Pravilnik = () => {
  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicHeader current="pravilnik" />

      <section className="pt-24 md:pt-32 pb-8 md:pb-16 bg-gradient-to-br from-rose-50 via-white to-neutral-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-1 md:mb-2">Pravilnik</h1>
          <p className="text-base md:text-lg text-neutral-500">Pravila takmičenja za turnire i lige</p>
        </div>
      </section>

      <section className="py-8 md:py-12 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl border border-neutral-200/60 p-6 md:p-8 space-y-8">

            {/* Turniri */}
            <div>
              <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-rose-500" /> Turniri
              </h2>
              <div className="space-y-4 text-neutral-600">
                <div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Format takmičenja</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Turniri se igraju po eliminacionom (nokaut) sistemu</li>
                    <li>Mečevi se igraju na dva dobijena seta (best of 3)</li>
                    <li>U slučaju rezultata 6:6 u setu, igra se tie-break do 7 poena</li>
                    <li>Moguć je i format jednog seta za ranije runde</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Bodovanje (ATP stil)</h3>
                  <p className="text-sm mb-2">Poeni se dodjeljuju po završetku meča prema rundi u kojoj je igrač ispao:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li><strong>Pobjednik turnira:</strong> 100% ukupnih poena turnira</li>
                    <li><strong>Finalist:</strong> 60% ukupnih poena</li>
                    <li><strong>Polufinalist:</strong> 36% ukupnih poena</li>
                    <li><strong>Četvrtfinalist:</strong> 18% ukupnih poena</li>
                    <li><strong>Osmina finala:</strong> 9% ukupnih poena</li>
                    <li><strong>1/16 finala:</strong> 5% ukupnih poena</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Žrijeb i nosioci</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Žrijeb se vrši automatski prema statusu nosioca</li>
                    <li>Nosioci se raspoređuju tako da se izbjegne njihov susret u ranijim rundama</li>
                    <li>U slučaju nepotpunog broja igrača, koriste se slobodna mjesta</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Rang lista */}
            <div className="pt-6 border-t border-neutral-100">
              <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-rose-500" /> Rang lista
              </h2>
              <div className="space-y-4 text-neutral-600">
                <div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Računanje poena</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Rang lista se računa na osnovu poena osvojenih u posljednjih 365 dana</li>
                    <li>Poeni stariji od godinu dana automatski istječu</li>
                    <li>Rang lista se automatski ažurira nakon svakog završenog meča</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Kategorije turnira</h3>
                  <p className="text-sm">Turniri mogu imati različite kategorije bodova (npr. 500, 1000, 2000) što utiče na ukupne poene koje igrači mogu osvojiti.</p>
                </div>
              </div>
            </div>

            {/* Opšta pravila */}
            <div className="pt-6 border-t border-neutral-100">
              <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-rose-500" /> Opšta pravila
              </h2>
              <div className="space-y-4 text-neutral-600 text-sm">
                <ul className="list-disc list-inside space-y-1">
                  <li>Svi igrači moraju biti registrovani na platformi</li>
                  <li>Fair play i sportsko ponašanje su obavezni</li>
                  <li>Rezultate unosi organizator ili ovlašteni administrator</li>
                  <li>U slučaju spora, odluku donosi organizator takmičenja</li>
                  <li>Odustajanje bez validnog razloga može rezultirati suspenzijom</li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

// ==================== ADMIN NEWS ====================
const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { confirm } = useConfirm();

  useEffect(() => {
    newsAPI.getAll()
      .then(res => { setNews(Array.isArray(res.data) ? res.data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    const confirmed = await confirm({
      title: 'Obriši vijest',
      message: 'Da li ste sigurni da želite obrisati ovu vijest?',
      confirmText: 'Obriši',
      cancelText: 'Odustani',
      variant: 'danger'
    });
    if (confirmed) {
      await newsAPI.delete(id);
      setNews(news.filter(n => n.id !== id));
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('sr-Latn-ME', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Vijesti</h1>
          <p className="text-neutral-500 mt-1">Upravljanje vijestima i obavještenjima</p>
        </div>
        <Link to="/panel/news/new" className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-xl text-sm hover:bg-neutral-800 transition-colors">
          <Plus className="w-4 h-4" /> Nova vijest
        </Link>
      </div>

      {/* News List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-neutral-300 border-t-neutral-600" />
        </div>
      ) : news.length > 0 ? (
        <div className="bg-white rounded-2xl border border-neutral-200/60 divide-y divide-neutral-100">
          {news.map(item => (
            <div
              key={item.id}
              onClick={() => navigate(`/panel/news/${item.id}`)}
              className="flex items-center gap-4 px-5 py-4 hover:bg-neutral-50 transition-colors cursor-pointer"
            >
              {item.image_url ? (
                <img src={item.image_url} alt="" className="w-12 h-12 rounded-xl object-cover bg-neutral-100 flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-neutral-100 flex items-center justify-center flex-shrink-0">
                  <Newspaper className="w-5 h-5 text-neutral-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-neutral-900 font-medium truncate">{item.title}</span>
                  {item.is_featured && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Istaknuto</span>
                  )}
                  {!item.is_published && (
                    <span className="text-xs bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded">Nacrt</span>
                  )}
                </div>
                <div className="text-sm text-neutral-500 mt-0.5">
                  {formatDate(item.created_at)}
                  {item.tournament_name && ` · ${item.tournament_name}`}
                </div>
              </div>
              <button
                onClick={(e) => handleDelete(item.id, e)}
                className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-neutral-400">
          <Newspaper className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nema objavljenih vijesti</p>
          <Link to="/panel/news/new" className="inline-block mt-3 text-sm text-rose-500 hover:text-rose-600">
            Dodaj prvu vijest →
          </Link>
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN NEWS FORM ====================
const AdminNewsForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    tournament: '',
    is_published: true,
    is_featured: false,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    tournamentsAPI.getAll().then(res => {
      setTournaments(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});

    if (id) {
      setLoading(true);
      newsAPI.get(id).then(res => {
        const data = res.data;
        setFormData({
          title: data.title || '',
          content: data.content || '',
          tournament: data.tournament || '',
          is_published: data.is_published ?? true,
          is_featured: data.is_featured ?? false,
        });
        if (data.image_url) setImagePreview(data.image_url);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [id]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = new FormData();
    data.append('title', formData.title);
    data.append('content', formData.content);
    if (formData.tournament) data.append('tournament', formData.tournament);
    data.append('is_published', formData.is_published);
    data.append('is_featured', formData.is_featured);
    if (imageFile) data.append('image', imageFile);

    try {
      if (id) {
        await newsAPI.update(id, data);
      } else {
        await newsAPI.create(data);
      }
      navigate('/panel/news');
    } catch (error) {
      console.error('Error saving news:', error);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-neutral-300 border-t-neutral-600" />
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-6">
      <button onClick={() => navigate('/panel/news')} className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 text-sm">
        <ArrowLeft className="w-4 h-4" /> Nazad na vijesti
      </button>

      <div>
        <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">{id ? 'Uredi vijest' : 'Nova vijest'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-neutral-200/60 p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1.5">Naslov</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
            placeholder="Unesite naslov vijesti"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1.5">Sadržaj</label>
          <textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300 resize-none"
            rows={5}
            placeholder="Unesite sadržaj vijesti"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1.5">Slika</label>
          <div className="flex items-start gap-4">
            {imagePreview ? (
              <div className="relative">
                <img src={imagePreview} alt="Preview" className="w-24 h-24 rounded-xl object-cover bg-neutral-100" />
                <button
                  type="button"
                  onClick={() => { setImageFile(null); setImagePreview(null); }}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-neutral-900 text-white rounded-full flex items-center justify-center hover:bg-neutral-700"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <label className="w-24 h-24 rounded-xl border-2 border-dashed border-neutral-200 hover:border-neutral-300 flex flex-col items-center justify-center cursor-pointer transition-colors">
                <Image className="w-6 h-6 text-neutral-400 mb-1" />
                <span className="text-xs text-neutral-500">Dodaj</span>
                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              </label>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1.5">Povezani turnir</label>
          <select
            value={formData.tournament}
            onChange={(e) => setFormData({ ...formData, tournament: e.target.value })}
            className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-neutral-300"
          >
            <option value="">Bez turnira</option>
            {tournaments.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_published}
              onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
              className="w-4 h-4 rounded border-neutral-300 text-neutral-900 focus:ring-neutral-500"
            />
            <span className="text-sm text-neutral-700">Objavljeno</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_featured}
              onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
              className="w-4 h-4 rounded border-neutral-300 text-neutral-900 focus:ring-neutral-500"
            />
            <span className="text-sm text-neutral-700">Istaknuto</span>
          </label>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full bg-neutral-900 hover:bg-neutral-800 text-white py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
        >
          {saving ? 'Čuvanje...' : (id ? 'Sačuvaj izmjene' : 'Objavi vijest')}
        </button>
      </form>
    </div>
  );
};

// ==================== ADMIN USERS ====================
const AdminUsers = () => {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    password_confirm: '',
    role: 'admin'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data || []);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          username: form.username,
          email: form.email,
          full_name: form.full_name,
          role: form.role,
        };
        if (form.password) {
          updateData.password = form.password;
        }
        await usersAPI.update(editingUser.id, updateData);
      } else {
        // Create new user
        await usersAPI.create(form);
      }
      await loadUsers();
      resetForm();
    } catch (err) {
      const errorMsg = err.response?.data?.error ||
                       err.response?.data?.password_confirm?.[0] ||
                       err.response?.data?.username?.[0] ||
                       'Greška pri čuvanju korisnika';
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({
      username: user.username,
      email: user.email || '',
      full_name: user.full_name || '',
      password: '',
      password_confirm: '',
      role: user.role
    });
    setShowForm(true);
    setError('');
  };

  const handleDelete = async (user) => {
    const confirmed = await confirm({
      title: 'Obriši korisnika',
      message: `Jeste li sigurni da želite obrisati korisnika "${user.username}"?`,
      confirmText: 'Obriši',
      cancelText: 'Odustani',
      variant: 'danger'
    });
    if (!confirmed) return;
    try {
      await usersAPI.delete(user.id);
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri brisanju korisnika');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingUser(null);
    setForm({
      username: '',
      email: '',
      full_name: '',
      password: '',
      password_confirm: '',
      role: 'admin'
    });
    setError('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-neutral-900 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Korisnici</h1>
          <p className="text-neutral-500 text-sm mt-1">Upravljanje administratorskim nalozima</p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setShowForm(true); setEditingUser(null); }}
            className="flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Novi korisnik
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-neutral-200/60 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-neutral-900">
              {editingUser ? 'Uredi korisnika' : 'Novi korisnik'}
            </h2>
            <button onClick={resetForm} className="text-neutral-400 hover:text-neutral-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-6 flex items-center gap-3">
              <X className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-red-600 text-sm font-medium">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Korisničko ime *</label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Puno ime</label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Uloga *</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
                >
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                  Lozinka {editingUser ? '(ostavite prazno za bez promjene)' : '*'}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
                  required={!editingUser}
                  minLength={6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                  Potvrda lozinke {editingUser ? '' : '*'}
                </label>
                <input
                  type="password"
                  value={form.password_confirm}
                  onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                  className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
                  required={!editingUser}
                  minLength={6}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-neutral-900 hover:bg-neutral-800 text-white py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
              >
                {saving ? 'Čuvanje...' : (editingUser ? 'Sačuvaj izmjene' : 'Kreiraj korisnika')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2.5 border border-neutral-200 rounded-xl text-sm font-medium text-neutral-600 hover:bg-neutral-50 transition-colors"
              >
                Otkaži
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-neutral-200/60 overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-100">
            <tr>
              <th className="text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider px-6 py-4">Korisnik</th>
              <th className="text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider px-6 py-4">Email</th>
              <th className="text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider px-6 py-4">Uloga</th>
              <th className="text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider px-6 py-4">Status</th>
              <th className="text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider px-6 py-4">Akcije</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-neutral-50/50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center">
                      <Shield className={`w-5 h-5 ${user.is_super_admin ? 'text-rose-500' : 'text-neutral-400'}`} />
                    </div>
                    <div>
                      <div className="font-medium text-neutral-900">{user.username}</div>
                      {user.full_name && <div className="text-sm text-neutral-500">{user.full_name}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-neutral-600">{user.email || '-'}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    user.is_super_admin
                      ? 'bg-rose-50 text-rose-700'
                      : 'bg-neutral-100 text-neutral-700'
                  }`}>
                    {user.is_super_admin ? 'Super Admin' : 'Admin'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    user.is_active
                      ? 'bg-green-50 text-green-700'
                      : 'bg-neutral-100 text-neutral-500'
                  }`}>
                    {user.is_active ? 'Aktivan' : 'Neaktivan'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleEdit(user)}
                      className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(user)}
                      className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-neutral-500">
                  Nema korisnika
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Info box about roles */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <h3 className="font-medium text-blue-900 mb-2">Informacije o ulogama</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li><strong>Super Admin:</strong> Puni pristup - može uređivati bodove turnira/liga i upravljati korisnicima</li>
          <li><strong>Admin:</strong> Može kreirati turnire, igrače i lige, ali ne može mijenjati bodove</li>
        </ul>
      </div>
    </div>
  );
};

// ==================== ADMIN SETTINGS ====================
const AdminSettings = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [form, setForm] = useState({
    old_password: '',
    new_password: '',
    new_password_confirm: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.new_password_confirm) {
      toast.error('Nove lozinke se ne poklapaju');
      return;
    }
    if (form.new_password.length < 6) {
      toast.error('Nova lozinka mora imati najmanje 6 karaktera');
      return;
    }
    setLoading(true);
    try {
      await authAPI.changePassword(form.old_password, form.new_password, form.new_password_confirm);
      toast.success('Lozinka uspješno promijenjena');
      setForm({ old_password: '', new_password: '', new_password_confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri promjeni lozinke');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Postavke</h1>
        <p className="text-neutral-500 text-sm mt-1">Upravljanje vašim nalogom</p>
      </div>

      {/* User Info */}
      <div className="bg-white rounded-2xl border border-neutral-200/60 p-6">
        <h2 className="text-lg font-semibold text-neutral-900 mb-4">Informacije o nalogu</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-500 mb-1">Korisničko ime</label>
            <p className="text-neutral-900">{user?.username}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-500 mb-1">Email</label>
            <p className="text-neutral-900">{user?.email || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-500 mb-1">Puno ime</label>
            <p className="text-neutral-900">{user?.full_name || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-500 mb-1">Uloga</label>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              user?.is_super_admin ? 'bg-rose-50 text-rose-700' : 'bg-neutral-100 text-neutral-700'
            }`}>
              {user?.is_super_admin ? 'Super Admin' : 'Admin'}
            </span>
          </div>
        </div>
      </div>

      {/* Password Change */}
      <div className="bg-white rounded-2xl border border-neutral-200/60 p-6">
        <h2 className="text-lg font-semibold text-neutral-900 mb-4">Promjena lozinke</h2>
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Trenutna lozinka</label>
            <input
              type="password"
              value={form.old_password}
              onChange={(e) => setForm({ ...form, old_password: e.target.value })}
              className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Nova lozinka</label>
            <input
              type="password"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
              required
              minLength={6}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Potvrdi novu lozinku</label>
            <input
              type="password"
              value={form.new_password_confirm}
              onChange={(e) => setForm({ ...form, new_password_confirm: e.target.value })}
              className="w-full border border-neutral-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
              required
              minLength={6}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Key className="w-4 h-4" />
            {loading ? 'Čuvanje...' : 'Promijeni lozinku'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ==================== STATISTICS ====================
const AdminStatistics = () => {
  const [stats, setStats] = useState({
    total: { visits: 0, unique: 0 },
    today: { visits: 0, unique: 0 },
    week: { visits: 0, unique: 0 },
    month: { visits: 0, unique: 0 },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsAPI.getStats()
      .then(res => {
        if (res.data) setStats(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-neutral-900 border-t-transparent" />
      </div>
    );
  }

  const StatCard = ({ title, visits, unique, icon: Icon, color }) => (
    <div className="bg-white rounded-2xl border border-neutral-200/60 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <span className="text-sm font-medium text-neutral-500">{title}</span>
      </div>
      <div className="space-y-1">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-neutral-900">{visits.toLocaleString()}</span>
          <span className="text-sm text-neutral-500">posjeta</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold text-neutral-700">{unique.toLocaleString()}</span>
          <span className="text-sm text-neutral-500">jedinstvenih</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Statistika posjeta</h1>
        <p className="text-neutral-500 text-sm mt-1">Pregled posjeta sajta</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Danas"
          visits={stats.today.visits}
          unique={stats.today.unique}
          icon={Calendar}
          color="bg-emerald-500"
        />
        <StatCard
          title="Ova sedmica"
          visits={stats.week.visits}
          unique={stats.week.unique}
          icon={TrendingUp}
          color="bg-blue-500"
        />
        <StatCard
          title="Ovaj mjesec"
          visits={stats.month.visits}
          unique={stats.month.unique}
          icon={BarChart3}
          color="bg-purple-500"
        />
        <StatCard
          title="Ukupno"
          visits={stats.total.visits}
          unique={stats.total.unique}
          icon={Users}
          color="bg-neutral-900"
        />
      </div>
    </div>
  );
};

// ==================== ACTIVITY LOG ====================
const AdminActivity = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    activityAPI.getAll()
      .then(res => { setActivities(Array.isArray(res.data) ? res.data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('sr-Latn-ME', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-neutral-900 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Aktivnosti</h1>
        <p className="text-neutral-500 text-sm mt-1">Pregled svih aktivnosti na platformi</p>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200/60 overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200/60">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Vrijeme</th>
              <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Korisnik</th>
              <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Akcija</th>
              <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Tip</th>
              <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Objekt</th>
              <th className="text-left px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">IP adresa</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {activities.map(activity => (
              <tr key={activity.id} className="hover:bg-neutral-50">
                <td className="px-6 py-4 text-sm text-neutral-600">{formatDate(activity.created_at)}</td>
                <td className="px-6 py-4 text-sm font-medium text-neutral-900">{activity.user_name || '-'}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    activity.action === 'create' ? 'bg-green-50 text-green-700' :
                    activity.action === 'update' ? 'bg-blue-50 text-blue-700' :
                    activity.action === 'delete' ? 'bg-red-50 text-red-700' :
                    activity.action === 'login' ? 'bg-purple-50 text-purple-700' :
                    activity.action === 'logout' ? 'bg-neutral-100 text-neutral-700' :
                    'bg-neutral-100 text-neutral-700'
                  }`}>
                    {activity.action_display}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-neutral-600">{activity.content_type_display}</td>
                <td className="px-6 py-4 text-sm text-neutral-600">{activity.object_name || '-'}</td>
                <td className="px-6 py-4 text-sm text-neutral-500">{activity.ip_address || '-'}</td>
              </tr>
            ))}
            {activities.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-neutral-500">
                  Nema zabilježenih aktivnosti
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== ADMIN ADS ====================
const AdminAds = () => {
  const [ads, setAds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [form, setForm] = useState({ name: '', link_url: '', position: 'homepage_top', is_active: true, start_date: '', end_date: '' });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const toast = useToast();
  const { confirm } = useConfirm();

  useEffect(() => { loadAds(); }, []);

  const loadAds = () => {
    adsAPI.getAll().then(res => setAds(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('name', form.name);
    formData.append('link_url', form.link_url || '');
    formData.append('position', form.position);
    formData.append('is_active', form.is_active);
    if (form.start_date) formData.append('start_date', form.start_date);
    if (form.end_date) formData.append('end_date', form.end_date);
    if (imageFile) formData.append('image', imageFile);

    try {
      if (editingAd) {
        await adsAPI.update(editingAd.id, formData);
        toast.success('Reklama ažurirana');
      } else {
        await adsAPI.create(formData);
        toast.success('Reklama kreirana');
      }
      loadAds();
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri čuvanju');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm('Obriši reklamu', 'Da li ste sigurni da želite obrisati ovu reklamu?');
    if (confirmed) {
      try {
        await adsAPI.delete(id);
        toast.success('Reklama obrisana');
        loadAds();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Greška pri brisanju reklame');
      }
    }
  };

  const openEdit = (ad) => {
    setEditingAd(ad);
    setForm({
      name: ad.name,
      link_url: ad.link_url || '',
      position: ad.position,
      is_active: ad.is_active,
      start_date: ad.start_date || '',
      end_date: ad.end_date || ''
    });
    setImagePreview(ad.image_url);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAd(null);
    setForm({ name: '', link_url: '', position: 'homepage_top', is_active: true, start_date: '', end_date: '' });
    setImageFile(null);
    setImagePreview(null);
  };

  const positionLabels = {
    homepage_top: 'Početna - Vrh',
    homepage_bottom: 'Početna - Dno',
    sidebar: 'Bočna traka'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Reklame</h1>
          <p className="text-neutral-500 text-sm mt-1">Upravljanje reklamama na sajtu</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-neutral-900 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-neutral-800">
          <Plus className="w-4 h-4" /> Nova reklama
        </button>
      </div>

      <div className="grid gap-4">
        {ads.map(ad => (
          <div key={ad.id} className="bg-white rounded-2xl border border-neutral-200/60 p-4 flex items-center gap-4">
            {ad.image_url && (
              <img src={ad.image_url} alt={ad.name} className="w-32 h-20 object-cover rounded-lg" />
            )}
            <div className="flex-1">
              <h3 className="font-semibold text-neutral-900">{ad.name}</h3>
              <p className="text-sm text-neutral-500">{positionLabels[ad.position]}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full ${ad.is_currently_active ? 'bg-green-50 text-green-600' : 'bg-neutral-100 text-neutral-500'}`}>
                  {ad.is_currently_active ? 'Aktivna' : 'Neaktivna'}
                </span>
                <span className="text-xs text-neutral-400">{ad.click_count} klikova</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => openEdit(ad)} className="p-2 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg">
                <Edit className="w-4 h-4" />
              </button>
              <button onClick={() => handleDelete(ad.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {ads.length === 0 && (
          <div className="bg-white rounded-2xl border border-neutral-200/60 p-12 text-center text-neutral-500">
            Nema reklama
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100]">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">{editingAd ? 'Uredi reklamu' : 'Nova reklama'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Naziv</label>
                <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Link URL</label>
                <input type="url" value={form.link_url} onChange={e => setForm({...form, link_url: e.target.value})}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Pozicija</label>
                <select value={form.position} onChange={e => setForm({...form, position: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500">
                  <option value="homepage_top">Početna - Vrh</option>
                  <option value="homepage_bottom">Početna - Dno</option>
                  <option value="sidebar">Bočna traka</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Slika {editingAd && !imageFile && <span className="text-neutral-400 font-normal">(trenutna slika će biti sačuvana)</span>}
                </label>
                {imagePreview && (
                  <div className="mb-2 relative">
                    <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                    {imageFile && (
                      <button type="button" onClick={() => { setImageFile(null); setImagePreview(editingAd?.image_url || null); }}
                        className="absolute top-2 right-2 p-1 bg-white rounded-full shadow hover:bg-neutral-100">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
                <input type="file" accept="image/*" onChange={e => {
                  if (e.target.files[0]) {
                    setImageFile(e.target.files[0]);
                    setImagePreview(URL.createObjectURL(e.target.files[0]));
                  }
                }} className="w-full text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Od datuma</label>
                  <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})}
                    className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Do datuma</label>
                  <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})}
                    className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="rounded" />
                <span className="text-sm">Aktivna</span>
              </label>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal} className="flex-1 px-4 py-2.5 border border-neutral-200 rounded-xl text-sm font-medium hover:bg-neutral-50">
                  Otkaži
                </button>
                <button type="submit" className="flex-1 bg-neutral-900 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-neutral-800">
                  {editingAd ? 'Sačuvaj' : 'Kreiraj'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN CLUBS ====================
const AdminClubs = () => {
  const [clubs, setClubs] = useState([]);
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm } = useConfirm();

  useEffect(() => { loadClubs(); }, []);

  const loadClubs = () => {
    clubsAPI.getAll().then(res => setClubs(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm('Obriši teren', 'Da li ste sigurni da želite obrisati ovaj teren/klub?');
    if (confirmed) {
      try {
        await clubsAPI.delete(id);
        toast.success('Teren obrisan');
        loadClubs();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Greška pri brisanju terena');
      }
    }
  };

  const surfaceLabels = { clay: 'Šljaka', hard: 'Hard', grass: 'Trava', carpet: 'Tepih', concrete: 'Beton', other: 'Ostalo' };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Tereni / Klubovi</h1>
          <p className="text-neutral-500 text-sm mt-1">Upravljanje teniskim terenima i klubovima</p>
        </div>
        <button onClick={() => navigate('/panel/clubs/new')} className="flex items-center gap-2 bg-neutral-900 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-neutral-800 whitespace-nowrap">
          <Plus className="w-4 h-4" /> Novi teren
        </button>
      </div>

      <div className="grid gap-4">
        {clubs.map(club => (
          <div key={club.id} className="bg-white rounded-2xl border border-neutral-200/60 p-4 cursor-pointer hover:border-neutral-300" onClick={() => navigate(`/panel/clubs/${club.id}`)}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {club.main_image_url ? (
                <img src={club.main_image_url} alt={club.name} className="w-full sm:w-24 h-32 sm:h-24 object-cover rounded-xl" />
              ) : (
                <div className="w-full sm:w-24 h-32 sm:h-24 bg-neutral-100 rounded-xl flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-neutral-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-neutral-900 truncate">{club.name}</h3>
                  {club.is_featured && <Star className="w-4 h-4 text-amber-500 fill-amber-500 flex-shrink-0" />}
                </div>
                <p className="text-sm text-neutral-500 truncate">{club.address}, {club.city}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-neutral-500">
                  <span>{club.courts_count} teren{club.courts_count > 1 ? 'a' : ''}</span>
                  <span>{surfaceLabels[club.surface] || club.surface}</span>
                  {club.has_lighting && <span>Rasvjeta</span>}
                  {club.is_indoor && <span>Zatvoreno</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 self-end sm:self-center" onClick={e => e.stopPropagation()}>
                <button onClick={() => navigate(`/panel/clubs/${club.id}/edit`)} className="p-2 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(club.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {clubs.length === 0 && (
          <div className="bg-white rounded-2xl border border-neutral-200/60 p-12 text-center text-neutral-500">
            Nema terena
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ADMIN CLUB FORM ====================
const AdminClubForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [club, setClub] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', address: '', city: 'Podgorica', google_maps_url: '',
    phone: '', email: '', website: '', instagram: '', facebook: '',
    courts_count: 1, surface: 'clay', has_lighting: false, is_indoor: false,
    working_hours: '', price_info: '', is_active: true, is_featured: false
  });
  const [images, setImages] = useState([]);
  const [newImageFile, setNewImageFile] = useState(null);

  useEffect(() => {
    if (id && id !== 'new') {
      setLoading(true);
      clubsAPI.get(id).then(res => {
        const data = res.data;
        setClub(data);
        setForm({
          name: data.name || '', description: data.description || '', address: data.address || '',
          city: data.city || 'Podgorica', google_maps_url: data.google_maps_url || '',
          phone: data.phone || '', email: data.email || '', website: data.website || '',
          instagram: data.instagram || '', facebook: data.facebook || '',
          courts_count: data.courts_count || 1, surface: data.surface || 'clay',
          has_lighting: data.has_lighting || false, is_indoor: data.is_indoor || false,
          working_hours: data.working_hours || '', price_info: data.price_info || '',
          is_active: data.is_active ?? true, is_featured: data.is_featured ?? false
        });
        setImages(data.images || []);
        setLoading(false);
      }).catch(() => { setLoading(false); navigate('/panel/clubs'); });
    }
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    Object.keys(form).forEach(key => formData.append(key, form[key]));

    try {
      if (id && id !== 'new') {
        await clubsAPI.update(id, formData);
        toast.success('Teren ažuriran');
      } else {
        const res = await clubsAPI.create(formData);
        toast.success('Teren kreiran');
        navigate(`/panel/clubs/${res.data.id}`);
        return;
      }
      navigate('/panel/clubs');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Greška pri čuvanju');
    }
  };

  const handleAddImage = async () => {
    if (!newImageFile || !id) return;
    const formData = new FormData();
    formData.append('image', newImageFile);
    try {
      await clubsAPI.addImage(id, formData);
      toast.success('Slika dodana');
      const res = await clubsAPI.get(id);
      setImages(res.data.images || []);
      setNewImageFile(null);
    } catch (err) {
      toast.error('Greška pri dodavanju slike');
    }
  };

  const handleRemoveImage = async (imageId) => {
    try {
      await clubsAPI.removeImage(id, imageId);
      toast.success('Slika obrisana');
      setImages(images.filter(img => img.id !== imageId));
    } catch (err) {
      toast.error('Greška pri brisanju slike');
    }
  };

  const handleSetMainImage = async (imageId) => {
    try {
      await clubsAPI.setMainImage(id, imageId);
      toast.success('Glavna slika postavljena');
      setImages(images.map(img => ({ ...img, is_main: img.id === imageId })));
    } catch (err) {
      toast.error('Greška');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-neutral-900 border-t-transparent" /></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/panel/clubs')} className="p-2 hover:bg-neutral-100 rounded-lg flex-shrink-0">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">{id && id !== 'new' ? 'Uredi teren' : 'Novi teren'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-neutral-200/60 p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Naziv *</label>
            <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Grad</label>
            <input type="text" value={form.city} onChange={e => setForm({...form, city: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Adresa *</label>
          <input type="text" value={form.address} onChange={e => setForm({...form, address: e.target.value})} required
            className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Opis</label>
          <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
            className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Telefon</label>
            <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Web stranica</label>
            <input type="url" value={form.website} onChange={e => setForm({...form, website: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Instagram</label>
            <input type="text" value={form.instagram} onChange={e => setForm({...form, instagram: e.target.value})} placeholder="@username"
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Facebook</label>
            <input type="text" value={form.facebook} onChange={e => setForm({...form, facebook: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Google Maps link</label>
            <input type="url" value={form.google_maps_url} onChange={e => setForm({...form, google_maps_url: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Broj terena</label>
            <input type="number" min="1" value={form.courts_count} onChange={e => setForm({...form, courts_count: parseInt(e.target.value) || 1})}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Podloga</label>
            <select value={form.surface} onChange={e => setForm({...form, surface: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500">
              <option value="clay">Šljaka</option>
              <option value="hard">Hard</option>
              <option value="grass">Trava</option>
              <option value="carpet">Tepih</option>
              <option value="concrete">Beton</option>
              <option value="other">Ostalo</option>
            </select>
          </div>
          <div className="col-span-2 flex items-center gap-4 md:pt-6">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.has_lighting} onChange={e => setForm({...form, has_lighting: e.target.checked})} className="rounded" />
              <span className="text-sm">Rasvjeta</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_indoor} onChange={e => setForm({...form, is_indoor: e.target.checked})} className="rounded" />
              <span className="text-sm">Zatvoreno</span>
            </label>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Radno vrijeme</label>
            <input type="text" value={form.working_hours} onChange={e => setForm({...form, working_hours: e.target.value})}
              placeholder="npr. Pon-Pet: 07-22h, Sub-Ned: 08-20h"
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
          </div>
          <div className="flex items-center gap-4 md:pt-6">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="rounded" />
              <span className="text-sm">Aktivan</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_featured} onChange={e => setForm({...form, is_featured: e.target.checked})} className="rounded" />
              <span className="text-sm">Istaknut</span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Cijene</label>
          <textarea value={form.price_info} onChange={e => setForm({...form, price_info: e.target.value})} rows={2}
            placeholder="Informacije o cijenama iznajmljivanja"
            className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" />
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          <button type="button" onClick={() => navigate('/panel/clubs')} className="w-full sm:w-auto px-6 py-2.5 border border-neutral-200 rounded-xl text-sm font-medium hover:bg-neutral-50">
            Otkaži
          </button>
          <button type="submit" className="w-full sm:w-auto bg-neutral-900 text-white px-6 py-2.5 rounded-xl text-sm font-medium hover:bg-neutral-800">
            {id && id !== 'new' ? 'Sačuvaj' : 'Kreiraj'}
          </button>
        </div>
      </form>

      {id && id !== 'new' && (
        <div className="bg-white rounded-2xl border border-neutral-200/60 p-4 sm:p-6 space-y-4">
          <h2 className="text-lg font-semibold">Slike</h2>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <input type="file" accept="image/*" onChange={e => setNewImageFile(e.target.files[0])}
              className="text-sm w-full sm:w-auto file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-neutral-100 file:text-neutral-700 file:font-medium hover:file:bg-neutral-200" />
            <button type="button" onClick={handleAddImage} disabled={!newImageFile}
              className="w-full sm:w-auto px-4 py-2 bg-neutral-900 text-white rounded-lg text-sm font-medium hover:bg-neutral-800 disabled:opacity-50">
              Dodaj sliku
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {images.map(img => (
              <div key={img.id} className="relative group">
                <img src={img.image_url} alt="" className={`w-full h-32 object-cover rounded-lg ${img.is_main ? 'ring-2 ring-rose-500' : ''}`} />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                  {!img.is_main && (
                    <button onClick={() => handleSetMainImage(img.id)} className="p-2 bg-white rounded-lg text-neutral-900 hover:bg-neutral-100">
                      <Star className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => handleRemoveImage(img.id)} className="p-2 bg-white rounded-lg text-red-500 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {img.is_main && <span className="absolute top-2 left-2 bg-rose-500 text-white text-xs px-2 py-0.5 rounded">Glavna</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN APP ====================
// Protected Route Component
const ProtectedRoute = ({ children, requireSuperAdmin = false }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
    if (!loading && requireSuperAdmin && !user?.is_super_admin) {
      navigate('/panel/leagues');
    }
  }, [user, loading, navigate, requireSuperAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-neutral-900 border-t-transparent" />
      </div>
    );
  }

  if (!user) return null;
  if (requireSuperAdmin && !user.is_super_admin) return null;

  return children;
};

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <Router>
          <OfflineIndicator />
          <AuthProvider>
            <ToastProvider>
              <ConfirmProvider>
                <AppRoutes />
              </ConfirmProvider>
            </ToastProvider>
          </AuthProvider>
        </Router>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

// Separate Routes component for page transitions
const AppRoutes = () => {
  const location = useLocation();

  return (
    <PageTransition key={location.pathname}>
      <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/rankings" element={<PublicRankings />} />
          <Route path="/power-index" element={<PublicPowerIndex />} />
          <Route path="/tournaments" element={<PublicTournaments />} />
          <Route path="/tournaments/:id" element={<PublicTournamentDetail />} />
          <Route path="/leagues" element={<PublicLeagues />} />
          <Route path="/players" element={<PublicPlayers />} />
          <Route path="/players/:id" element={<PublicPlayerDetail />} />
          <Route path="/news" element={<PublicNews />} />
          <Route path="/news/:id" element={<PublicNews />} />
          <Route path="/head-to-head" element={<HeadToHead />} />
          <Route path="/leagues/:id" element={<PublicLeagueDetail />} />
          <Route path="/clubs" element={<PublicClubs />} />
          <Route path="/clubs/:id" element={<PublicClubDetail />} />
          <Route path="/pravilnik" element={<Pravilnik />} />

          {/* Admin Routes */}
          <Route path="/panel/dashboard" element={<ProtectedRoute><AdminLayout><Dashboard /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/players" element={<ProtectedRoute><AdminLayout><Players /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/players/new" element={<ProtectedRoute><AdminLayout><PlayerForm /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/players/:id" element={<ProtectedRoute><AdminLayout><PlayerDetail /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/players/:id/edit" element={<ProtectedRoute><AdminLayout><PlayerForm /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/rankings" element={<ProtectedRoute><AdminLayout><Rankings /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/power-index" element={<ProtectedRoute><AdminLayout><PowerIndex /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/tournaments" element={<ProtectedRoute><AdminLayout><Tournaments /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/tournaments/:id" element={<ProtectedRoute><AdminLayout><TournamentDetail /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/matches/:id" element={<ProtectedRoute><AdminLayout><MatchDetail /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/leagues" element={<ProtectedRoute><AdminLayout><Leagues /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/leagues/:id" element={<ProtectedRoute><AdminLayout><LeagueDetail /></AdminLayout></ProtectedRoute>} />
          {/* Super Admin Only Routes */}
          <Route path="/panel/news" element={<ProtectedRoute requireSuperAdmin><AdminLayout><AdminNews /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/news/new" element={<ProtectedRoute requireSuperAdmin><AdminLayout><AdminNewsForm /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/news/:id" element={<ProtectedRoute requireSuperAdmin><AdminLayout><AdminNewsForm /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/news/:id/edit" element={<ProtectedRoute requireSuperAdmin><AdminLayout><AdminNewsForm /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/users" element={<ProtectedRoute requireSuperAdmin><AdminLayout><AdminUsers /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/activity" element={<ProtectedRoute requireSuperAdmin><AdminLayout><AdminActivity /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/ads" element={<ProtectedRoute requireSuperAdmin><AdminLayout><AdminAds /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/clubs" element={<ProtectedRoute requireSuperAdmin><AdminLayout><AdminClubs /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/clubs/new" element={<ProtectedRoute requireSuperAdmin><AdminLayout><AdminClubForm /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/clubs/:id" element={<ProtectedRoute requireSuperAdmin><AdminLayout><AdminClubForm /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/clubs/:id/edit" element={<ProtectedRoute requireSuperAdmin><AdminLayout><AdminClubForm /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/statistics" element={<ProtectedRoute requireSuperAdmin><AdminLayout><AdminStatistics /></AdminLayout></ProtectedRoute>} />
          <Route path="/panel/settings" element={<ProtectedRoute><AdminLayout><AdminSettings /></AdminLayout></ProtectedRoute>} />
        </Routes>
    </PageTransition>
  );
};

export default App;
